// Apocalypse Dispatch — V4 app (rewrite phase 4.1).
//
// The playable V4 loop as the real, saveable app: plan the day (pick a target
// + squad) -> play the supply run as turn-based infiltration (the mounted tactical-view)
// -> the outcome (deaths, banked items, threat) persists in a campaign-v4 ->
// equip survivors from base storage -> repeat. Loads real neighbourhood
// geometry from the backend tile cache (see loadTileGeometry / plan 4.2b), with
// the committed fixture as an offline fallback; a tile picker, auth, and
// Classic/Custom character creation land on later increments.
//
// The old V3 mission-dispatch app is preserved at /legacy-v3 and in git at
// 465d970. This file is a thin orchestrator over the tested domain modules +
// the extracted tactical-session/tactical-view; it deliberately stays small.
//
// Starting a supply run doesn't swap to a different map: it flies the same
// MapLibre camera in on the target and mounts the tactical layer (units,
// vision cones, drag-to-order) directly on top, then flies back out on
// return — see startSupplyRun()/returnToBase(). tactical-view.mjs owns everything
// about the tactical layer itself; this file only owns the transition.

import { buildRoadGraph, distanceInMeters, findRouteBetweenPoints, geodesicCircle, nearestPointOnPolyline } from "./road-graph.mjs";
import { buildingDataToGeoJSON } from "./building-geojson.mjs";
import { pointInBuildings } from "./domain/line-of-sight.mjs";
import {
  baseShapeCellsToGeoJSON, baseShapeOutlineToGeoJSON,
  buildingFootprintMetrics, featureCentroidCoordinate, fitBestBuildingGrid, fitBuildingGridFromConfig,
  generateBaseShape, gridCellsToGeoJSON, gridConfigFromFit,
} from "./domain/building-footprint.mjs";
import { getItem, inventoryMaterials, inventoryNutrition, inventoryValue, isFood, isMaterial } from "./domain/items.mjs";
import { assignBuildingScavengeState, buildingKey, promisingCount, PROMISING_MAX_ITEMS } from "./domain/building-scavenge.mjs";
import { assignSurvivorBuilding } from "./domain/survivor-building.mjs";
import { createTacticalState, DEFAULT_CARRY_CAPACITY } from "./domain/tactical-turn.mjs";
import { areaThreatAt, GLOBAL_THREAT_THRESHOLD, THREAT_MAX, THREAT_TURN_INTERVAL } from "./domain/threat.mjs";
import { ON_FOOT_SPEED_MPM, MAX_POIS_PER_DAY } from "./domain/day-planner.mjs";
import {
  buildTacticalForce, buildTacticalEncounterZone, createTacticalUnit, DEFAULT_TACTICAL_MOVE_METERS_PER_TURN,
  DEFAULT_TACTICAL_ZONE_RADIUS_METERS, defenseOutcomeFromState, ENEMY_SPAWN_AVOID_SPAWN_METERS, supplyRunOutcomeFromState,
  routeEntryCoordinateForZone, squadSpawnPositions, westernEntryCrossing,
} from "./domain/tactical-session.mjs";
import { createTacticalView } from "./domain/tactical-view.mjs";
import { SURVIVOR_MAX_HEALTH } from "./domain/combat-math.mjs";
import { buildTacticalZone } from "./domain/tactical-zone.mjs";
import { generateBaseLayout, isBaseDefenseValid } from "./domain/base-layout.mjs";
import { detectRooms, roomSignature } from "./domain/base-room-detection.mjs";
import { doorSwingsToGeoJSON, makeFromWorld, makeToWorld, roomsToGeoJSON, wallStateToGeoJSON } from "./domain/base-defense-geometry.mjs";
import { cellKey } from "./domain/base-walls.mjs";
import { buildBaseAssault, buildBaseNavGrid, defenderSpawnPositions } from "./domain/base-nav.mjs";
import {
  canEquipItemId,
  EQUIPMENT_LOADOUT_SLOT_ORDER,
  EQUIPMENT_LOADOUT_SLOTS,
  equipmentLoadoutMap,
  equipmentLoadoutSlot,
  getEquipmentEffects,
} from "./domain/survivor-upgrades.mjs";
import {
  acknowledgeEndgameBriefing,
  activeSurvivorsV4, advanceCampaignDay, applyDefenseOutcome, applySupplyRunOutcome, availableEquipmentValue, campaignConcluded, campaignDailyQuota, carriedInventory, createCampaignV4,
  addMaterials, baseMaterials, campaignBaseRoomStats, campaignHousingCapacity, campaignRescueDay, isOverHousingCapacity, spendMaterials,
  currentGlobalThreat, currentPoiThreat, defenseDueOnDay, defenseWaveSize, depositCarriedInventory, discoverPois, equipSurvivorInventoryItem, equipSurvivorItem, finalWaveSize, getSurvivorV4, globalThreatCritical,
  isPoiDiscovered, isRescueDay, isSurvivorWounded, pantryFood, parseCampaignV4,
  recordBuildingScouting, recruitSurvivorV4, resolveRescue, serializeCampaignV4, shouldShowEndgameBriefing, survivorsExtracted, totalRecruited, transferSurvivorEquipmentItem, unequipSurvivorItem,
} from "./domain/campaign-v4.mjs";
import { BASE_ROOM_CATALOG, BASE_ROOM_TYPES, seedDefaultRoomTypes } from "./domain/base-rooms.mjs";
import {
  categoryDetails, professionCategory, professionName, professionNotes, professionPoiReveal, SURVIVOR_PROFESSION_DEFINITIONS, SURVIVOR_PROFESSION_IDS,
} from "./domain/professions-v4.mjs";
import { findRevealBuilding } from "./domain/poi-reveal.mjs";
import { availableTutorialLessons, TUTORIAL_CONTEXTS, TUTORIAL_LESSONS } from "./domain/tutorial-lessons.mjs";
import { completeTutorialLesson, resetTutorialProgress, setTutorialEnabled } from "./domain/tutorial-state.mjs";
import { createTutorialView } from "./domain/tutorial-view.mjs";
import { DEFAULT_DEV_SETTINGS, DEV_SETTING_SCHEMA, loadDevSettings, saveDevSettings } from "./game-settings.mjs";

const SAVE_KEY = "apocalypse-dispatch.v4.autosave";
const MAX_SUPPLY_RUN_SQUAD_SIZE = 3;
const DAY_BUDGET_MINUTES = 600; // travel budget gating which targets are reachable today (tuned later)
const ON_SITE_MINUTES_PER_POI = 60;
// Asymmetric padding: extra room below the supply-run circle for the on-map End Turn
// button (so the circle sits a bit above vertical center, which is fine).
const TACTICAL_CAMERA_PADDING = { top: 24, bottom: 36, left: 56, right: 56 };
const TACTICAL_CAMERA_DURATION_MS = 900;
// The base-defense engagement circle: much tighter than a supply run's 150m so the
// base fills the view and the horde's street approach reads clearly.
// The base-defense engagement circle scales with the building: diameter ~= 3x
// the base's longer side (radius ~= 1.5x), so the base fills the frame instead
// of floating in a big circle. Clamped so tiny bases still get a usable circle
// and huge ones don't zoom out too far.
const DEFENSE_ZONE_LENGTH_MULTIPLIER = 1.5;
const DEFENSE_ZONE_MIN_RADIUS_METERS = 22;
const DEFENSE_ZONE_MAX_RADIUS_METERS = 55;

function defenseZoneRadiusMeters(shape) {
  if (!shape?.cells?.size) return DEFENSE_ZONE_MIN_RADIUS_METERS;
  let minRow = Infinity, maxRow = -Infinity, minCol = Infinity, maxCol = -Infinity;
  for (const key of shape.cells) {
    const [row, col] = key.split(":").map(Number);
    if (row < minRow) minRow = row; if (row > maxRow) maxRow = row;
    if (col < minCol) minCol = col; if (col > maxCol) maxCol = col;
  }
  const lengthMeters = Math.max(maxRow - minRow + 1, maxCol - minCol + 1) * (shape.cellSizeMeters || BASE_SHAPE_CELL_METERS);
  return Math.max(DEFENSE_ZONE_MIN_RADIUS_METERS, Math.min(DEFENSE_ZONE_MAX_RADIUS_METERS, lengthMeters * DEFENSE_ZONE_LENGTH_MULTIPLIER));
}
let devSettings = loadDevSettings();
const TACTICAL_ACTIVITY_MAX_ROWS = 12;
const DEFAULT_ROSTER = ["Alex", "Bailey", "Casey"]; // Classic default until character creation lands
const BASE_BUILDING_SOURCE_ID = "base-building";
const BASE_BUILDING_FILL_LAYER_ID = "base-building-fill";
const BASE_BUILDING_OUTLINE_LAYER_ID = "base-building-outline";
const BASE_GRID_SOURCE_ID = "base-grid";
const BASE_GRID_FILL_LAYER_ID = "base-grid-fill";
const BASE_GRID_LINE_LAYER_ID = "base-grid-line";
const BASE_GRID_MIN_ZOOM = 19;
const BASE_GRID_TARGET_ZOOM = 19.25;
// Base-tab interior overlay: rooms/walls/doors drawn on the hero map (the same
// visual language as base defense) when the Base tab is active.
const BASE_ROOMS_SOURCE_ID = "basetab-rooms";
const BASE_ROOMS_FILL_LAYER_ID = "basetab-rooms-fill";
const BASE_EDGES_SOURCE_ID = "basetab-edges";
const BASE_EDGES_LINE_LAYER_ID = "basetab-edges-line";
const BASE_DOORS_SOURCE_ID = "basetab-doors";
const BASE_DOORS_LINE_LAYER_ID = "basetab-doors-line";
const BASE_ROOM_LABELS_SOURCE_ID = "basetab-room-labels";
const BASE_ROOM_LABELS_LAYER_ID = "basetab-room-labels-layer";
const BASE_INTERIOR_LAYER_IDS = [
  BASE_ROOMS_FILL_LAYER_ID, BASE_EDGES_LINE_LAYER_ID, BASE_DOORS_LINE_LAYER_ID, BASE_ROOM_LABELS_LAYER_ID,
];
// Fill tint per built room type; unbuilt regions read as faint neutral grey.
const ROOM_TYPE_FILL_COLORS = { barracks: "#4f9dde", kitchen: "#e08a5f", bedroom: "#6fdc8c", storage: "#9aa6ad" };
const ROOM_UNBUILT_FILL_COLOR = "#c2ccd2";
const BASE_GRID_VERSION = 2;
const BASE_GRID_EXTRA_SHRINK_PERCENT = 0.03;
const BASE_SHAPE_CELL_METERS = 2;
const BASE_SHAPE_SIMPLIFY_METERS = 2;
const VENDOR_BUILDING_LAYER_ID = "building"; // Positron's own building fill layer
const OVERWORLD_TILE_FOG_SOURCE_ID = "overworld-tile-fog";
const OVERWORLD_TILE_FOG_FILL_LAYER_ID = "overworld-tile-fog-fill";
const OVERWORLD_TILE_FOG_LINE_LAYER_ID = "overworld-tile-fog-line";
const OVERWORLD_BOUNDS_PADDING_RATIO = 1;
const OVERWORLD_MIN_ZOOM_DELTA = 0.35;
const PLAYTEST_QUERY_PARAM = "playtest";
const PLAYTEST_ALIASES = {
  overworld: "overworld",
  "overworld-target": "overworld-target-selected",
  "overworld-target-selected": "overworld-target-selected",
  "supply-run": "tactical",
  "supply-run-turn-states": "supply-run-turn-states",
  "supply-run-threat": "supply-run-threat",
  "supply-run-items": "supply-run-items-nearby",
  "supply-run-items-nearby": "supply-run-items-nearby",
  "supply-run-success": "supply-run-end-success",
  "supply-run-end-success": "supply-run-end-success",
  "supply-run-loss": "supply-run-end-loss",
  "supply-run-end-loss": "supply-run-end-loss",
  defense: "defense",
  "base-defense": "defense",
  "return-summary": "return-summary",
  "base-upgrades": "base-upgrades",
  "hungry-day": "hungry-day",
  "critical-threat": "critical-threat",
  "base-building": "base-building",
  "tutorial-overworld": "tutorial-overworld",
  "tutorial-supply-run": "tutorial-supply-run",
  "endgame-briefing": "endgame-briefing",
  professions: "profession-gallery",
  "profession-gallery": "profession-gallery",
};
const REPORT_SESSION_ID = `v4-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
const SUPABASE_URL = "https://xoqdprtofuqzmbycltlr.supabase.co";
const SUPABASE_PUBLISHABLE_KEY = "sb_publishable_rnvas7GTuR_nHJU2H9Y9wQ_ru0dGx-4";
const AUTH_RETURN_PENDING_KEY = "apocalypse-dispatch.auth-return-pending.v4";

function requestedPlaytestEntry() {
  const raw = new URLSearchParams(window.location.search).get(PLAYTEST_QUERY_PARAM);
  if (!raw) return null;
  return PLAYTEST_ALIASES[raw.trim().toLowerCase()] ?? null;
}

const playtestEntry = requestedPlaytestEntry();
let playtestState = playtestEntry ? { requested: playtestEntry, applied: false, targetId: null, syntheticResult: false } : null;
let pendingNewGameLog = null;
const pendingNewGameLogs = [];
const reportedDiscordLoginUsers = new Set();
const authState = {
  client: null,
  session: null,
  user: null,
  profileData: null,
  error: "",
  returnPending: false,
  ready: false,
};

// --- Tile geometry ------------------------------------------------------
// Real neighbourhood geometry from the backend tile cache (Overpass -> local
// cache -> Supabase precache; see V4_APP_REWRITE_PLAN.md 4.2b). The `/api/*`
// endpoints are same-origin (no CORS) and backed by the precache, so a
// precached tile needs no live Overpass. Falls back to the committed fixture if
// the backend/tile is unavailable — a zero-network dev path. The returned shape
// matches the old fixture, so nothing downstream changes.

const DEFAULT_TILE = { z: 14, x: 2615, y: 6321 }; // bundled "Dominican / downtown San Rafael"

async function fetchTileDataset(kind, tile) {
  const response = await fetch(`/api/${kind}?z=${tile.z}&x=${tile.x}&y=${tile.y}`);
  if (!response.ok) throw new Error(`/api/${kind} responded ${response.status}`);
  return response.json();
}

async function loadTileGeometry(tile) {
  try {
    const [roads, buildings, pois, manifest] = await Promise.all([
      fetchTileDataset("roads", tile),
      fetchTileDataset("buildings", tile),
      fetchTileDataset("pois", tile),
      fetch(`/api/tile-manifest?z=${tile.z}&x=${tile.x}&y=${tile.y}`).then((response) => (response.ok ? response.json() : null)).catch(() => null),
    ]);
    const [south, west, north, east] = buildings.bounds; // tile bounds: [S, W, N, E]
    // buildings.data is already GeoJSON in our building-geojson shape; roads/pois
    // .data are raw Overpass { elements }.
    return { source: "tile", label: manifest?.label ?? null, roads: roads.data, buildingCollection: buildings.data, pois: pois.data, bbox: { south, west, north, east } };
  } catch (error) {
    console.warn(`Tile ${tile.z}/${tile.x}/${tile.y} unavailable (${error}); using bundled fixture.`);
    const fixture = await fetch("./fixtures/tactical-demo-map.json").then((response) => response.json());
    return { source: "fixture", label: null, roads: fixture.roads, buildingCollection: buildingDataToGeoJSON(fixture.buildings), pois: fixture.pois, bbox: fixture.bbox };
  }
}

const tileGeometry = await loadTileGeometry(DEFAULT_TILE);
const roadGraph = buildRoadGraph(tileGeometry.roads);
const buildingCollection = tileGeometry.buildingCollection;

function titleCase(text) {
  return text.replace(/[-_]/g, " ").replace(/\b\w/g, (character) => character.toUpperCase());
}
function formatResource(value) {
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}
function elementCoordinate(element) {
  if (Number.isFinite(element.lat) && Number.isFinite(element.lon)) return [element.lon, element.lat];
  if (element.center) return [element.center.lon, element.center.lat];
  return null;
}
function elementName(element, index) {
  const tags = element.tags || {};
  if (tags.name) return tags.name;
  if (tags.shop) return `${titleCase(tags.shop)} Shop`;
  if (tags.amenity) return `${titleCase(tags.amenity)} Site`;
  return `Site ${index + 1}`;
}

const POIS = (tileGeometry.pois?.elements ?? [])
  .map((element, index) => ({ id: `poi:${element.id}`, name: elementName(element, index), coordinate: elementCoordinate(element) }))
  .filter((poi) => Array.isArray(poi.coordinate));

function stableHash(value) {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

function pickFallbackBaseCoordinate() {
  const { south, west, north, east } = tileGeometry.bbox;
  const insideTile = (c) => c[1] >= south && c[1] <= north && c[0] >= west && c[0] <= east;
  let best = null;
  let bestScore = -Infinity;
  for (const node of roadGraph.nodes.values()) {
    if (!insideTile(node.coordinate)) continue;
    const nearest = POIS.length ? Math.min(...POIS.map((poi) => distanceInMeters(node.coordinate, poi.coordinate))) : 0;
    if (nearest > bestScore) { bestScore = nearest; best = node.coordinate; }
  }
  return best ?? [(west + east) / 2, (south + north) / 2];
}

// Any building on the map is a supply-run target (V4 "search any building" model). Each
// carries a stable targetId so a map click resolves to a target — GeoJSON
// string feature ids aren't reliably surfaced by MapLibre click events, so we
// stash it in properties instead.
const overworldBuildings = {
  type: "FeatureCollection",
  features: buildingCollection.features.map((feature) => ({ ...feature, properties: { ...feature.properties, targetId: feature.id } })),
};

function overworldBuildingsForCampaign(campaignState) {
  const intelById = campaignState.buildingIntelById ?? {};
  return {
    type: "FeatureCollection",
    features: overworldBuildings.features.map((feature) => {
      const targetId = String(feature.properties?.targetId ?? feature.id ?? "");
      const intel = intelById[targetId] ?? null;
      return {
        ...feature,
        properties: {
          ...feature.properties,
          scoutingState: intel?.scavengeState ?? "unscouted",
          scouted: Boolean(intel),
          itemCount: intel?.itemCount ?? 0,
          promising: Boolean(intel?.promising),
        },
      };
    }),
  };
}

const buildingTargets = new Map(); // targetId -> { id, name, coordinate, feature }
for (const feature of overworldBuildings.features) {
  const coordinate = featureCentroidCoordinate(feature);
  if (!coordinate) continue;
  const props = feature.properties;
  buildingTargets.set(props.targetId, { id: props.targetId, name: props.name || props.kindLabel || "Building", coordinate, feature });
}

// Base defense wants a building that generates a workably-sized interior: big
// enough to partition into a few rooms, small enough to defend. Filter by the
// actual generated cell count (36 = 6x6 .. 81 = 9x9). Cached per building set
// since generateBaseShape isn't free; a cheap floor-area pre-filter keeps the
// generation pass to a few hundred candidates rather than all ~2.7k.
const MIN_BASE_CELLS = 36;
const MAX_BASE_CELLS = 81;
let baseCandidateCache = { count: -1, list: null };

function baseShapeCellCount(feature) {
  const shape = generateBaseShape(feature, {
    cellSizeMeters: BASE_SHAPE_CELL_METERS,
    simplifyToleranceMeters: BASE_SHAPE_SIMPLIFY_METERS,
  });
  return shape?.cells?.size ?? 0;
}

function baseBuildingCandidates() {
  const count = buildingCollection.features.length;
  if (baseCandidateCache.count === count && baseCandidateCache.list) return baseCandidateCache.list;
  const sized = [];
  for (const feature of buildingCollection.features) {
    const areaM2 = buildingFootprintMetrics(feature).floorAreaSquareMeters;
    if (!(areaM2 >= 100 && areaM2 <= 500)) continue; // ~36..81 cells at 2m, with slack
    const cells = baseShapeCellCount(feature);
    if (cells >= MIN_BASE_CELLS && cells <= MAX_BASE_CELLS) sized.push(feature);
  }
  const largeEnough = buildingCollection.features.filter((feature) => (
    buildingFootprintMetrics(feature).floorAreaSquareFeet >= 200
  ));
  const list = sized.length ? sized : (largeEnough.length ? largeEnough : buildingCollection.features);
  baseCandidateCache = { count, list };
  return list;
}

function pickBaseBuilding(worldSeed) {
  const candidates = baseBuildingCandidates();
  if (!candidates.length) return null;
  return candidates[stableHash(`base:${worldSeed}`) % candidates.length];
}

function solveBaseGrid(feature) {
  const grid = gridConfigFromFit(fitBestBuildingGrid(feature, {
    angleStepDegrees: 2,
    extraShrinkPercent: BASE_GRID_EXTRA_SHRINK_PERCENT,
    maxShrinkPercent: 0.25,
  }));
  return grid ? { ...grid, version: BASE_GRID_VERSION } : null;
}

function baseRecordForFeature(feature, existingBase = {}) {
  const coordinate = featureCentroidCoordinate(feature) ?? pickFallbackBaseCoordinate();
  const properties = feature?.properties ?? {};
  const grid = solveBaseGrid(feature);
  return {
    coordinate,
    // Rooms are keyed by region signature, which is tied to the current base
    // geometry; a re-pick means new geometry, so start empty and let
    // ensureBaseRoomDefaults() re-seed. (Existing rooms survive the normal
    // load path, which doesn't route through here.)
    rooms: {},
    buildingId: String(feature?.id ?? properties.osmId ?? "base-building"),
    buildingName: properties.name || properties.kindLabel || "Base",
    buildingKind: properties.kind || "unknown",
    ...(grid ? { grid } : {}),
  };
}

function baseFeatureForCampaign(currentCampaign = campaign) {
  const buildingId = currentCampaign?.base?.buildingId;
  if (!buildingId) return null;
  return buildingCollection.features.find((feature) => String(feature.id) === buildingId) ?? null;
}

// The forked base shape (snap-and-fill): a new cell shape + outline whose
// silhouette reads as the base building. Cached per building id.
let baseShapeCache = { key: "", shape: null };
function currentBaseShape() {
  const feature = baseFeatureForCampaign();
  if (!feature) return null;
  const key = String(campaign.base?.buildingId ?? feature.id ?? "");
  if (baseShapeCache.key === key && baseShapeCache.shape) return baseShapeCache.shape;
  const shape = generateBaseShape(feature, {
    cellSizeMeters: BASE_SHAPE_CELL_METERS,
    simplifyToleranceMeters: BASE_SHAPE_SIMPLIFY_METERS,
  });
  baseShapeCache = { key, shape };
  return shape;
}

function ensureCampaignBaseBuilding(currentCampaign) {
  const currentFeature = baseFeatureForCampaign(currentCampaign);
  if (currentFeature && currentCampaign.base?.grid?.version === BASE_GRID_VERSION) return currentCampaign;
  if (currentFeature) {
    const grid = solveBaseGrid(currentFeature);
    return grid ? { ...currentCampaign, base: { ...currentCampaign.base, grid } } : currentCampaign;
  }
  const feature = pickBaseBuilding(currentCampaign.worldSeed);
  if (!feature) return currentCampaign;
  return { ...currentCampaign, base: baseRecordForFeature(feature, currentCampaign.base) };
}

function baseCoordinate() {
  return campaign.base?.coordinate ?? pickFallbackBaseCoordinate();
}

let baseGridMapCache = { key: "", data: null };

// The building a POI sits in (else the nearest) — a POI marker click starts a run at that
// polygon. Containment only checked on nearby buildings to keep it cheap.
function buildingTargetIdForPoi(poi) {
  let nearest = null;
  let nearestDistance = Infinity;
  let containing = null;
  for (const target of buildingTargets.values()) {
    const distance = distanceInMeters(poi.coordinate, target.coordinate);
    if (distance < nearestDistance) { nearestDistance = distance; nearest = target; }
    if (!containing && distance < 120 && pointInBuildings(poi.coordinate, [target.feature])) containing = target;
  }
  return (containing ?? nearest)?.id ?? null;
}
for (const poi of POIS) poi.buildingTargetId = buildingTargetIdForPoi(poi);

const zoneCache = new Map();
function zoneInfoFor(target) {
  if (!zoneCache.has(target.id)) zoneCache.set(target.id, buildTacticalEncounterZone(target, { roadGraph, buildingCollection }));
  return zoneCache.get(target.id);
}
function poiById(id) { return buildingTargets.get(id) ?? POIS.find((poi) => poi.id === id); }

// Buildings a "knows the area" survivor revealed (poi-reveal.mjs): they start
// scouted and are forced to hold a real haul when searched. Derived live from the
// roster (dead survivors keep their revealedBuildingId), so the local knowledge
// outlives the person who had it.
function knownBuildingIds() {
  return new Set(
    Object.values(campaign.survivorsById ?? {})
      .map((survivor) => survivor.revealedBuildingId)
      .filter(Boolean),
  );
}

// Scavenge layout previews and supply runs seed from the same entry: preferably where
// the visible base-to-target route crosses into the supply-run circle, with the old
// western crossing as fallback.
function supplyRunEntryCoordinateForTarget(target) {
  const zone = zoneInfoFor(target).zone;
  return routeEntryCoordinateForZone(zone, routeToTarget(target.id)) ?? westernEntryCrossing(zone);
}
function scavengeStateForTarget(target) {
  const zone = zoneInfoFor(target).zone;
  return assignBuildingScavengeState({
    buildings: zone.buildings, zoneId: zone.zoneId, worldSeed: `${campaign.worldSeed}`, entryCoordinate: supplyRunEntryCoordinateForTarget(target),
    forcePromisingIds: knownBuildingIds(),
  });
}

// One recruitable wild survivor per supply run (see survivor-building.mjs). Picks a
// building that holds no supplies (excludes anything stocked in `scavengeState`) so
// a survivor building is never also scavengeable, then clears that building's
// scavenge contents belt-and-suspenders. Names avoid the current roster.
function survivorBuildingForZone(zone, scavengeState) {
  const excludeIds = new Set();
  for (const [id, descriptor] of scavengeState) {
    if (descriptor.promising || descriptor.contents.length > 0) excludeIds.add(id);
  }
  const excludeNames = new Set(
    Object.values(campaign.survivorsById ?? {}).map((survivor) => survivor.name).filter(Boolean),
  );
  const descriptor = assignSurvivorBuilding({
    buildings: zone.buildings, zoneId: zone.zoneId, worldSeed: `${campaign.worldSeed}`,
    excludeIds, excludeNames,
  });
  if (!descriptor) return [];
  const pool = scavengeState.get(descriptor.buildingId);
  if (pool) pool.contents = [];
  return [descriptor];
}
function promisingCountFor(target) {
  return promisingCount(scavengeStateForTarget(target));
}

// --- Campaign persistence ----------------------------------------------

// Classic mode: every survivor's background is fully randomized (worldSeed-
// deterministic, same pattern as pickBaseBuilding) rather than player-chosen —
// player-chosen backgrounds are Custom mode's job once character creation
// lands (plans/i-think-i-want-rosy-stream.md).
//
// A few professions also carry a poiReveal (domain/poi-reveal.mjs): if a
// matching building exists on this tile, that survivor "knows" it. If not,
// that's fine — no fallback, they just commuted in from a different tile.
function survivorWithBackground({ id, name, backgroundId, revealSeed }) {
  const poiReveal = professionPoiReveal(backgroundId);
  const revealRng = () => stableHash(`poi-reveal:${revealSeed}`) / 0x100000000;
  const revealedBuilding = poiReveal ? findRevealBuilding(poiReveal, buildingCollection.features, revealRng) : null;
  return {
    id,
    name,
    backgroundId,
    ...(revealedBuilding ? { revealedBuildingId: revealedBuilding.id } : {}),
  };
}

function rosterWithRandomBackgrounds(names, worldSeed) {
  return names.map((name, index) => survivorWithBackground({
    id: `survivor:${name.toLowerCase()}`,
    name,
    backgroundId: SURVIVOR_PROFESSION_IDS[stableHash(`background:${worldSeed}:${index}`) % SURVIVOR_PROFESSION_IDS.length],
    revealSeed: `${worldSeed}:${index}`,
  }));
}

// Debug-only: one survivor per profession, so ?playtest=professions can browse
// every card / pill / popup / poiReveal at once (see PLAYTEST_ALIASES). Not a
// real game state — the roster is deliberately huge.
const GALLERY_NAMES = [
  "Avery", "Blair", "Casey", "Devon", "Ellis", "Frankie", "Gray", "Harper",
  "Indy", "Jules", "Kai", "Lane", "Max", "Nico", "Oakley", "Parker", "Quinn",
  "Reese", "Sage", "Tatum", "Val", "Wren", "Yuki", "Zion",
];

function rosterOfEveryProfession(worldSeed) {
  return SURVIVOR_PROFESSION_IDS.map((backgroundId, index) => survivorWithBackground({
    id: `survivor:gallery-${index}`,
    name: GALLERY_NAMES[index % GALLERY_NAMES.length],
    backgroundId,
    revealSeed: `gallery:${worldSeed}:${index}`,
  }));
}

// Mark every "knows the area" building (a survivor's revealedBuildingId) as
// already scouted, holding a big haul — so it starts known (named, large supply cache)
// on the strategy map instead of an unknown grey shape. The matching run-time
// guarantee lives in scavengeStateForTarget (forcePromisingIds).
function withKnownBuildingIntel(seededCampaign) {
  const reports = Object.values(seededCampaign.survivorsById ?? {})
    .map((survivor) => survivor.revealedBuildingId)
    .filter(Boolean)
    .map((buildingId) => ({ buildingId, scavengeState: "promising", promising: true, itemCount: PROMISING_MAX_ITEMS }));
  return reports.length ? recordBuildingScouting(seededCampaign, reports) : seededCampaign;
}

function seedCampaign({ tutorialEnabled = false } = {}) {
  const worldSeed = Math.floor(Math.random() * 1e9);
  const survivors = rosterWithRandomBackgrounds(DEFAULT_ROSTER, worldSeed);
  const baseFeature = pickBaseBuilding(worldSeed);
  return withKnownBuildingIntel(createCampaignV4({
    campaignId: `run-${Date.now()}`,
    createdAt: new Date().toISOString(),
    worldSeed,
    survivors,
    tutorial: { enabled: tutorialEnabled },
    base: baseFeature ? baseRecordForFeature(baseFeature) : { coordinate: pickFallbackBaseCoordinate() },
  }));
}

function seedPlaytestCampaign(entry) {
  const worldSeed = stableHash(`playtest:${entry}`);
  const survivors = entry === "profession-gallery"
    ? rosterOfEveryProfession(worldSeed)
    : rosterWithRandomBackgrounds(DEFAULT_ROSTER, worldSeed);
  const baseFeature = pickBaseBuilding(worldSeed);
  return withKnownBuildingIntel(createCampaignV4({
    campaignId: `playtest-${entry}`,
    createdAt: "2026-01-01T00:00:00.000Z",
    worldSeed,
    survivors,
    tutorial: { enabled: entry.startsWith("tutorial-") },
    base: baseFeature ? baseRecordForFeature(baseFeature) : { coordinate: pickFallbackBaseCoordinate() },
  }));
}

function loadOrCreateCampaign() {
  if (playtestEntry) return seedPlaytestCampaign(playtestEntry);
  const stored = localStorage.getItem(SAVE_KEY);
  if (stored) {
    try { return ensureCampaignBaseBuilding(parseCampaignV4(stored)); } catch { /* fall through to a fresh run */ }
  }
  const fresh = seedCampaign();
  pendingNewGameLog = { reason: "created", campaign: fresh };
  return fresh;
}

function save() {
  if (playtestEntry) return;
  localStorage.setItem(SAVE_KEY, serializeCampaignV4(campaign));
}

function reportNewGameStart(reason, startedCampaign) {
  if (playtestEntry || !startedCampaign?.campaignId) return;
  const payload = {
    source: "app",
    reason,
    campaignId: startedCampaign.campaignId,
    worldSeed: startedCampaign.worldSeed,
    day: startedCampaign.day,
    survivorCount: Object.keys(startedCampaign.survivorsById ?? {}).length,
  };
  if (!authState.ready) {
    pendingNewGameLogs.push(payload);
    return;
  }
  sendNewGameActivity(payload);
}

function flushPendingNewGameLogs() {
  while (pendingNewGameLogs.length) sendNewGameActivity(pendingNewGameLogs.shift());
}

function authHeaders() {
  return authState.session?.access_token ? { "X-Supabase-Access-Token": authState.session.access_token } : {};
}

function sendNewGameActivity(payload) {
  fetch("/api/activity/new-game", {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify(payload),
  }).catch(() => {});
}

function authElement(id) {
  return document.getElementById(id);
}

function initDiscordAuth() {
  authState.returnPending = sessionStorage.getItem(AUTH_RETURN_PENDING_KEY) === "1"
    || new URLSearchParams(window.location.search).has("code")
    || new URLSearchParams(window.location.hash.slice(1)).has("access_token");
  const factory = window.supabase?.createClient;
  if (!factory) {
    authState.error = "Discord sign-in unavailable";
    authState.ready = true;
    renderAuthState();
    flushPendingNewGameLogs();
    return;
  }
  authState.client = factory(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
    },
  });
  authState.client.auth.onAuthStateChange((_event, session) => {
    void handleAuthSession(session);
  });
  void authState.client.auth.getSession()
    .then(({ data, error }) => {
      if (error) throw error;
      return handleAuthSession(data.session);
    })
    .catch((error) => {
      authState.error = authErrorMessage(error);
      authState.ready = true;
      renderAuthState();
      flushPendingNewGameLogs();
    });
}

async function handleAuthSession(session) {
  authState.session = session || null;
  authState.user = session?.user || null;
  authState.error = "";
  authState.ready = true;
  renderAuthState();
  flushPendingNewGameLogs();
  if (!authState.user) return;
  let pendingProfile = null;
  const shouldReportDiscordLogin = authState.returnPending;
  if (shouldReportDiscordLogin) {
    authState.returnPending = false;
    sessionStorage.removeItem(AUTH_RETURN_PENDING_KEY);
    pendingProfile = authProfileDataFromUser(authState.user, { profileWarning: "Saving profile data..." });
    authState.profileData = pendingProfile;
    showAuthConnectedDialog(pendingProfile);
  }
  if (shouldReportDiscordLogin) reportDiscordLogin(session);
  const savedProfile = await upsertUserProfile(authState.user);
  if (pendingProfile && savedProfile) showAuthConnectedDialog(savedProfile);
}

function isSignedIn() {
  return Boolean(authState.user);
}

function authDisplayName(user) {
  const metadata = user?.user_metadata || {};
  return metadata.global_name
    || metadata.user_name
    || metadata.preferred_username
    || metadata.full_name
    || metadata.name
    || user?.email
    || "Discord user";
}

function authDiscordUsername(user) {
  const metadata = user?.user_metadata || {};
  const identity = (user?.identities || []).find((candidate) => candidate.provider === "discord");
  const data = identity?.identity_data || {};
  return metadata.user_name
    || metadata.preferred_username
    || metadata.username
    || data.user_name
    || data.preferred_username
    || data.username
    || null;
}

function authDiscordUserId(user) {
  const identity = (user?.identities || []).find((candidate) => candidate.provider === "discord");
  return identity?.identity_data?.sub
    || identity?.identity_data?.id
    || user?.user_metadata?.provider_id
    || null;
}

function authContactEmail(user) {
  return user?.email || user?.user_metadata?.email || null;
}

function authProfileDataFromUser(user, overrides = {}) {
  const contactEmail = authContactEmail(user);
  return {
    displayName: authDisplayName(user),
    discordUsername: authDiscordUsername(user),
    discordUserId: authDiscordUserId(user),
    contactEmail,
    contactEmailSaved: Boolean(contactEmail),
    profileSynced: false,
    profileWarning: "",
    ...overrides,
  };
}

function profileMissingContactEmailColumn(error) {
  const message = authErrorMessage(error).toLowerCase();
  return message.includes("contact_email") && message.includes("schema cache");
}

async function upsertProfileRecord(profile) {
  const { error } = await authState.client
    .from("profiles")
    .upsert(profile, { onConflict: "id" });
  return error;
}

async function upsertUserProfile(user) {
  if (!authState.client || !user?.id) return null;
  const profileData = authProfileDataFromUser(user);
  const profile = {
    id: user.id,
    discord_user_id: profileData.discordUserId,
    display_name: profileData.displayName,
    contact_email: profileData.contactEmail,
    avatar_url: null,
  };
  let error = await upsertProfileRecord(profile);
  if (error && profileMissingContactEmailColumn(error)) {
    const fallbackProfile = { ...profile };
    delete fallbackProfile.contact_email;
    error = await upsertProfileRecord(fallbackProfile);
    profileData.contactEmailSaved = false;
    profileData.profileWarning = "Email could not be saved yet because the database schema has not refreshed. Sign-in still worked.";
  }
  if (error) {
    authState.error = `Profile sync failed: ${error.message}`;
    profileData.contactEmailSaved = false;
    profileData.profileWarning = `Profile sync failed: ${error.message}`;
    authState.profileData = profileData;
    renderAuthState();
    return profileData;
  }
  profileData.profileSynced = true;
  authState.profileData = profileData;
  renderAuthState();
  return profileData;
}

function renderAuthState() {
  const menuButtonEl = authElement("auth-menu-button");
  const actionEl = authElement("auth-menu-action");
  const statusEl = authElement("auth-menu-status");
  const discordButton = authElement("auth-discord-button");
  const discordLabel = authElement("auth-discord-button-label");
  const splashAuthButton = authElement("start-auth-button");
  const splashAuthNote = authElement("start-auth-note");
  const errorEl = authElement("auth-dialog-error");
  const signedIn = isSignedIn();
  const displayName = signedIn ? authDisplayName(authState.user) : "";
  if (actionEl) actionEl.textContent = signedIn ? "Sign out" : "Sign in with Discord";
  if (statusEl) statusEl.textContent = authState.error || (signedIn ? `Signed in as ${displayName}` : "Playing without an account");
  if (menuButtonEl) {
    menuButtonEl.classList.toggle("signed-in", signedIn);
    menuButtonEl.setAttribute("aria-label", signedIn ? `Signed in as ${displayName}. Sign out` : "Sign in with Discord");
  }
  if (discordButton) discordButton.disabled = !authState.client || signedIn;
  if (discordLabel) discordLabel.textContent = signedIn ? "Signed in" : "Sign in with Discord";
  if (splashAuthButton) {
    const splashAuthLabel = splashAuthButton.querySelector(".start-auth-label");
    if (splashAuthLabel) splashAuthLabel.textContent = signedIn ? `Logged in as ${displayName}` : "Login with Discord";
    splashAuthButton.classList.toggle("signed-in", signedIn);
    splashAuthButton.disabled = !startSplashReady || (!authState.client && !signedIn);
  }
  if (splashAuthNote) {
    splashAuthNote.textContent = authState.error || "Discord auth not required to play. Used for cloud saves, leaderboard, playing custom map tiles, bug and performance reporting assistance, dev/tester mode.";
  }
  if (errorEl) {
    errorEl.hidden = !authState.error;
    errorEl.textContent = authState.error;
  }
}

function showAuthConnectedDialog(profileData = authState.profileData) {
  const dialog = authElement("auth-connected-dialog");
  if (!dialog) return;
  authElement("auth-dialog")?.close();
  const savedEl = authElement("auth-connected-saved");
  if (savedEl) {
    const savedItems = [];
    if (profileData?.profileSynced) {
      if (profileData.displayName) savedItems.push(`Discord display name: ${profileData.displayName}`);
      if (profileData.discordUsername) savedItems.push(`Discord username: ${profileData.discordUsername}`);
      if (profileData.discordUserId) savedItems.push(`Discord user ID: ${profileData.discordUserId}`);
      if (profileData.contactEmail && profileData.contactEmailSaved !== false) savedItems.push(`Email: ${profileData.contactEmail}`);
    }
    savedEl.textContent = profileData?.profileSynced ? savedItems.join("; ") || "No profile fields were saved yet." : "Profile save in progress...";
  }
  const noteEl = authElement("auth-connected-note");
  if (noteEl) {
    noteEl.hidden = !profileData?.profileWarning;
    noteEl.textContent = profileData?.profileWarning || "";
  }
  if (typeof dialog.showModal === "function" && !dialog.open) dialog.showModal();
}

function showAuthDialog() {
  const dialog = authElement("auth-dialog");
  if (!dialog) return;
  authState.error = "";
  renderAuthState();
  if (typeof dialog.showModal === "function" && !dialog.open) dialog.showModal();
}

function requestSignOut() {
  if (!isSignedIn()) return;
  const dialog = authElement("auth-sign-out-dialog");
  if (!dialog) {
    if (window.confirm("Sign out?")) void signOut();
    return;
  }
  if (typeof dialog.showModal === "function" && !dialog.open) {
    dialog.returnValue = "";
    dialog.showModal();
  }
}

async function signInWithDiscord() {
  if (!authState.client) {
    authState.error = "Account sign-in is unavailable right now.";
    renderAuthState();
    return;
  }
  authState.returnPending = true;
  sessionStorage.setItem(AUTH_RETURN_PENDING_KEY, "1");
  const { error } = await authState.client.auth.signInWithOAuth({
    provider: "discord",
    options: {
      redirectTo: window.location.origin + window.location.pathname,
      scopes: "identify email",
    },
  });
  if (error) {
    sessionStorage.removeItem(AUTH_RETURN_PENDING_KEY);
    authState.error = authErrorMessage(error);
    renderAuthState();
  }
}

async function signOut() {
  if (!authState.client) return;
  const { error } = await authState.client.auth.signOut();
  if (error) {
    authState.error = authErrorMessage(error);
    renderAuthState();
    return;
  }
  authState.session = null;
  authState.user = null;
  authState.profileData = null;
  renderAuthState();
  setTopbarMenuOpen(false);
}

function reportDiscordLogin(session) {
  const userId = session?.user?.id || session?.access_token;
  if (!session?.access_token || !userId || reportedDiscordLoginUsers.has(userId)) return;
  reportedDiscordLoginUsers.add(userId);
  fetch("/api/activity/discord-login", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Supabase-Access-Token": session.access_token,
    },
    body: JSON.stringify({ source: "app" }),
  }).catch(() => {});
}

function authErrorMessage(error) {
  return error?.message || String(error || "Unknown auth error");
}

// Lazily generate (or regenerate a stale) base-defense layout onto the
// campaign's base. Lives here rather than in campaign-v4 because only the app
// can see the base's generated cell shape (currentBaseShape); campaign-v4
// stays shape-ignorant. Deterministic, so regenerating on load is safe.
function ensureBaseDefense() {
  if (!campaign.base) return;
  const shape = currentBaseShape();
  if (!shape) return;
  if (!isBaseDefenseValid(shape.cells, campaign.base.defense)) {
    const { wallState, safeRoomCellKey } = generateBaseLayout(shape.cells);
    if (!safeRoomCellKey) return;
    campaign = { ...campaign, base: { ...campaign.base, defense: { ...wallState, safeRoomCellKey } } };
    save();
  }
  // Once the layout exists, seed default room types (idempotent — no-op after
  // the first run or once the player has built anything).
  ensureBaseRoomDefaults();
}

// Seed default room TYPES onto the detected regions the first time a base has a
// layout (safe room -> Barracks, one region -> Kitchen; see seedDefaultRoomTypes).
// Only seeds when base.rooms is empty, so it never clobbers the player's builds.
function ensureBaseRoomDefaults() {
  if (!campaign.base) return;
  const defense = campaign.base.defense;
  if (!defense?.safeRoomCellKey) return;
  if (campaign.base.rooms && Object.keys(campaign.base.rooms).length) return;
  const shape = currentBaseShape();
  if (!shape) return;

  const { rooms } = detectRooms(shape.cells, defense);
  const seeded = seedDefaultRoomTypes(rooms, defense.safeRoomCellKey);
  campaign = { ...campaign, base: { ...campaign.base, rooms: seeded } };
  save();
}

// --- App state ----------------------------------------------------------

// Captured before loadOrCreateCampaign()/save() below can write the very
// first autosave, so it reflects whether this browser has ever played.
const isFirstVisit = !playtestEntry && !localStorage.getItem(SAVE_KEY);

let startSplashReady = false;
let campaign = loadOrCreateCampaign();
save();
if (pendingNewGameLog) {
  reportNewGameStart(pendingNewGameLog.reason, pendingNewGameLog.campaign);
  pendingNewGameLog = null;
}
let selectedSupplyRunSquadIds = new Set(defaultSupplyRunSquadIds(campaign));
let selectedGearPayload = null;
let squadSwapMenuEl = null;
let selectedPoiId = null;     // the selected building's targetId
let supplyRunsToday = 0;         // ephemeral: resets on reload (fine for the prototype)
let pendingCapacityWarning = false; // a field recruit pushed the roster over housing; warn on return to base
let activeTab = "survivors"; // overworld drawer tab: survivors | quests | base
let tacticalView = null;
let activeSupplyRun = null;      // { poiId, squadUnits, over } while a supply run is mounted
let activeDefense = false;  // true while a base defense is mounted (it reuses the tactical screen + chrome)
let defenseRender = null;   // { shape, wallState, detection, waveSize } while a defense is mounted — lets the tactical chrome rebuild the door/breach overlay from live field-object HP + resolve the win/lose check
let defenseResolved = false; // true once a mounted defense's outcome has been applied (guards the once-only resolution)
let map = null;
const routeCache = new Map(); // targetId -> base->target route (memoized)
let mapStyleReady = false;
let baseMarker = null;
let baseLayerHandlersBound = false;
let baseInteriorHandlersBound = false;
let hoveredRegionKey = null;  // the base-tab room region under the cursor (whole-region hover)
let roomBuildPopup = null;    // the "build a room" popup over a clicked region
let targetPopup = null;       // the floating "pick a target" popup above a building
let popupClosingProgrammatically = false; // true while WE remove the popup, so its close event doesn't clear the selection
let preTacticalCamera = null;     // marks that supply-run return should fly back to the default overworld map
let preDefenseCamera = null; // camera saved before a defense flies in, restored on exit
let defenseIsFinal = false;  // true while the day-20 rescue assault is mounted, so its completion ends the run
let resolveMapReady = null;
const mapReady = new Promise((resolve) => { resolveMapReady = resolve; });
let resolveNextTacticalMounted = null;
const tutorialView = createTutorialView({
  boxElement: document.getElementById("tutorial-box"),
  modalElement: document.getElementById("tutorial-modal"),
  onComplete: (lessonId) => {
    const next = completeTutorialLesson(campaign, lessonId);
    if (next === campaign) return;
    campaign = next;
    save();
    renderTutorial();
  },
});

function setScreen(screen) {
  document.body.dataset.screen = screen;
  syncDrawerCollapseState();
  renderTutorial();
}

function tutorialScreenContext() {
  const screen = document.body.dataset.screen === "defense"
    ? TUTORIAL_CONTEXTS.baseDefense
    : document.body.dataset.screen;
  return {
    screen,
    activeTab,
    selectedPoiId,
    tacticalActive: Boolean(tacticalView),
    tacticalOver: Boolean(tacticalView?.isOver()),
    campaign,
  };
}

function renderTutorial() {
  const visibleLessons = availableTutorialLessons(tutorialScreenContext(), campaign.tutorial);
  tutorialView.render({ enabled: Boolean(campaign.tutorial?.enabled), visibleLessons });
  syncTutorialDevControls();
}

function showWelcomeDialog() {
  const dialog = document.getElementById("welcome-dialog");
  if (!dialog || dialog.open) return;
  if (typeof dialog.showModal === "function") dialog.showModal();
}

function hideStartSplash() {
  const splash = document.getElementById("start-splash");
  if (!splash) return;
  splash.hidden = true;
}

function setStartSplashReady(ready) {
  startSplashReady = ready;
  const splash = document.getElementById("start-splash");
  if (splash) {
    splash.dataset.ready = String(ready);
    const versionEl = document.getElementById("start-version");
    if (versionEl) versionEl.textContent = document.body.dataset.appVersion || "V4";
  }
  const tutorialButton = document.getElementById("start-tutorial-button");
  if (tutorialButton) {
    tutorialButton.disabled = !ready;
    tutorialButton.textContent = ready ? "Tutorial" : "Loading...";
  }
  const newGameButton = document.getElementById("start-new-game-button");
  if (newGameButton) newGameButton.disabled = !ready;
  renderAuthState();
}

function beginTutorialFromSplash() {
  if (!startSplashReady) return;
  campaign = setTutorialEnabled(campaign, true);
  save();
  hideStartSplash();
  renderTutorial();
  if (isFirstVisit) showWelcomeDialog();
}

function beginNewGameFromSplash() {
  if (!startSplashReady) return;
  hideStartSplash();
  resetRun({ tutorialEnabled: false });
}

// The profession pill's popup: flavor text for who this survivor was, plus
// what their category actually does (or "design only" if it's not wired into
// the sim yet — see professions-v4.mjs's PROFESSION_CATEGORY_DETAILS).
function showProfessionDialog(survivor) {
  const dialog = document.getElementById("profession-dialog");
  if (!dialog || !survivor) return;
  const category = professionCategory(survivor.backgroundId);
  const details = categoryDetails(category);
  document.getElementById("profession-dialog-category").textContent = category === "none" ? "No specialty" : `Skill: ${details.label}`;
  document.getElementById("profession-dialog-title").textContent = professionName(survivor.backgroundId) || "Unassigned";
  document.getElementById("profession-dialog-notes").textContent = professionNotes(survivor.backgroundId)
    || "Nothing else is known about their background.";
  document.getElementById("profession-dialog-skill-summary").textContent = category === "none"
    ? "No unlock — just an ordinary person along for the ride."
    : details.summary + (details.implemented ? "" : " (Design only — not yet wired into this build.)");
  const revealEl = document.getElementById("profession-dialog-reveal");
  const revealedBuilding = survivor.revealedBuildingId ? buildingTargets.get(survivor.revealedBuildingId) : null;
  const poiReveal = professionPoiReveal(survivor.backgroundId);
  revealEl.hidden = !revealedBuilding;
  if (revealedBuilding) revealEl.textContent = `Knows the area — ${poiReveal?.relation ?? "a place they knew"}: ${revealedBuilding.name}. It's already scouted on your map and worth a supply run.`;
  if (typeof dialog.showModal === "function") dialog.showModal();
}

function showEndgameBriefingIfNeeded() {
  if (!shouldShowEndgameBriefing(campaign)) return;
  const dialog = document.getElementById("endgame-dialog");
  if (!dialog || dialog.open) return;
  if (typeof dialog.showModal === "function") dialog.showModal();
}

document.getElementById("endgame-dialog")?.addEventListener("close", () => {
  if (!shouldShowEndgameBriefing(campaign)) return;
  campaign = acknowledgeEndgameBriefing(campaign);
  save();
  renderOverworld();
});

// --- Helpers ------------------------------------------------------------

function survivorName(survivorId) { return getSurvivorV4(campaign, survivorId)?.name ?? survivorId; }
function itemName(itemId) { return getItem(itemId)?.name ?? itemId; }
function routeToTarget(id) {
  const target = poiById(id);
  const key = `${baseCoordinate().join(",")}:${id}`;
  if (!routeCache.has(key)) {
    routeCache.set(key, target ? findRouteBetweenPoints(roadGraph, baseCoordinate(), target.coordinate) : null);
  }
  return routeCache.get(key);
}
// A target is reachable today if there's a walkable route and the round trip
// plus on-site time fits the day's budget — and there's a supply run left to spend.
function isTargetReachable(id) {
  if (supplyRunsLeftToday() <= 0) return false;
  const route = routeToTarget(id);
  if (!route?.reachable) return false;
  const roundTripMinutes = (2 * route.distanceMeters) / ON_FOOT_SPEED_MPM;
  return roundTripMinutes + ON_SITE_MINUTES_PER_POI <= DAY_BUDGET_MINUTES;
}

function eligibleSupplyRunSurvivors(currentCampaign) {
  return activeSurvivorsV4(currentCampaign).filter((survivor) => !isSurvivorWounded(survivor));
}

function defaultSupplyRunSquadIds(currentCampaign) {
  return eligibleSupplyRunSurvivors(currentCampaign).slice(0, MAX_SUPPLY_RUN_SQUAD_SIZE).map((survivor) => survivor.id);
}

function normalizeSelectedSquadIds() {
  const eligibleIds = new Set(eligibleSupplyRunSurvivors(campaign).map((survivor) => survivor.id));
  selectedSupplyRunSquadIds = new Set([...selectedSupplyRunSquadIds].filter((id) => eligibleIds.has(id)).slice(0, MAX_SUPPLY_RUN_SQUAD_SIZE));
}

function selectedSquad() {
  normalizeSelectedSquadIds();
  return activeSurvivorsV4(campaign).filter((survivor) => selectedSupplyRunSquadIds.has(survivor.id));
}
function supplyRunsLeftToday() { return MAX_POIS_PER_DAY - supplyRunsToday; }

function logLine(className, text) {
  const list = document.getElementById("log");
  const item = document.createElement("li");
  item.className = className;
  item.textContent = text;
  list.appendChild(item);
  list.scrollTop = list.scrollHeight;
}

// --- Overworld screen ---------------------------------------------------

function renderOverworld() {
  const alive = activeSurvivorsV4(campaign);
  const rescueDay = campaignRescueDay(campaign);
  document.getElementById("survivors-heading").textContent = `Day ${campaign.day} Scouts`;
  document.getElementById("hud-day").textContent = campaign.day >= 2 ? `${campaign.day} / ${rescueDay}` : campaign.day;
  document.getElementById("hud-supply-runs").textContent = supplyRunsToday;
  document.getElementById("hud-supply-runs-max").textContent = MAX_POIS_PER_DAY;
  document.getElementById("hud-equipment-value").textContent = availableEquipmentValue(campaign);
  const food = pantryFood(campaign);
  const quota = campaignDailyQuota(campaign);
  const foodEl = document.getElementById("hud-food");
  foodEl.textContent = formatResource(food);
  foodEl.style.color = food < quota ? "#ff8a5a" : "#8fdca0"; // warn when the pantry can't cover tonight
  document.getElementById("hud-quota").textContent = formatResource(quota);
  document.getElementById("hud-materials").textContent = formatResource(baseMaterials(campaign));
  document.getElementById("hud-housing").textContent = campaignHousingCapacity(campaign);
  const globalThreatEl = document.getElementById("hud-global-threat");
  globalThreatEl.textContent = Math.round(currentGlobalThreat(campaign));
  globalThreatEl.classList.toggle("critical", globalThreatCritical(campaign));
  document.getElementById("hud-alive").textContent = alive.length;

  renderDrawerTabs();
  renderBaseStats();
  syncDevMenu();
  syncOverworldBuildingScouting();
  updateSelectedBuilding();
  syncBaseMarker();
  syncOverworldBuildingsFilter();
  syncBaseBuildingLayer();
  syncBaseGridLayer();
  syncBaseInteriorLayers();
  syncTargetPopup();
  updateSupplyRunBoundary();
  updatePoiThreatCircles();
  updateRouteLine();
  renderRoster();

  const actionBtn = document.getElementById("btn-tactical-advance");
  actionBtn.classList.add("day-mode");
  actionBtn.classList.remove("return-mode");
  actionBtn.querySelector(".label").textContent = "End Day ▶";
  actionBtn.querySelector(".hotkey").textContent = "Nightfall";
  actionBtn.disabled = alive.length === 0 || campaignConcluded(campaign);
  renderTutorial();
}

function syncOverworldBuildingScouting() {
  const source = map?.getSource("overworld-buildings");
  if (!source) return;
  source.setData(overworldBuildingsForCampaign(campaign));
}

// Overworld drawer tabs (Base / Survivors / Quests). Plain visibility toggle —
// the section renderers (renderBaseStats / renderRoster) keep filling their own
// content every tick regardless of which tab is showing.
const DRAWER_TABS = ["survivors", "quests", "base"];
const mobileDrawerQuery = window.matchMedia("(max-width: 760px)");
let mobileDrawerCollapsed = mobileDrawerQuery.matches;

function renderDrawerTabs() {
  for (const tab of DRAWER_TABS) {
    const section = document.getElementById(`tab-${tab}`);
    if (section) section.hidden = tab !== activeTab;
  }
  for (const button of document.querySelectorAll("#drawer-tabs .drawer-tab")) {
    const active = button.dataset.tab === activeTab;
    button.classList.toggle("active", active);
    button.setAttribute("aria-selected", String(active));
  }
}

function setActiveTab(tab) {
  if (!DRAWER_TABS.includes(tab)) return;
  if (tab !== activeTab) {
    activeTab = tab;
    renderDrawerTabs();
  }
  if (tab === "base") {
    // Convenience: jump to the zoom where the room layers render (minzoom
    // BASE_GRID_MIN_ZOOM). Runs even when re-selecting the tab so clicking it
    // again re-zooms. The room layers themselves are NOT gated on this tab —
    // see syncBaseInteriorLayers(), which shows them purely by zoom level so
    // they're visible/buildable from any tab once you're zoomed into the base.
    zoomToBaseGrid();
  }
}

function syncDrawerCollapseState() {
  const collapsed = mobileDrawerQuery.matches && mobileDrawerCollapsed;
  const expanded = mobileDrawerQuery.matches && !mobileDrawerCollapsed;
  document.body.classList.toggle("drawer-collapsed", collapsed);
  document.body.classList.toggle("drawer-expanded", expanded);
  syncResourceBarPlacement();
  const button = document.getElementById("drawer-toggle");
  if (!button) return;
  const activeDrawerId = document.body.dataset.screen === "tactical" ? "tactical-drawer" : "overworld-drawer";
  button.textContent = collapsed ? "‹" : "›";
  button.setAttribute("aria-controls", activeDrawerId);
  button.setAttribute("aria-expanded", String(!collapsed));
  button.setAttribute("aria-label", collapsed ? "Expand drawer" : "Collapse drawer");
}

function syncResourceBarPlacement() {
  const stats = document.querySelector(".topbar-stats");
  const header = document.querySelector("header.topbar");
  if (!stats || !header) return;
  if (mobileDrawerQuery.matches) {
    const activeDrawerId = document.body.dataset.screen === "tactical" ? "tactical-drawer" : "overworld-drawer";
    const drawer = document.getElementById(activeDrawerId);
    if (drawer && stats.parentElement !== drawer) drawer.insertBefore(stats, drawer.firstElementChild);
    return;
  }
  const hiddenStats = header.querySelector("span[hidden]");
  if (stats.parentElement !== header) header.insertBefore(stats, hiddenStats);
}

function setMobileDrawerCollapsed(collapsed) {
  mobileDrawerCollapsed = collapsed;
  syncDrawerCollapseState();
}

mobileDrawerQuery.addEventListener("change", (event) => {
  mobileDrawerCollapsed = event.matches;
  syncDrawerCollapseState();
});

syncDrawerCollapseState();

// --- Overworld map (MapLibre) ------------------------------------------

const EMPTY_COLLECTION = { type: "FeatureCollection", features: [] };

function closedRing(coordinates) {
  if (!coordinates.length) return coordinates;
  const first = coordinates[0];
  const last = coordinates[coordinates.length - 1];
  return first[0] === last[0] && first[1] === last[1] ? coordinates : [...coordinates, first];
}

function tileFogFeature(bbox) {
  const width = bbox.east - bbox.west;
  const height = bbox.north - bbox.south;
  const padLon = Math.max(width * 3, 0.08);
  const padLat = Math.max(height * 3, 0.06);
  const outer = closedRing([
    [bbox.west - padLon, bbox.south - padLat],
    [bbox.east + padLon, bbox.south - padLat],
    [bbox.east + padLon, bbox.north + padLat],
    [bbox.west - padLon, bbox.north + padLat],
  ]);
  const tile = closedRing([
    [bbox.west, bbox.south],
    [bbox.east, bbox.south],
    [bbox.east, bbox.north],
    [bbox.west, bbox.north],
  ]);
  return { type: "Feature", properties: {}, geometry: { type: "Polygon", coordinates: [outer, tile] } };
}

function paddedBoundsForBbox(bbox, paddingRatio) {
  const width = bbox.east - bbox.west;
  const height = bbox.north - bbox.south;
  const padLon = width * paddingRatio;
  const padLat = height * paddingRatio;
  return [[bbox.west - padLon, bbox.south - padLat], [bbox.east + padLon, bbox.north + padLat]];
}

let overworldMapBounds = null;
let overworldMinZoom = null;

function setOverworldCameraConstraints(enabled) {
  if (!map) return;
  if (enabled) {
    if (overworldMapBounds) map.setMaxBounds(overworldMapBounds);
    if (Number.isFinite(overworldMinZoom)) map.setMinZoom(overworldMinZoom);
    return;
  }
  map.setMaxBounds(null);
  map.setMinZoom(0);
}

function fitDefaultOverworldCamera({ duration = 0 } = {}) {
  if (!map) return;
  const bbox = tileGeometry.bbox;
  map.fitBounds([[bbox.west, bbox.south], [bbox.east, bbox.north]], { padding: 44, duration });
}

function initMap() {
  const bbox = tileGeometry.bbox;
  map = new maplibregl.Map({
    container: "map",
    style: "https://tiles.openfreemap.org/styles/positron",
    center: [(bbox.west + bbox.east) / 2, (bbox.south + bbox.north) / 2],
    zoom: 15,
    attributionControl: false,
  });
  map.addControl(new maplibregl.AttributionControl({ compact: true }), "bottom-right");
  map.addControl(new maplibregl.NavigationControl({ showCompass: false }), "bottom-right");
  map.on("load", () => {
    mapStyleReady = true;
    fitDefaultOverworldCamera();
    overworldMapBounds = paddedBoundsForBbox(bbox, OVERWORLD_BOUNDS_PADDING_RATIO);
    overworldMinZoom = Math.max(0, map.getZoom() - OVERWORLD_MIN_ZOOM_DELTA);
    setOverworldCameraConstraints(true);

    // Every building is a clickable supply-run target. A separate highlight source
    // lifts the selected one (string feature ids don't drive feature-state
    // reliably, so we swap a one-feature source instead).
    // Own 100% of building pixels: hide Positron's vendor building layer and draw
    // every building from our own GeoJSON as a solid fill (styling tuned later in
    // the dark-basemap pass). Removes ghost buildings and lets the base render as
    // its forked shape. See base-map-render-lab.html.
    if (map.getLayer(VENDOR_BUILDING_LAYER_ID)) map.setLayoutProperty(VENDOR_BUILDING_LAYER_ID, "visibility", "none");
    map.addSource(OVERWORLD_TILE_FOG_SOURCE_ID, { type: "geojson", data: { type: "FeatureCollection", features: [tileFogFeature(bbox)] } });
    map.addSource("overworld-buildings", { type: "geojson", data: overworldBuildingsForCampaign(campaign) });
    map.addLayer({
      id: "overworld-buildings-fill",
      type: "fill",
      source: "overworld-buildings",
      paint: {
        "fill-color": [
          "case",
          [">=", ["get", "itemCount"], 2], "#1f8f9b",
          [">", ["get", "itemCount"], 0], "#7ec8cf",
          ["==", ["get", "scoutingState"], "emptied"], "#515b63",
          "#c4c9cd",
        ],
        "fill-opacity": ["case", ["==", ["get", "scouted"], true], 0.78, 0.62],
      },
    });
    map.addLayer({
      id: "overworld-buildings-line",
      type: "line",
      source: "overworld-buildings",
      paint: {
        "line-color": [
          "case",
          [">=", ["get", "itemCount"], 2], "#8ee9f0",
          [">", ["get", "itemCount"], 0], "#d0f5f7",
          ["==", ["get", "scoutingState"], "emptied"], "#404950",
          "#eef2f4",
        ],
        "line-width": ["case", ["==", ["get", "scouted"], true], 1.05, 0.75],
        "line-opacity": ["case", ["==", ["get", "scouted"], true], 0.82, 0.48],
      },
    });
    map.addSource("overworld-building-selected", { type: "geojson", data: EMPTY_COLLECTION });
    map.addLayer({ id: "overworld-building-selected-fill", type: "fill", source: "overworld-building-selected", paint: { "fill-color": "#7bb8ff", "fill-opacity": 0.42 } });
    map.addLayer({ id: "overworld-building-selected-line", type: "line", source: "overworld-building-selected", paint: { "line-color": "#d7ecff", "line-width": 2.25 } });

    map.on("click", "overworld-buildings-fill", (event) => {
      const id = event.features?.[0]?.properties?.targetId;
      if (id == null || !buildingTargets.has(id)) return;
      selectedPoiId = id;
      renderOverworld();
    });
    map.on("mouseenter", "overworld-buildings-fill", () => { map.getCanvas().style.cursor = "pointer"; });
    map.on("mouseleave", "overworld-buildings-fill", () => { map.getCanvas().style.cursor = ""; });

    // Supply-run boundary circle (the 150m tactical-zone radius the run will build
    // around the target). Added after buildings so it draws on top.
    map.addSource("supply-run-boundary", { type: "geojson", data: EMPTY_COLLECTION });
    map.addLayer({
      id: "supply-run-boundary-fill",
      type: "fill",
      source: "supply-run-boundary",
      paint: { "fill-color": "#e0b75b", "fill-opacity": 0.06 },
    });
    map.addLayer({
      id: "supply-run-boundary-line",
      type: "line",
      source: "supply-run-boundary",
      layout: { "line-cap": "round", "line-join": "round" },
      paint: { "line-color": "#e0b75b", "line-width": 1.5, "line-opacity": 0.5, "line-dasharray": [2, 2] },
    });

    // Persistent per-POI threat circles: a supply run leaves threat over its whole
    // radius, shown here so hot ground stays visible between supply runs and visibly
    // cools (opacity drops) as it decays each day. Added before the route so
    // the route/selection still read on top.
    map.addSource("poi-threat", { type: "geojson", data: EMPTY_COLLECTION });
    map.addLayer({
      id: "poi-threat-fill",
      type: "fill",
      source: "poi-threat",
      paint: {
        "fill-color": "#ff5a3c",
        "fill-opacity": ["interpolate", ["linear"], ["get", "threat"], 0, 0.04, THREAT_MAX, 0.34],
      },
    });
    map.addLayer({
      id: "poi-threat-line",
      type: "line",
      source: "poi-threat",
      paint: {
        "line-color": "#ff7a4a",
        "line-width": 1,
        "line-opacity": ["interpolate", ["linear"], ["get", "threat"], 0, 0.15, THREAT_MAX, 0.6],
      },
    });

    map.addSource("selected-route", { type: "geojson", data: EMPTY_COLLECTION });
    map.addLayer({
      id: "selected-route-glow",
      type: "line",
      source: "selected-route",
      layout: { "line-cap": "round", "line-join": "round" },
      paint: { "line-color": "#e0b75b", "line-width": 10, "line-blur": 5, "line-opacity": 0.24 },
    });
    map.addLayer({
      id: "selected-route-line",
      type: "line",
      source: "selected-route",
      layout: { "line-cap": "round", "line-join": "round" },
      paint: { "line-color": "#e0b75b", "line-width": 4, "line-opacity": 0.92, "line-dasharray": [0.45, 1.1] },
    });
    // Drawn late in the overworld stack so off-tile basemap/building polygons
    // cannot poke over the mask and create jagged tile-edge silhouettes.
    map.addLayer({
      id: OVERWORLD_TILE_FOG_FILL_LAYER_ID,
      type: "fill",
      source: OVERWORLD_TILE_FOG_SOURCE_ID,
      paint: { "fill-color": "#11161a", "fill-opacity": 0.58 },
    });
    map.addLayer({
      id: OVERWORLD_TILE_FOG_LINE_LAYER_ID,
      type: "line",
      source: OVERWORLD_TILE_FOG_SOURCE_ID,
      paint: { "line-color": "#d8e1e7", "line-width": 1, "line-opacity": 0.18 },
    });

    updateSelectedBuilding();
    syncOverworldBuildingsFilter();
    syncBaseBuildingLayer();
    syncBaseGridLayer();
    syncBaseInteriorLayers();
    updateSupplyRunBoundary();
    updatePoiThreatCircles();
    updateRouteLine();
    resolveMapReady?.();
  });

  const baseElement = document.createElement("div");
  baseElement.className = "base-marker-wrap";
  baseElement.setAttribute("aria-label", "Base");
  baseElement.title = "Base";
  baseElement.innerHTML = `
    <svg class="base-marker" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M3 11.4 12 4l9 7.4v8.1a1.5 1.5 0 0 1-1.5 1.5h-5.2v-5.8H9.7V21H4.5A1.5 1.5 0 0 1 3 19.5z"></path>
    </svg>`;
  baseElement.addEventListener("click", (event) => {
    event.stopPropagation();
    zoomToBaseGrid();
  });
  baseMarker = new maplibregl.Marker({ element: baseElement }).setLngLat(baseCoordinate()).addTo(map);
  syncBaseMarkerZoomVisibility();
  map.on("zoom", syncBaseMarkerZoomVisibility);
}

// Lift the selected building out of the field via the highlight source.
function updateSelectedBuilding() {
  const source = map?.getSource("overworld-building-selected");
  if (!source) return;
  const target = selectedPoiId ? buildingTargets.get(selectedPoiId) : null;
  source.setData(target?.feature ? { type: "FeatureCollection", features: [target.feature] } : EMPTY_COLLECTION);
}

function syncBaseMarker() {
  if (!baseMarker) return;
  baseMarker.setLngLat(baseCoordinate());
  syncBaseMarkerZoomVisibility();
}

function syncBaseMarkerZoomVisibility() {
  if (!map || !baseMarker) return;
  baseMarker.getElement().classList.toggle("hidden-by-zoom", map.getZoom() >= BASE_GRID_MIN_ZOOM);
}

// Exclude the base building from the all-buildings layer so only its forked
// shape (base-building source) renders — no real footprint poking out underneath.
function syncOverworldBuildingsFilter() {
  if (!map || !map.getLayer("overworld-buildings-fill")) return;
  const baseId = campaign.base?.buildingId ? String(campaign.base.buildingId) : null;
  const filter = baseId ? ["!=", ["to-string", ["get", "targetId"]], baseId] : null;
  map.setFilter("overworld-buildings-fill", filter);
  map.setFilter("overworld-buildings-line", filter);
}

function syncBaseBuildingLayer() {
  if (!map || !mapStyleReady) return;
  const data = baseShapeOutlineToGeoJSON(currentBaseShape());
  const source = map.getSource(BASE_BUILDING_SOURCE_ID);
  if (source) {
    source.setData(data);
    bindBaseLayerHandlers();
    return;
  }
  map.addSource(BASE_BUILDING_SOURCE_ID, { type: "geojson", data });
  map.addLayer({
    id: BASE_BUILDING_FILL_LAYER_ID,
    type: "fill",
    source: BASE_BUILDING_SOURCE_ID,
    // The green fill reads the base as a blob when zoomed out, but fades away
    // as you zoom into the grid/rooms so it doesn't wash out the interior
    // detail (walls, room colors, labels). The green outline stays.
    paint: { "fill-color": "#6fdc8c", "fill-opacity": ["interpolate", ["linear"], ["zoom"], 18.5, 0.24, 20, 0] },
  });
  map.addLayer({
    id: BASE_BUILDING_OUTLINE_LAYER_ID,
    type: "line",
    source: BASE_BUILDING_SOURCE_ID,
    paint: { "line-color": "#a8ffc0", "line-width": 3 },
  });
  bindBaseLayerHandlers();
}

function bindBaseLayerHandlers() {
  if (!map || baseLayerHandlersBound || !map.getLayer(BASE_BUILDING_FILL_LAYER_ID)) return;
  baseLayerHandlersBound = true;
  // Only zoom in from far out; once we're already at the grid, a click that
  // lands on a room should reach the room-fill handler, not recentre the map.
  map.on("click", BASE_BUILDING_FILL_LAYER_ID, () => { if (map.getZoom() < BASE_GRID_TARGET_ZOOM) zoomToBaseGrid(); });
  map.on("mouseenter", BASE_BUILDING_FILL_LAYER_ID, () => { map.getCanvas().style.cursor = "pointer"; });
  map.on("mouseleave", BASE_BUILDING_FILL_LAYER_ID, () => { map.getCanvas().style.cursor = ""; });
}

function zoomToBaseGrid() {
  if (!map) return;
  map.easeTo({
    center: baseCoordinate(),
    zoom: Math.max(map.getZoom(), BASE_GRID_TARGET_ZOOM),
    duration: 700,
  });
}

function baseGridFeatureCollection() {
  const shape = currentBaseShape();
  const key = `${campaign.base?.buildingId ?? ""}:${shape?.cells?.size ?? 0}`;
  if (baseGridMapCache.key === key && baseGridMapCache.data) return baseGridMapCache.data;
  const data = baseShapeCellsToGeoJSON(shape);
  baseGridMapCache = { key, data };
  return data;
}

function syncBaseGridLayer() {
  if (!map || !mapStyleReady) return;
  const data = baseGridFeatureCollection();
  const source = map.getSource(BASE_GRID_SOURCE_ID);
  if (source) {
    source.setData(data);
    return;
  }
  map.addSource(BASE_GRID_SOURCE_ID, { type: "geojson", data });
  map.addLayer({
    id: BASE_GRID_FILL_LAYER_ID,
    type: "fill",
    source: BASE_GRID_SOURCE_ID,
    minzoom: BASE_GRID_MIN_ZOOM,
    paint: { "fill-color": "#b7c2c9", "fill-opacity": 0.09 },
  }, BASE_BUILDING_OUTLINE_LAYER_ID);
  map.addLayer({
    id: BASE_GRID_LINE_LAYER_ID,
    type: "line",
    source: BASE_GRID_SOURCE_ID,
    minzoom: BASE_GRID_MIN_ZOOM,
    // Kept light and subtle so the dark base-defense walls read as the
    // structure and the grid is just faint guides beneath them.
    paint: { "line-color": "#dfe6ea", "line-width": 1, "line-opacity": 0.5 },
  }, BASE_BUILDING_OUTLINE_LAYER_ID);
}

// --- Base-tab interior overlay (rooms/walls/doors on the hero map) ---------

// Detected rooms + wall/door state for the current base, or null if there's no
// layout yet. safeRoomId maps the persisted safe-room signature to the detected
// room id used by the geometry helpers.
function baseInteriorContext() {
  if (!campaign.base) return null;
  const shape = currentBaseShape();
  const defense = campaign.base.defense;
  if (!shape || !defense?.safeRoomCellKey) return null;
  const detection = detectRooms(shape.cells, defense);
  const safeRoom = detection.rooms.find((room) => roomSignature(room) === defense.safeRoomCellKey);
  return { shape, defense, detection, safeRoomId: safeRoom?.id ?? null };
}

// Per-cell room polygons carrying regionKey + built type, so the fill tints by
// type and hover/click can resolve the whole region a cell belongs to.
function baseRoomsCollection(ctx) {
  const builtRooms = campaign.base?.rooms ?? {};
  const roomProps = new Map();
  for (const room of ctx.detection.rooms) {
    const regionKey = roomSignature(room);
    roomProps.set(room.id, { regionKey, type: builtRooms[regionKey] ?? "", buildable: !room.isCorridor });
  }
  return roomsToGeoJSON(ctx.shape, ctx.detection.rooms, ctx.safeRoomId, [], roomProps);
}

// One label per real room (corridors skipped) at an interior cell, showing the
// built type (or "Empty") and marking the safe room.
function baseRoomLabelsCollection(ctx) {
  const builtRooms = campaign.base?.rooms ?? {};
  const toWorld = makeToWorld(ctx.shape);
  const features = [];
  for (const room of ctx.detection.rooms) {
    if (room.isCorridor) continue;
    const regionKey = roomSignature(room);
    const [col, row] = labelCellFor(room);
    const type = builtRooms[regionKey];
    const name = type ? (BASE_ROOM_CATALOG[type]?.name ?? titleCase(type)) : "Empty";
    const label = regionKey === ctx.defense.safeRoomCellKey ? `${name}\n(Safe Room)` : name;
    features.push({ type: "Feature", properties: { label }, geometry: { type: "Point", coordinates: toWorld(col + 0.5, row + 0.5) } });
  }
  return { type: "FeatureCollection", features };
}

// A representative interior cell for a room's label: the member cell closest to
// the room's average position, so the label sits inside non-rectangular rooms.
function labelCellFor(room) {
  let sumRow = 0, sumCol = 0, count = 0;
  for (const key of room.cellKeys) { const [r, c] = key.split(":").map(Number); sumRow += r; sumCol += c; count += 1; }
  const avgRow = sumRow / count, avgCol = sumCol / count;
  let best = null, bestDist = Infinity;
  for (const key of room.cellKeys) {
    const [r, c] = key.split(":").map(Number);
    const dist = (r - avgRow) ** 2 + (c - avgCol) ** 2;
    if (dist < bestDist) { bestDist = dist; best = [c, r]; }
  }
  return best;
}

// Fill-opacity that lifts the whole hovered region (its cells share regionKey)
// and keeps corridors faint. Rebuilt whenever the hovered region changes.
function baseRoomsFillOpacity() {
  return [
    "case",
    ["==", ["get", "buildable"], false], 0.1,
    ["==", ["get", "regionKey"], hoveredRegionKey ?? "__no_region__"], 0.62,
    0.34,
  ];
}

const BASE_ROOMS_FILL_COLOR = [
  "match", ["get", "type"],
  "barracks", ROOM_TYPE_FILL_COLORS.barracks,
  "kitchen", ROOM_TYPE_FILL_COLORS.kitchen,
  "bedroom", ROOM_TYPE_FILL_COLORS.bedroom,
  "storage", ROOM_TYPE_FILL_COLORS.storage,
  ROOM_UNBUILT_FILL_COLOR,
];

function syncBaseInteriorLayers() {
  if (!map || !mapStyleReady) return;
  const ctx = baseInteriorContext();
  if (!ctx) { setLayersVisible(BASE_INTERIOR_LAYER_IDS, false); return; }

  const roomsData = baseRoomsCollection(ctx);
  const edgesData = wallStateToGeoJSON(ctx.shape, ctx.defense);
  const doorsData = doorSwingsToGeoJSON(ctx.shape, ctx.shape.cells, ctx.defense);
  const labelsData = baseRoomLabelsCollection(ctx);

  if (map.getSource(BASE_ROOMS_SOURCE_ID)) {
    map.getSource(BASE_ROOMS_SOURCE_ID).setData(roomsData);
    map.getSource(BASE_EDGES_SOURCE_ID).setData(edgesData);
    map.getSource(BASE_DOORS_SOURCE_ID).setData(doorsData);
    map.getSource(BASE_ROOM_LABELS_SOURCE_ID).setData(labelsData);
    map.setPaintProperty(BASE_ROOMS_FILL_LAYER_ID, "fill-opacity", baseRoomsFillOpacity());
  } else {
    map.addSource(BASE_ROOMS_SOURCE_ID, { type: "geojson", data: roomsData });
    map.addLayer({
      id: BASE_ROOMS_FILL_LAYER_ID, type: "fill", source: BASE_ROOMS_SOURCE_ID, minzoom: BASE_GRID_MIN_ZOOM,
      paint: { "fill-color": BASE_ROOMS_FILL_COLOR, "fill-opacity": baseRoomsFillOpacity() },
    }, BASE_BUILDING_OUTLINE_LAYER_ID);

    // Walls read as dark structure (matching base defense); doors are the
    // architectural swing symbol in the wall gap.
    map.addSource(BASE_EDGES_SOURCE_ID, { type: "geojson", data: edgesData });
    map.addLayer({
      id: BASE_EDGES_LINE_LAYER_ID, type: "line", source: BASE_EDGES_SOURCE_ID, minzoom: BASE_GRID_MIN_ZOOM,
      filter: ["==", ["get", "kind"], "wall"],
      layout: { "line-cap": "round", "line-join": "round" },
      paint: { "line-color": "#10161d", "line-width": 5 },
    });
    map.addSource(BASE_DOORS_SOURCE_ID, { type: "geojson", data: doorsData });
    map.addLayer({
      id: BASE_DOORS_LINE_LAYER_ID, type: "line", source: BASE_DOORS_SOURCE_ID, minzoom: BASE_GRID_MIN_ZOOM,
      layout: { "line-cap": "round", "line-join": "round" },
      paint: { "line-color": "#3fd0e0", "line-width": 2.5 },
    });

    map.addSource(BASE_ROOM_LABELS_SOURCE_ID, { type: "geojson", data: labelsData });
    map.addLayer({
      id: BASE_ROOM_LABELS_LAYER_ID, type: "symbol", source: BASE_ROOM_LABELS_SOURCE_ID, minzoom: BASE_GRID_MIN_ZOOM,
      layout: { "text-field": ["get", "label"], "text-size": 13, "text-allow-overlap": true },
      paint: { "text-color": "#1f2a33", "text-halo-color": "rgba(255,255,255,0.9)", "text-halo-width": 1.6 },
    });

    bindBaseInteriorHandlers();
  }

  // Visible from any tab; each layer's own `minzoom: BASE_GRID_MIN_ZOOM` (set
  // above) is what actually gates them in — same contract as the base grid
  // overlay (syncBaseGridLayer). No Base-tab requirement.
  setLayersVisible(BASE_INTERIOR_LAYER_IDS, true);
}

// Whole-region hover + click-to-build, bound once on the room-fill layer.
// Room building is an overworld/base-tab activity only. During a raid or base
// defense the same base geometry is on screen but interacting with it (hover
// highlight, the "build a room" popup) is off — a defender move-click must not
// open the build menu.
function baseInteriorBuildingActive() {
  return document.body.dataset.screen === "overworld";
}

function bindBaseInteriorHandlers() {
  if (baseInteriorHandlersBound || !map.getLayer(BASE_ROOMS_FILL_LAYER_ID)) return;
  baseInteriorHandlersBound = true;
  map.on("mousemove", BASE_ROOMS_FILL_LAYER_ID, (event) => {
    if (!baseInteriorBuildingActive()) return;
    const props = event.features?.[0]?.properties;
    const key = isBuildableRegion(props) ? props.regionKey : null;
    map.getCanvas().style.cursor = key ? "pointer" : "";
    if (key !== hoveredRegionKey) { hoveredRegionKey = key; refreshRoomHover(); }
  });
  map.on("mouseleave", BASE_ROOMS_FILL_LAYER_ID, () => {
    if (hoveredRegionKey !== null) { hoveredRegionKey = null; refreshRoomHover(); }
    map.getCanvas().style.cursor = "";
  });
  map.on("click", BASE_ROOMS_FILL_LAYER_ID, (event) => {
    if (!baseInteriorBuildingActive()) return;
    const props = event.features?.[0]?.properties;
    if (isBuildableRegion(props)) openRoomBuildPopup(props.regionKey, event.lngLat);
  });
}

// MapLibre may hand booleans back as real booleans (geojson) or strings.
function isBuildableRegion(props) {
  return Boolean(props) && (props.buildable === true || props.buildable === "true");
}

function refreshRoomHover() {
  if (map?.getLayer(BASE_ROOMS_FILL_LAYER_ID)) {
    map.setPaintProperty(BASE_ROOMS_FILL_LAYER_ID, "fill-opacity", baseRoomsFillOpacity());
  }
}

// A short effect blurb for the build menu.
function roomEffectLabel(definition) {
  if (definition.housingCapacity) return `Housing +${definition.housingCapacity}`;
  if (definition.foodConsumptionMultiplier) return `${Math.round((1 - definition.foodConsumptionMultiplier) * 100)}% less food`;
  return "Storage / empty";
}

// The floating "build a room" menu over a clicked region. Each type costs
// materials; unaffordable options are disabled. The current type is shown
// selected (and inert). Clearing a room back to empty is free.
function openRoomBuildPopup(regionKey, lngLat) {
  closeRoomBuildPopup();
  const currentType = campaign.base?.rooms?.[regionKey] ?? "";
  const materials = baseMaterials(campaign);
  const options = Object.values(BASE_ROOM_CATALOG).map((definition) => {
    const cost = definition.buildCost ?? 0;
    const isCurrent = definition.id === currentType;
    const affordable = isCurrent || materials >= cost;
    const costClass = affordable ? "" : " short";
    const costLabel = cost > 0 ? `${cost} mat` : "Free";
    return `<button type="button" class="room-build-option${isCurrent ? " selected" : ""}" data-room-type="${escapeHtml(definition.id)}" ${affordable && !isCurrent ? "" : "disabled"}>
      <b>${escapeHtml(definition.name)}</b>
      <span class="room-build-effect">${escapeHtml(roomEffectLabel(definition))} · <span class="room-build-cost${costClass}">${costLabel}</span></span>
    </button>`;
  }).join("");
  const clear = currentType ? `<button type="button" class="room-build-option clear" data-room-type="">Clear to empty</button>` : "";
  const container = document.createElement("div");
  container.className = "room-build-popup";
  container.innerHTML = `<h4>Build a room · ${materials} materials</h4><div class="room-build-options">${options}${clear}</div>`;
  container.addEventListener("click", (event) => {
    const button = event.target.closest("[data-room-type]");
    if (button && !button.disabled) buildRoom(regionKey, button.dataset.roomType);
  });
  roomBuildPopup = new maplibregl.Popup({ offset: 16, closeButton: true, closeOnClick: false, className: "room-build-popup-wrap" })
    .setLngLat(lngLat).setDOMContent(container).addTo(map);
  roomBuildPopup.on("close", () => { roomBuildPopup = null; });
}

function buildRoom(regionKey, typeId) {
  const currentType = campaign.base?.rooms?.[regionKey] ?? "";
  if (typeId === currentType) { closeRoomBuildPopup(); return; } // no-op re-selection

  const rooms = { ...(campaign.base?.rooms ?? {}) };
  // "Demolishing" is converting to Storage or clearing to empty — both read as
  // the neutral unbuilt state (see roomEffectLabel), so both refund below.
  const isDemolish = !typeId || typeId === BASE_ROOM_TYPES.storage;
  const previousDefinition = currentType ? BASE_ROOM_CATALOG[currentType] : null;

  if (typeId && BASE_ROOM_CATALOG[typeId]) {
    const cost = BASE_ROOM_CATALOG[typeId].buildCost ?? 0;
    if (cost > 0) {
      const spent = spendMaterials(campaign, cost);
      if (!spent.ok) { logLine("day", `Not enough materials to build a ${BASE_ROOM_CATALOG[typeId].name}.`); return; }
      campaign = spent.campaign;
      logLine("day", `Built a ${BASE_ROOM_CATALOG[typeId].name} (−${cost} materials).`);
    }
    rooms[regionKey] = typeId;
  } else {
    delete rooms[regionKey]; // clearing a room is free
  }

  // Demolishing a previously built room refunds half its original materials
  // cost — switching directly between two built types (e.g. Bedroom ->
  // Barracks) does not get a refund, only a true demolish-to-empty/storage.
  if (isDemolish && previousDefinition?.buildCost) {
    const refund = Math.floor(previousDefinition.buildCost / 2);
    if (refund > 0) {
      campaign = addMaterials(campaign, refund);
      logLine("day", `Reclaimed ${refund} materials from the old ${previousDefinition.name}.`);
    }
  }

  campaign = { ...campaign, base: { ...campaign.base, rooms } };
  save();
  closeRoomBuildPopup();
  renderOverworld();
}

function closeRoomBuildPopup() {
  if (!roomBuildPopup) return;
  const popup = roomBuildPopup;
  roomBuildPopup = null;
  popup.remove();
}

// Fog reveal: completing a supply run uncovers any POI whose polygon falls inside the
// supply-run circle, OR that you pass close to on the route out (the "drive past en
// route" trigger — approximated by proximity to the base->target road path; a
// real car mechanic can refine this later).
const DRIVE_PAST_METERS = 60;
function revealPoisForSupplyRun(targetId) {
  const target = poiById(targetId);
  if (!target) return;
  const route = routeToTarget(targetId);
  const routeCoords = route?.reachable ? route.coordinates : null;
  const newlyFound = [];
  for (const poi of POIS) {
    if (isPoiDiscovered(campaign, poi.id)) continue;
    const inSupplyRunCircle = distanceInMeters(poi.coordinate, target.coordinate) <= DEFAULT_TACTICAL_ZONE_RADIUS_METERS;
    const passedEnRoute = routeCoords && (nearestPointOnPolyline(poi.coordinate, routeCoords)?.distanceMeters ?? Infinity) <= DRIVE_PAST_METERS;
    if (inSupplyRunCircle || passedEnRoute) newlyFound.push(poi.id);
  }
  if (!newlyFound.length) return;
  campaign = discoverPois(campaign, newlyFound);
  save();
  for (const id of newlyFound) logLine("items", `Scouted: ${POIS.find((poi) => poi.id === id)?.name}.`);
}

// The circle showing where the tactical supply-run zone will be drawn around the
// selected target, so you can see the supply run's footprint before committing.
function updateSupplyRunBoundary() {
  const source = map?.getSource("supply-run-boundary");
  if (!source) return;
  const poi = selectedPoiId ? poiById(selectedPoiId) : null;
  if (!poi) { source.setData(EMPTY_COLLECTION); return; }
  source.setData({
    type: "FeatureCollection",
    features: [{
      type: "Feature",
      properties: {},
      geometry: { type: "Polygon", coordinates: [geodesicCircle(poi.coordinate, DEFAULT_TACTICAL_ZONE_RADIUS_METERS)] },
    }],
  });
}

// Draw a threat circle (supply-run-radius footprint) around every POI still carrying
// threat, so searched ground stays visibly "hot" between supply runs and fades as it
// cools. Opacity is driven by each feature's `threat` property (see the layer's
// interpolate expression).
function updatePoiThreatCircles() {
  const source = map?.getSource("poi-threat");
  if (!source) return;
  const features = [];
  for (const poiId of Object.keys(campaign.poiThreatState ?? {})) {
    const threat = currentPoiThreat(campaign, poiId);
    if (threat <= 0) continue;
    const poi = poiById(poiId);
    if (!poi?.coordinate) continue;
    features.push({
      type: "Feature",
      properties: { threat },
      geometry: { type: "Polygon", coordinates: [geodesicCircle(poi.coordinate, DEFAULT_TACTICAL_ZONE_RADIUS_METERS)] },
    });
  }
  source.setData({ type: "FeatureCollection", features });
}

// The base-to-target path, drawn along the actual road graph (not a straight
// line) so it reads as a real route — restores the route preview V3 had.
function updateRouteLine() {
  const source = map?.getSource("selected-route");
  if (!source) return;
  const poi = selectedPoiId ? poiById(selectedPoiId) : null;
  if (!poi) { source.setData(EMPTY_COLLECTION); return; }
  const route = routeToTarget(selectedPoiId);
  if (!route?.reachable) { source.setData(EMPTY_COLLECTION); return; }
  source.setData({
    type: "FeatureCollection",
    features: [{ type: "Feature", properties: {}, geometry: { type: "LineString", coordinates: route.coordinates } }],
  });
}

// The floating target popup above the clicked building carries revealed building
// intel and the Supply Run button, while the map colors carry neighbourhood scouting.
function ensureTargetPopup() {
  if (!targetPopup) {
    targetPopup = new maplibregl.Popup({ offset: 22, anchor: "bottom", closeButton: true, closeOnClick: false, className: "target-popup-wrap" });
    // Only a *user* dismiss (the X) should deselect. We guard our own
    // remove()s so moving/refreshing the popup doesn't clear the selection.
    targetPopup.on("close", () => {
      if (popupClosingProgrammatically) return;
      if (selectedPoiId) { selectedPoiId = null; updateSelectedBuilding(); }
    });
  }
  return targetPopup;
}

// The hot POIs (own threat > 0) with coordinates, for spatial area-threat reads.
function hotPoiEntries() {
  const entries = [];
  for (const poiId of Object.keys(campaign.poiThreatState ?? {})) {
    const threat = currentPoiThreat(campaign, poiId);
    if (threat <= 0) continue;
    const poi = poiById(poiId);
    if (poi?.coordinate) entries.push({ coordinate: poi.coordinate, threat });
  }
  return entries;
}

// A POI's effective threat: its own threat plus any hot POI whose supply-run circle
// covers it — so searching one building leaves the whole supply-run radius hot, not
// just that building. (A POI's own entry sits at distance 0, so this includes
// its own threat.)
function effectivePoiThreat(poiId) {
  const poi = poiById(poiId);
  if (!poi?.coordinate) return currentPoiThreat(campaign, poiId);
  return areaThreatAt(poi.coordinate, hotPoiEntries(), DEFAULT_TACTICAL_ZONE_RADIUS_METERS, distanceInMeters);
}

function discoveredSpecialPoiForTarget(targetId) {
  return POIS.find((poi) => String(poi.buildingTargetId) === String(targetId) && isPoiDiscovered(campaign, poi.id)) ?? null;
}

function buildingScoutingForTarget(target) {
  return campaign.buildingIntelById?.[String(target.id)] ?? null;
}

function supplyClassForTarget(target, intel) {
  if (!intel) return "";
  if (discoveredSpecialPoiForTarget(target.id)) return "Special supplies";
  if (intel.scavengeState === "emptied" || intel.itemCount <= 0) return "Empty";
  if (intel.itemCount >= 2) return "Large supply cache";
  return "Small supply cache";
}

function buildTargetPopupContent(poi) {
  const threat = effectivePoiThreat(poi.id);
  const reachable = isTargetReachable(poi.id);
  const squadReady = selectedSquad().length > 0;
  const canStartRun = reachable && squadReady && activeSurvivorsV4(campaign).length > 0;
  const intel = buildingScoutingForTarget(poi);
  const specialPoi = intel ? discoveredSpecialPoiForTarget(poi.id) : null;
  const displayName = intel ? (specialPoi?.name ?? poi.name) : "Unknown building";
  const supplyClass = supplyClassForTarget(poi, intel);

  const element = document.createElement("div");
  element.className = "target-popup";
  element.innerHTML = `<h3></h3><p class="poi-scouting" hidden></p><p class="poi-threat"></p>`;
  element.querySelector("h3").textContent = displayName;
  const scoutingEl = element.querySelector(".poi-scouting");
  if (supplyClass) {
    scoutingEl.textContent = supplyClass;
    scoutingEl.hidden = false;
  }
  const startingForce = (devSettings.supplyRun.guardCount ?? 0) + (devSettings.supplyRun.patrollerCount ?? 0);
  element.querySelector(".poi-threat").textContent = threat > 0
    ? `Threat ${threat.toFixed(1)}/${THREAT_MAX} · ${startingForce} enemies + reinforcements — let it cool`
    : `Cold · ${startingForce} enemies to start`;

  const button = document.createElement("button");
  button.className = "primary";
  button.textContent = "Start supply run →";
  button.disabled = !canStartRun;
  if (!squadReady) button.title = "Pick at least one survivor to send.";
  else if (!reachable) button.title = "Out of today's travel range or past the supply-run limit.";
  button.addEventListener("click", () => startSupplyRun(poi.id));
  element.appendChild(button);
  return element;
}

function syncTargetPopup() {
  if (!map) return;
  if (selectedPoiId) {
    const poi = poiById(selectedPoiId);
    const popup = ensureTargetPopup();
    // Update position + content in place. Only addTo() when it isn't already
    // open — calling addTo() on an open popup makes MapLibre remove() it first
    // (firing a spurious close that would clear the selection).
    popup.setLngLat(poi.coordinate).setDOMContent(buildTargetPopupContent(poi));
    if (!popup.isOpen()) popup.addTo(map);
  } else if (targetPopup?.isOpen()) {
    popupClosingProgrammatically = true;
    targetPopup.remove();
    popupClosingProgrammatically = false;
  }
}

function formatWhole(value) {
  return Math.round(value).toLocaleString();
}

function escapeHtml(value) {
  return String(value).replace(/[&<>"']/g, (character) => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    "\"": "&quot;",
    "'": "&#39;",
  })[character]);
}

function renderBaseStats() {
  const container = document.getElementById("base-stats");
  if (!container) return;
  const feature = baseFeatureForCampaign();
  if (!feature) {
    container.innerHTML = `<div class="card"><h3>Base</h3><p class="meta">No building footprint selected.</p></div>`;
    return;
  }
  const metrics = buildingFootprintMetrics(feature);
  const roomStats = campaignBaseRoomStats(campaign);
  const base = campaign.base ?? {};
  const grid = base.grid;
  const name = base.buildingName || feature.properties?.name || "Base";
  const kind = titleCase(base.buildingKind || feature.properties?.kind || "building");
  const carriedItems = carriedInventory(campaign);
  container.innerHTML = `
    <div class="card base-card">
      <h3>${escapeHtml(name)}</h3>
      <p class="meta">${escapeHtml(kind)}</p>
      <div class="base-stat-grid">
        <span>Internal area</span><b>${formatWhole(metrics.floorAreaSquareFeet)} sq ft</b>
        <span>Perimeter</span><b>${formatWhole(metrics.exteriorPerimeterFeet)} ft</b>
        <span>Materials</span><b>${formatResource(baseMaterials(campaign))}</b>
        <span>Housing</span><b>${roomStats.housingCapacity}</b>
        <span>Food need</span><b>${formatResource(campaignDailyQuota(campaign))}/day</b>
        ${grid ? `<span>Grid</span><b>${grid.insideCells} cells @ ${grid.cellSizeMeters.toFixed(2)} m</b>` : ""}
      </div>
      <div class="base-carried-inventory">
        <div class="base-carried-head">
          <h4>Storage</h4>
          <span>${carriedItems.length ? "Deposits at night" : ""}</span>
        </div>
        ${renderInventorySlots(carriedItems, Math.max(1, carriedItems.length))}
      </div>
      <p class="hint">Open the map and click a room to build. Rooms come from your base layout.</p>
      ${defenseSummaryHtml()}
    </div>`;
}

// Base-defense readout in the Base tab. The "Defend now (debug)" trigger itself
// lives in the main "AD" topbar menu (see syncDevMenu) since it's a dev action,
// not part of the base description.
function defenseSummaryHtml() {
  ensureBaseDefense();
  const defense = campaign.base?.defense;
  return defense
    ? `<p class="meta">Interior: <b>${defense.doorEdgeKeys.length}</b> door(s) · next wave ~<b>${defenseWaveSize(campaign)}</b></p>`
    : `<p class="meta">No defensive layout yet.</p>`;
}

function effectLine(itemId) {
  const effects = getItem(itemId)?.equipment?.effects ?? {};
  const parts = [];
  if (effects.offenseBonus) parts.push(`attack +${effects.offenseBonus}`);
  if (effects.defenseBonus) parts.push(`defense +${effects.defenseBonus}`);
  if (effects.moveBonusMetersPerTurn) parts.push(`speed +${effects.moveBonusMetersPerTurn}m`);
  if (effects.carryCapacityBonus) parts.push(`bag +${effects.carryCapacityBonus}`);
  if (effects.scavengeTurnsDelta) parts.push(`search ${effects.scavengeTurnsDelta}`);
  return parts.join(" · ") || "equipment";
}

function storageEquipmentItems() {
  return (campaign.baseInventory?.items ?? []).filter((itemId) => getItem(itemId)?.equipment);
}

function equipmentStatsHtml(survivor) {
  const effects = getEquipmentEffects(survivor.equipmentItemIds);
  const move = DEFAULT_TACTICAL_MOVE_METERS_PER_TURN + effects.moveBonusMetersPerTurn;
  return `Att ${effects.offenseBonus} / Def ${effects.defenseBonus} / Speed ${move}`;
}

function equipmentSlotLabel(loadoutSlot) {
  if (loadoutSlot === EQUIPMENT_LOADOUT_SLOTS.weapon) return "Weapon";
  if (loadoutSlot === EQUIPMENT_LOADOUT_SLOTS.defense) return "Defense";
  return "Utility";
}

function renderEquipmentSlots(survivor, { interactive = false, disabled = false, compact = false } = {}) {
  const equippedBySlot = equipmentLoadoutMap(survivor?.equipmentItemIds);
  return `
    <ul class="equipment-list${compact ? " compact" : ""}">
      ${EQUIPMENT_LOADOUT_SLOT_ORDER.map((loadoutSlot) => {
        const equipped = equippedBySlot.get(loadoutSlot);
        const itemId = equipped?.itemId;
        const index = equipped?.index;
        const slotLabel = equipmentSlotLabel(loadoutSlot);
        const targetClass = interactive && !disabled && selectedGearPayload && canMoveSelectedGearTo(survivor.id, loadoutSlot) ? " gear-target" : "";
        const dropAttrs = interactive && !disabled ? ` data-gear-drop-survivor="${escapeHtml(survivor.id)}" data-gear-drop-slot="${escapeHtml(loadoutSlot)}"` : "";
        if (!itemId) return `<li class="equipment-slot empty${targetClass}"${dropAttrs} aria-label="Open ${escapeHtml(slotLabel.toLowerCase())} slot"><span class="gear-marker faint" aria-hidden="true">${gearMarkerIcon()}</span><span class="equipment-slot-name">${escapeHtml(slotLabel)}</span></li>`;
        const item = getItem(itemId);
        const actionAttrs = interactive && !disabled ? ` data-survivor="${escapeHtml(survivor.id)}" data-equip-index="${index}" data-item="${escapeHtml(itemId)}" data-gear-drag-kind="equipped" draggable="true"` : "";
        const selectedClass = interactive && !disabled && isSelectedGear({ kind: "equipped", survivorId: survivor.id, index, itemId }) ? " selected-gear" : "";
        return `
          <li class="equipment-slot${selectedClass}">
            <button class="unequip" type="button"${actionAttrs} title="${escapeHtml(itemName(itemId))}: ${escapeHtml(effectLine(itemId))}" ${disabled || !interactive ? "disabled" : ""}>
              <span class="gear-marker" aria-hidden="true">${gearMarkerIcon()}</span>
              <span class="item-inventory-icon" aria-hidden="true">${gearIconForItem(itemId)}</span>
              <span class="equipment-slot-name">${escapeHtml(itemName(itemId))}</span>
              <span class="equipment-slot-effect">${escapeHtml(effectLine(itemId))}</span>
            </button>
          </li>`;
      }).join("")}
    </ul>`;
}

function renderRoster() {
  const container = document.getElementById("roster");
  closeSquadSwapMenu();
  normalizeSelectedSquadIds();
  container.innerHTML = "";
  const availableEquipment = storageEquipmentItems();
  const survivors = Object.values(campaign.survivorsById);
  const selectedSurvivors = survivors.filter((survivor) => selectedSupplyRunSquadIds.has(survivor.id));
  const reserveSurvivors = survivors.filter((survivor) => !selectedSupplyRunSquadIds.has(survivor.id));
  const scoutSection = document.createElement("div");
  scoutSection.className = "roster-section roster-scouts";
  const reserveSection = document.createElement("div");
  reserveSection.className = "roster-section roster-reserves";

  const renderEntry = (survivor, { reserve = false } = {}) => {
    const dead = survivor.status === "dead";
    const wounded = !dead && isSurvivorWounded(survivor);
    const selected = selectedSupplyRunSquadIds.has(survivor.id) && !dead && !wounded;
    const status = dead ? "Lost" : wounded ? "Wounded" : survivor.hunger >= 1 ? "Hungry" : "Ready";
    const tacticalUnit = { ...createTacticalUnit(survivor), inventory: survivor.inventory ?? [] };
    const equipped = survivor.equipmentItemIds ?? [];
    const equipOptions = availableEquipment.filter((itemId) => canEquipItemId(equipped, itemId));
    const storageHtml = !reserve && !dead && equipOptions.length
      ? `<div class="equipment-storage">${equipOptions.map((itemId) => `
          <button class="equip" data-survivor="${survivor.id}" data-item="${itemId}">
            ${escapeHtml(itemName(itemId))}<span>${escapeHtml(effectLine(itemId))}</span>
          </button>`).join("")}</div>`
      : "";
    const entry = document.createElement("div");
    entry.className = "survivor-roster-entry";
    entry.innerHTML = `
      <div data-squad="${survivor.id}" data-roster-role="${reserve ? "reserve" : "scout"}" class="${["tactical-unit-card", "survivor-loadout-card", "roster-survivor-card", reserve ? "roster-reserve-card" : "", selected ? "selected" : "", dead ? "dead" : "", wounded ? "wounded" : "", survivor.hunger >= 1 ? "hungry" : ""].filter(Boolean).join(" ")}" role="button" tabindex="${dead || wounded ? "-1" : "0"}" aria-pressed="${selected ? "true" : "false"}" aria-disabled="${dead || wounded ? "true" : "false"}">
        ${renderSurvivorStatusCard({
          survivorId: survivor.id,
          statusLabel: status,
          inventory: tacticalUnit.inventory,
          capacity: tacticalUnit.carryCapacity,
          equipmentInteractive: true,
          equipmentDisabled: dead,
          compactEquipment: reserve,
          showInventory: !reserve,
        })}
      </div>
      ${storageHtml ? `<div class="equipment-storage-wrap">${storageHtml}</div>` : ""}`;
    return entry;
  };

  for (const survivor of selectedSurvivors.slice(0, MAX_SUPPLY_RUN_SQUAD_SIZE)) scoutSection.appendChild(renderEntry(survivor));
  for (let index = selectedSurvivors.length; index < MAX_SUPPLY_RUN_SQUAD_SIZE; index += 1) {
    const empty = document.createElement("div");
    empty.className = "roster-empty-slot";
    empty.textContent = "Open scout slot";
    scoutSection.appendChild(empty);
  }
  container.appendChild(scoutSection);
  const divider = document.createElement("hr");
  divider.className = "roster-divider";
  container.appendChild(divider);
  for (const survivor of reserveSurvivors) reserveSection.appendChild(renderEntry(survivor, { reserve: true }));
  container.appendChild(reserveSection);

  for (const card of container.querySelectorAll("[data-squad]")) {
    const toggleCard = () => {
      const survivorId = card.dataset.squad;
      if (selectedSupplyRunSquadIds.has(survivorId)) selectedSupplyRunSquadIds.delete(survivorId);
      else if (selectedSupplyRunSquadIds.size < MAX_SUPPLY_RUN_SQUAD_SIZE) selectedSupplyRunSquadIds.add(survivorId);
      renderOverworld();
    };
    card.addEventListener("click", (event) => {
      if (card.getAttribute("aria-disabled") === "true") return;
      if (event.target.closest("[data-gear-drag-kind], [data-gear-drop-survivor]")) return;
      if (selectedGearPayload) {
        selectedGearPayload = null;
        renderOverworld();
        return;
      }
      if (card.dataset.rosterRole === "reserve" && !selectedSupplyRunSquadIds.has(card.dataset.squad) && selectedSupplyRunSquadIds.size >= MAX_SUPPLY_RUN_SQUAD_SIZE) {
        openSquadSwapMenu(event.clientX, event.clientY, card.dataset.squad);
        return;
      }
      toggleCard();
    });
    card.addEventListener("keydown", (event) => {
      if (card.getAttribute("aria-disabled") === "true") return;
      if (event.key !== "Enter" && event.key !== " ") return;
      event.preventDefault();
      if (card.dataset.rosterRole === "reserve" && !selectedSupplyRunSquadIds.has(card.dataset.squad) && selectedSupplyRunSquadIds.size >= MAX_SUPPLY_RUN_SQUAD_SIZE) {
        const rect = card.getBoundingClientRect();
        openSquadSwapMenu(rect.left + rect.width / 2, rect.top + 8, card.dataset.squad);
        return;
      }
      toggleCard();
    });
  }
  for (const button of container.querySelectorAll(".equip[data-item]")) {
    button.addEventListener("click", () => equipItem(button.dataset.survivor, button.dataset.item));
  }
  for (const button of container.querySelectorAll("[data-profession-survivor]")) {
    button.addEventListener("click", (event) => {
      event.stopPropagation(); // don't also toggle squad selection on the card underneath
      showProfessionDialog(getSurvivorV4(campaign, button.dataset.professionSurvivor));
    });
  }
  attachEquipmentDragHandlers(container);
}

function closeSquadSwapMenu() {
  if (squadSwapMenuEl) { squadSwapMenuEl.remove(); squadSwapMenuEl = null; }
  document.removeEventListener("pointerdown", onSquadSwapOutsidePointer, true);
  document.removeEventListener("keydown", onSquadSwapKey, true);
}

function onSquadSwapOutsidePointer(event) {
  if (squadSwapMenuEl && !squadSwapMenuEl.contains(event.target)) closeSquadSwapMenu();
}

function onSquadSwapKey(event) {
  if (event.key === "Escape") closeSquadSwapMenu();
}

function swapScout(outgoingId, incomingId) {
  const next = [...selectedSupplyRunSquadIds];
  const index = next.indexOf(outgoingId);
  if (index === -1 || !incomingId || selectedSupplyRunSquadIds.has(incomingId)) return;
  next[index] = incomingId;
  selectedSupplyRunSquadIds = new Set(next.slice(0, MAX_SUPPLY_RUN_SQUAD_SIZE));
  selectedGearPayload = null;
  closeSquadSwapMenu();
  renderOverworld();
}

function professionLabelForSurvivor(survivor) {
  return SURVIVOR_PROFESSION_DEFINITIONS[survivor.professionId || survivor.backgrounds?.adult || survivor.backgroundId]?.name || "Survivor";
}

function openSquadSwapMenu(clientX, clientY, incomingId) {
  closeSquadSwapMenu();
  const incoming = getSurvivorV4(campaign, incomingId);
  if (!incoming) return;
  const menu = document.createElement("div");
  menu.className = "squad-swap-menu";
  Object.assign(menu.style, { left: `${clientX}px`, top: `${clientY}px` });
  const title = document.createElement("div");
  title.className = "swap-title";
  title.textContent = `Swap in ${survivorName(incomingId)}`;
  menu.appendChild(title);
  for (const survivorId of selectedSupplyRunSquadIds) {
    const survivor = getSurvivorV4(campaign, survivorId);
    if (!survivor) continue;
    const button = document.createElement("button");
    button.type = "button";
    button.innerHTML = `${escapeHtml(survivor.name ?? survivor.id)}<span>${escapeHtml(professionLabelForSurvivor(survivor))}</span>`;
    button.addEventListener("click", () => swapScout(survivorId, incomingId));
    menu.appendChild(button);
  }
  document.body.appendChild(menu);
  const rect = menu.getBoundingClientRect();
  menu.style.left = `${Math.max(8, Math.min(clientX, window.innerWidth - rect.width - 8))}px`;
  menu.style.top = `${Math.max(8, Math.min(clientY, window.innerHeight - rect.height - 8))}px`;
  squadSwapMenuEl = menu;
  setTimeout(() => {
    document.addEventListener("pointerdown", onSquadSwapOutsidePointer, true);
    document.addEventListener("keydown", onSquadSwapKey, true);
  }, 0);
}

function equipItem(survivorId, itemId) {
  const result = equipSurvivorItem(campaign, survivorId, itemId);
  if (!result.ok) return;
  campaign = result.campaign;
  save();
  selectedGearPayload = null;
  logLine("upgrade", `${survivorName(survivorId)} equipped ${itemName(itemId)}.`);
  renderOverworld();
}

function unequipItem(survivorId, itemIndex) {
  const itemId = getSurvivorV4(campaign, survivorId)?.equipmentItemIds?.[itemIndex];
  const result = unequipSurvivorItem(campaign, survivorId, itemIndex);
  if (!result.ok) return;
  campaign = result.campaign;
  save();
  selectedGearPayload = null;
  logLine("upgrade", `${survivorName(survivorId)} returned ${itemName(itemId)} to storage.`);
  renderOverworld();
}

function gearPayloadKey(payload) {
  if (!payload) return "";
  return `${payload.kind}:${payload.survivorId}:${payload.index}:${payload.itemId}`;
}

function isSelectedGear(payload) {
  return gearPayloadKey(selectedGearPayload) === gearPayloadKey(payload);
}

function canMoveSelectedGearTo(targetSurvivorId, targetLoadoutSlot = null) {
  if (!selectedGearPayload || !targetSurvivorId) return false;
  const target = getSurvivorV4(campaign, targetSurvivorId);
  if (!target || target.status === "dead") return false;
  const payloadSlot = equipmentLoadoutSlot(selectedGearPayload.itemId);
  if (targetLoadoutSlot && payloadSlot !== targetLoadoutSlot) return false;
  if (!canEquipItemId(target.equipmentItemIds, selectedGearPayload.itemId)) return false;
  return selectedGearPayload.kind === "bag" || selectedGearPayload.survivorId !== targetSurvivorId;
}

function equipmentDragData(element) {
  return {
    kind: element.dataset.gearDragKind,
    survivorId: element.dataset.survivor,
    itemId: element.dataset.item,
    index: Number(element.dataset.bagIndex ?? element.dataset.equipIndex),
  };
}

function readEquipmentDragData(event) {
  try {
    return JSON.parse(event.dataTransfer.getData("application/x-apocalypse-gear"));
  } catch {
    return null;
  }
}

function moveDraggedGear(payload, targetSurvivorId) {
  if (!payload || !targetSurvivorId) return;
  if (payload.kind === "equipped" && payload.survivorId === targetSurvivorId) return;
  const sourceName = survivorName(payload.survivorId);
  const targetName = survivorName(targetSurvivorId);
  const result = payload.kind === "bag"
    ? equipSurvivorInventoryItem(campaign, payload.survivorId, payload.index, targetSurvivorId)
    : transferSurvivorEquipmentItem(campaign, payload.survivorId, payload.index, targetSurvivorId);
  if (!result.ok) return;
  campaign = result.campaign;
  save();
  const itemId = result.itemId ?? payload.itemId;
  const message = payload.kind === "bag" && payload.survivorId === targetSurvivorId
    ? `${sourceName} equipped ${itemName(itemId)}.`
    : `${sourceName} moved ${itemName(itemId)} to ${targetName}'s gear.`;
  selectedGearPayload = null;
  logLine("upgrade", message);
  renderOverworld();
}

function attachEquipmentDragHandlers(container) {
  for (const source of container.querySelectorAll("[data-gear-drag-kind]")) {
    source.addEventListener("click", (event) => {
      event.preventDefault();
      event.stopPropagation();
      const payload = equipmentDragData(source);
      selectedGearPayload = isSelectedGear(payload) ? null : payload;
      renderOverworld();
    });
    source.addEventListener("dragstart", (event) => {
      const payload = equipmentDragData(source);
      selectedGearPayload = null;
      event.dataTransfer.effectAllowed = "move";
      event.dataTransfer.setData("application/x-apocalypse-gear", JSON.stringify(payload));
      event.dataTransfer.setData("text/plain", itemName(payload.itemId));
      source.classList.add("dragging");
    });
    source.addEventListener("dragend", () => source.classList.remove("dragging"));
  }
  for (const slot of container.querySelectorAll("[data-gear-drop-survivor]")) {
    slot.addEventListener("dragover", (event) => {
      event.preventDefault();
      event.dataTransfer.dropEffect = "move";
      slot.classList.add("drag-over");
    });
    slot.addEventListener("dragleave", () => slot.classList.remove("drag-over"));
    slot.addEventListener("click", (event) => {
      if (!selectedGearPayload) return;
      event.preventDefault();
      event.stopPropagation();
      moveDraggedGear(selectedGearPayload, slot.dataset.gearDropSurvivor);
    });
    slot.addEventListener("drop", (event) => {
      event.preventDefault();
      event.stopPropagation();
      slot.classList.remove("drag-over");
      moveDraggedGear(readEquipmentDragData(event), slot.dataset.gearDropSurvivor);
    });
  }
}

// --- Supply-run screen --------------------------------------------------------
// Starting a supply run flies the same map's camera in on the target instead of
// swapping to a separate renderer; tactical-view.mjs mounts its tactical layer
// directly on `map` once the camera settles. The overworld's own
// buildings/POI-and-base markers step aside for the duration (they'd just be
// visual clutter competing with the tactical layer) and come back on return.

const OVERWORLD_CHROME_LAYER_IDS = [
  OVERWORLD_TILE_FOG_FILL_LAYER_ID, OVERWORLD_TILE_FOG_LINE_LAYER_ID,
  "overworld-buildings-fill", "overworld-buildings-line",
  "overworld-building-selected-fill", "overworld-building-selected-line",
  "supply-run-boundary-fill", "supply-run-boundary-line",
  "selected-route-glow", "selected-route-line",
  // Persistent POI threat circles from prior supply runs: hide them during a supply run —
  // a neighbouring one bleeding into the supply-run view is just confusing there.
  "poi-threat-fill", "poi-threat-line",
  BASE_BUILDING_FILL_LAYER_ID, BASE_BUILDING_OUTLINE_LAYER_ID,
  BASE_GRID_FILL_LAYER_ID, BASE_GRID_LINE_LAYER_ID,
];

// During a base defense we KEEP the base building + grid layers (the defense
// draws on top of them) and only hide the rest of the overworld chrome — the
// inverse of a supply run, which hides the base layers too.
const BASE_DEFENSE_KEPT_LAYER_IDS = new Set([
  BASE_BUILDING_FILL_LAYER_ID, BASE_BUILDING_OUTLINE_LAYER_ID,
  BASE_GRID_FILL_LAYER_ID, BASE_GRID_LINE_LAYER_ID,
]);
const DEFENSE_HIDDEN_LAYER_IDS = OVERWORLD_CHROME_LAYER_IDS.filter((id) => !BASE_DEFENSE_KEPT_LAYER_IDS.has(id));

function setLayersVisible(ids, visible) {
  if (!map) return;
  const value = visible ? "visible" : "none";
  for (const id of ids) {
    if (map.getLayer(id)) map.setLayoutProperty(id, "visibility", value);
  }
}

function setOverworldLayersVisible(visible) {
  if (!map) return;
  const value = visible ? "visible" : "none";
  for (const id of OVERWORLD_CHROME_LAYER_IDS) {
    if (map.getLayer(id)) map.setLayoutProperty(id, "visibility", value);
  }
}

function setOverworldMarkersVisible(visible) {
  if (baseMarker) baseMarker.getElement().style.display = visible ? "" : "none";
}

// A coarse bounding box around a metric-radius circle, for fitBounds — reuses
// geodesicCircle rather than hand-deriving a lat/lon delta so it stays
// correct at any latitude.
function boundsForCircle(center, radiusMeters) {
  const ring = geodesicCircle(center, radiusMeters, 16);
  const lons = ring.map((coordinate) => coordinate[0]);
  const lats = ring.map((coordinate) => coordinate[1]);
  return [[Math.min(...lons), Math.min(...lats)], [Math.max(...lons), Math.max(...lats)]];
}

function startSupplyRun(poiId) {
  const poi = poiById(poiId);
  const squad = selectedSquad();
  if (!poi || !squad.length || supplyRunsLeftToday() <= 0) return;

  // Travelling to + searching this target scouts the POIs in its circle and along
  // the way (fog reveal).
  revealPoisForSupplyRun(poiId);

  if (targetPopup?.isOpen()) { popupClosingProgrammatically = true; targetPopup.remove(); popupClosingProgrammatically = false; }
  setOverworldMarkersVisible(false);

  const zoneInfo = zoneInfoFor(poi);
  // Starting force is set by the dev tools (guards + patrollers), not threat;
  // threat still layers reinforcements on during the supply run.
  const guardCount = devSettings.supplyRun.guardCount ?? 0;
  const patrollerCount = devSettings.supplyRun.patrollerCount ?? 0;
  const enemyCount = guardCount + patrollerCount;
  const squadUnits = squad.map((survivor) => ({
    ...createTacticalUnit(survivor),
    inventory: [...(survivor.inventory ?? [])],
  }));
  activeSupplyRun = { poiId, squadUnits, over: false };

  const createSession = () => {
    const entryCoordinate = supplyRunEntryCoordinateForTarget(poi);
    const positions = squadSpawnPositions(zoneInfo.zone, squadUnits.length, { preferredEntryCoordinate: entryCoordinate });
    const units = squadUnits.map((unit, index) => ({ ...unit, position: positions[index] }));
    // Buildings become scavengeable containers (promising ones distributed to
    // force movement) — deterministic per campaign+zone, matching the popup preview.
    const buildingContents = scavengeStateForTarget(poi);
    return createTacticalState({
      zoneId: zoneInfo.zone.zoneId,
      units,
      // Guards hold intersections, patrollers walk streets — both kept off the
      // squad's doorstep (see ENEMY_SPAWN_AVOID_SPAWN_METERS).
      enemies: buildTacticalForce(zoneInfo.zone, { guards: guardCount, patrollers: patrollerCount }, { avoidCoordinate: entryCoordinate, avoidRadiusMeters: ENEMY_SPAWN_AVOID_SPAWN_METERS }),
      // Supply runs no longer start with supplies scattered on the ground — a building's
      // "Potential Supplies" tooltip is the pull now. The scatteredItems pool still
      // exists so a survivor can DROP an item to free a bag slot and reclaim it.
      scatteredItems: [],
      buildingContents,
      // One recruitable wild survivor per supply run (purple building, no supplies).
      survivorBuildings: survivorBuildingForZone(zoneInfo.zone, buildingContents),
    });
  };

  document.getElementById("supply-run-poi-name").textContent = `${poi.name} (${enemyCount} patrol${enemyCount === 1 ? "" : "s"})`;
  document.getElementById("supply-run-summary").innerHTML = "";
  closeOutcomeModal();
  clearThreatAlert();
  clearThreatLog();
  clearTacticalActivity();
  document.getElementById("threat-banner")?.classList.remove("show");
  setScreen("tactical");

  preTacticalCamera = { center: map.getCenter(), zoom: map.getZoom(), bearing: map.getBearing(), pitch: map.getPitch() };
  setOverworldCameraConstraints(false);
  let tacticalMounted = false;
  const mountTactical = () => {
    if (tacticalMounted) return;
    tacticalMounted = true;
    setOverworldLayersVisible(false);
    tacticalView = createTacticalView({
      map,
      zone: zoneInfo.zone,
      roadGraph,
      navGrid: zoneInfo.navGrid,
      poiCoordinate: poi.coordinate,
      poiBuilding: zoneInfo.poiBuilding,
      poiEntrances: zoneInfo.poiEntrances,
      tunables: currentTacticalTunables(),
      createSession,
      hooks: { onChange: renderTacticalChrome, onLog: onTacticalLog, onSessionOver: onTacticalOver, onPhaseChange: showTurnBanner, onTransfer: showTransferResult },
    });
    renderTacticalChrome();
    resolveNextTacticalMounted?.();
    resolveNextTacticalMounted = null;
  };
  map.once("moveend", mountTactical);
  map.fitBounds(boundsForCircle(poi.coordinate, zoneInfo.zone.radiusMeters), { padding: TACTICAL_CAMERA_PADDING, duration: playtestEntry ? 0 : TACTICAL_CAMERA_DURATION_MS });
  if (playtestEntry) setTimeout(mountTactical, 0);
}

function waitForNextTacticalMounted() {
  if (tacticalView) return Promise.resolve();
  return new Promise((resolve) => { resolveNextTacticalMounted = resolve; });
}

function inventoryLabel(inventory) {
  if (!inventory?.length) return "empty-handed";
  return inventory.map(itemName).join(", ");
}

// The little status chip on a supply-run unit card. A lost unit shows nothing here
// (it gets a LOST overlay instead); a living unit that hasn't acted shows
// nothing either ("Ready" was just noise for "alive").
function tacticalUnitStatusLabel({ unit, isDead, isExtracted, hasActed }) {
  if (isDead) return "";
  if (isExtracted) return "Extracted";
  if (unit?.status === "scavenging") return "Inside";
  if (hasActed) return "Already moved";
  return "";
}

function renderHealthPips(health) {
  const max = Math.max(1, health?.max ?? 5);
  const current = Math.max(0, health?.current ?? max);
  let html = `<span class="hp-pips" aria-label="${current} of ${max} HP">`;
  for (let i = 0; i < max; i += 1) html += `<span class="hp-pip${i < current ? "" : " empty"}"></span>`;
  return `${html}</span>`;
}

function itemIcon(category) {
  if (category === "food") {
    return `<svg viewBox="0 0 256 256"><path fill="currentColor" d="M224 104h-8.37a88 88 0 0 0-175.26 0H32a8 8 0 0 0-8 8a104.35 104.35 0 0 0 56 92.28V208a16 16 0 0 0 16 16h64a16 16 0 0 0 16-16v-3.72A104.35 104.35 0 0 0 232 112a8 8 0 0 0-8-8m-59.34 88a8 8 0 0 0-4.66 7.3v8.7H96v-8.7a8 8 0 0 0-4.66-7.3a88.29 88.29 0 0 1-51-72h175.29a88.29 88.29 0 0 1-50.97 72"/></svg>`;
  }
  if (category === "materials") {
    return `<svg viewBox="0 0 256 256"><path fill="currentColor" d="M216 48H40a16 16 0 0 0-16 16v128a16 16 0 0 0 16 16h176a16 16 0 0 0 16-16V64a16 16 0 0 0-16-16M40 64h80v48H40Zm0 64h48v64H40Zm64 64v-64h112v64Zm112-80h-80V64h80Z"/></svg>`;
  }
  if (category === "tools") {
    return `<svg viewBox="0 0 256 256"><path fill="currentColor" d="m226.76 69l-39.73 39.74l-39.76-39.75l39.74-39.73A64 64 0 0 0 99.57 108l-70.31 70.31a24 24 0 0 0 33.94 33.94l70.31-70.31A64 64 0 0 0 226.76 69M51.88 200.94a8 8 0 0 1-11.31-11.31l70.31-70.31a8 8 0 0 1 11.31 11.31Z"/></svg>`;
  }
  if (category === "consumables") {
    return `<svg viewBox="0 0 256 256"><path fill="currentColor" d="M213.66 82.34l-40-40a8 8 0 0 0-11.32 0l-120 120a8 8 0 0 0 0 11.32l40 40a8 8 0 0 0 11.32 0l120-120a8 8 0 0 0 0-11.32M88 196.69L59.31 168L96 131.31L124.69 160Zm48-48L107.31 120L168 59.31L196.69 88Z"/></svg>`;
  }
  return `<svg viewBox="0 0 256 256"><path fill="currentColor" d="M184 89.57V84c0-25.08-37.83-44-88-44S8 58.92 8 84v40c0 20.89 26.25 37.49 64 42.46V172c0 25.08 37.83 44 88 44s88-18.92 88-44v-40c0-20.7-25.42-37.32-64-42.43M96 56c41.21 0 72 14.78 72 28s-30.79 28-72 28s-72-14.78-72-28s30.79-28 72-28m64 144c-41.21 0-72-14.78-72-28v-4.17c2.63.1 5.29.17 8 .17c17.21 0 33.14-2.23 46.37-6.08A122 122 0 0 0 160 160c41.21 0 72-14.78 72-28v40c0 13.22-30.79 28-72 28"/></svg>`;
}

function lucideIcon(path) {
  return `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">${path}</svg>`;
}

function swordIcon() {
  return lucideIcon(`<path d="M14.5 17.5 3 6V3h3l11.5 11.5"/><path d="m13 19 6-6"/><path d="m16 16 4 4"/><path d="m19 13 2-2"/>`);
}

function shieldPlusIcon() {
  return lucideIcon(`<path d="M20 13c0 5-3.5 7.5-8 9-4.5-1.5-8-4-8-9V5l8-3 8 3z"/><path d="M9 12h6"/><path d="M12 9v6"/>`);
}

function sneakerMoveIcon() {
  return lucideIcon(`<path d="M3 17h13.5c1.7 0 3.1-.9 3.8-2.4L21 13l-4.2-1.4-3.6-4.1H10v5H7.5L6 10H3z"/><path d="M3 17v2h14"/><path d="M17 5l2-2"/><path d="M20 8h2"/><path d="M16 9l2-2"/>`);
}

function backpackIcon() {
  return lucideIcon(`<path d="M6 7.5C6 5 8 3 10.5 3h3C16 3 18 5 18 7.5V21H6z"/><path d="M9 7V5h6v2"/><path d="M6 12h12"/><path d="M9 16h6"/><path d="M4 10v7"/><path d="M20 10v7"/>`);
}

function xIcon() {
  return lucideIcon(`<path d="M18 6 6 18"/><path d="m6 6 12 12"/>`);
}

function flashlightIcon() {
  return lucideIcon(`<path d="m6 6 12 12"/><path d="m4 8 4-4"/><path d="m2 6 4-4"/><path d="m9 9 7-7 6 6-7 7"/><path d="M18 10h4"/><path d="M14 2v4"/>`);
}

function gearIconForItem(itemId) {
  const item = getItem(itemId);
  const effects = item?.equipment?.effects ?? {};
  if (itemId === "flashlight") return flashlightIcon();
  if (effects.carryCapacityBonus) return backpackIcon();
  if (effects.moveBonusMetersPerTurn) return sneakerMoveIcon();
  const loadoutSlot = equipmentLoadoutSlot(itemId);
  if (loadoutSlot === EQUIPMENT_LOADOUT_SLOTS.weapon) return swordIcon();
  if (loadoutSlot === EQUIPMENT_LOADOUT_SLOTS.defense) return shieldPlusIcon();
  return itemIcon(item?.category ?? "tools");
}

function gearMarkerIcon() {
  return `<svg viewBox="0 0 256 256"><path fill="currentColor" d="M230.1 108.8l-23.6-13.6c.5-4.7.5-9.7 0-14.4l23.6-13.6a8 8 0 0 0 3.1-10.8l-24-41.6a8 8 0 0 0-10.4-3.4l-23.9 10.1a79.8 79.8 0 0 0-12.6-7.3L159.4 8a8 8 0 0 0-7.9-6.6h-48a8 8 0 0 0-7.9 6.6l-2.9 26.2a79.8 79.8 0 0 0-12.6 7.3L56.2 31.4a8 8 0 0 0-10.4 3.4l-24 41.6a8 8 0 0 0 3.1 10.8l23.6 13.6c-.5 4.7-.5 9.7 0 14.4L24.9 128.8a8 8 0 0 0-3.1 10.8l24 41.6a8 8 0 0 0 10.4 3.4l23.9-10.1a79.8 79.8 0 0 0 12.6 7.3l2.9 26.2a8 8 0 0 0 7.9 6.6h48a8 8 0 0 0 7.9-6.6l2.9-26.2a79.8 79.8 0 0 0 12.6-7.3l23.9 10.1a8 8 0 0 0 10.4-3.4l24-41.6a8 8 0 0 0-3.1-10.8l-23.6-13.6c.2-2.4.4-4.8.4-7.2s-.1-4.8-.4-7.2ZM128 144a36 36 0 1 1 36-36a36 36 0 0 1-36 36Z"/></svg>`;
}

function renderInventorySlots(inventory, capacity, { survivorId = "", interactiveEquipment = false } = {}) {
  const slots = [];
  const size = Math.max(capacity ?? DEFAULT_CARRY_CAPACITY, inventory?.length ?? 0);
  for (let i = 0; i < size; i += 1) {
    const itemId = inventory?.[i] ?? null;
    if (!itemId) {
      slots.push(`<div class="item-inventory-slot empty" aria-label="Empty inventory slot"><span class="item-inventory-icon"></span><span class="item-inventory-name"></span></div>`);
      continue;
    }
    const item = getItem(itemId);
    const category = item?.category ?? "valuables";
    const equippable = Boolean(item?.equipment);
    const dragAttrs = interactiveEquipment && equippable
      ? ` draggable="true" data-gear-drag-kind="bag" data-survivor="${escapeHtml(survivorId)}" data-bag-index="${i}" data-item="${escapeHtml(itemId)}"`
      : "";
    const selectedClass = interactiveEquipment && isSelectedGear({ kind: "bag", survivorId, index: i, itemId }) ? " selected-gear" : "";
    slots.push(`<div class="item-inventory-slot ${escapeHtml(category)}${equippable ? " equippable" : ""}${selectedClass}" title="${escapeHtml(item?.name ?? itemId)}"${dragAttrs}>
      ${equippable ? `<span class="gear-marker" aria-hidden="true">${gearMarkerIcon()}</span>` : ""}
      <span class="item-inventory-icon" aria-hidden="true">${equippable ? gearIconForItem(itemId) : itemIcon(category)}</span>
      <span class="item-inventory-name">${escapeHtml(item?.name ?? itemId)}</span>
    </div>`);
  }
  return `<div class="item-inventory-grid">${slots.join("")}</div>`;
}

function renderOutcomeItemBoxes(items) {
  const inventory = items ?? [];
  return `<div class="outcome-item-boxes">${renderInventorySlots(inventory, Math.max(1, inventory.length))}</div>`;
}

function outcomeMetricValueHtml(value, category = "") {
  const icon = category
    ? `<span class="outcome-metric-icon ${escapeHtml(category)}" aria-hidden="true">${itemIcon(category)}</span>`
    : "";
  return `<b>+${formatResource(value)}${icon}</b>`;
}

function renderSurvivorStatusCard({
  survivorId,
  statusLabel = "",
  inventory = [],
  capacity = DEFAULT_CARRY_CAPACITY,
  equipmentInteractive = false,
  equipmentDisabled = false,
  compactEquipment = false,
  showInventory = true,
}) {
  const survivor = getSurvivorV4(campaign, survivorId);
  const health = survivor?.health ?? { current: SURVIVOR_MAX_HEALTH, max: SURVIVOR_MAX_HEALTH };
  const professionHasUnlock = professionCategory(survivor?.backgroundId) !== "none";
  return `
    <div class="tactical-unit-head">
      <div class="tactical-unit-title">
        <b>${escapeHtml(survivorName(survivorId))}</b>
        <button type="button" class="profession-pill${professionHasUnlock ? " has-unlock" : ""}" data-profession-survivor="${survivorId}">${escapeHtml(professionName(survivor?.backgroundId) || "Unassigned")}</button>
        <span class="tactical-unit-stats">${escapeHtml(equipmentStatsHtml(survivor))}</span>
      </div>
      ${renderHealthPips(health)}
      ${statusLabel ? `<span class="tactical-unit-status">${escapeHtml(statusLabel)}</span>` : ""}
    </div>
    <div class="survivor-slot-section equipment-panel">
      <div class="survivor-slot-label">Gear:</div>
      ${renderEquipmentSlots(survivor, { interactive: equipmentInteractive, disabled: equipmentDisabled, compact: compactEquipment })}
    </div>
    ${showInventory ? `<div class="survivor-slot-section">
      <div class="survivor-slot-label">Bag:</div>
      ${renderInventorySlots(inventory, capacity, { survivorId, interactiveEquipment: equipmentInteractive && !equipmentDisabled })}
    </div>` : ""}
  `;
}

// The supply-run unit card renders the unit's LIVE loadout (equipment can change
// mid-run — see tacticalView.equipItem), not the base survivor's. A survivor can
// equip only what it's currently carrying; there are no cross-unit transfers.
function renderTacticalUnitCard({ id, unit, isDead, isExtracted, hasActed = false, interactive = false, transferSelection = null }) {
  const survivorId = unit?.survivorId ?? id;
  const survivor = getSurvivorV4(campaign, survivorId);
  // Prefer the live tactical unit's HP so wounds taken mid-run show on the card;
  // fall back to the campaign survivor for dead/extracted units (no live unit).
  const health = unit?.health ?? survivor?.health ?? { current: SURVIVOR_MAX_HEALTH, max: SURVIVOR_MAX_HEALTH };
  const professionHasUnlock = professionCategory(survivor?.backgroundId) !== "none";
  const equipmentItemIds = unit?.equipmentItemIds ?? [];
  const inventory = unit?.inventory ?? [];
  const capacity = unit?.carryCapacity ?? DEFAULT_CARRY_CAPACITY;
  const canEquip = interactive && unit && !isDead && !isExtracted;
  const move = unit?.moveMetersPerTurn ?? (DEFAULT_TACTICAL_MOVE_METERS_PER_TURN);
  const stats = `Att ${unit?.offenseBonus ?? 0} / Def ${unit?.defenseBonus ?? 0} / Speed ${move}`;

  const equippedBySlot = equipmentLoadoutMap(equipmentItemIds);
  const gearSlots = EQUIPMENT_LOADOUT_SLOT_ORDER.map((loadoutSlot) => {
    const equipped = equippedBySlot.get(loadoutSlot);
    const slotLabel = equipmentSlotLabel(loadoutSlot);
    if (!equipped) return `<li class="equipment-slot empty"><span class="gear-marker faint" aria-hidden="true">${gearMarkerIcon()}</span><span class="equipment-slot-name">${escapeHtml(slotLabel)}</span></li>`;
    const itemId = equipped.itemId;
    const attrs = canEquip ? ` data-equipment-unequip="${escapeHtml(id)}::${escapeHtml(itemId)}"` : "";
    return `<li class="equipment-slot">
        <button class="unequip" type="button"${attrs} title="${escapeHtml(itemName(itemId))}: ${escapeHtml(effectLine(itemId))} — click to unequip" ${canEquip ? "" : "disabled"}>
          <span class="gear-marker" aria-hidden="true">${gearMarkerIcon()}</span>
          <span class="item-inventory-icon" aria-hidden="true">${gearIconForItem(itemId)}</span>
          <span class="equipment-slot-name">${escapeHtml(itemName(itemId))}</span>
          <span class="equipment-slot-effect">${escapeHtml(effectLine(itemId))}</span>
        </button>
      </li>`;
  }).join("");

  const bagSize = Math.max(capacity, inventory.length);
  const bagSlots = [];
  for (let i = 0; i < bagSize; i += 1) {
    const itemId = inventory[i] ?? null;
    if (!itemId) { bagSlots.push(`<div class="item-inventory-slot empty"><span class="item-inventory-icon"></span><span class="item-inventory-name"></span></div>`); continue; }
    const item = getItem(itemId);
    const category = item?.category ?? "valuables";
    const equippable = Boolean(item?.equipment);
    const equipAttrs = canEquip && equippable ? ` data-equipment-equip="${escapeHtml(id)}::${escapeHtml(itemId)}" role="button" tabindex="0" title="${escapeHtml(itemName(itemId))}: ${escapeHtml(effectLine(itemId))} — click to equip"` : ` title="${escapeHtml(item?.name ?? itemId)}"`;
    // A give button hands the item to a nearby survivor (see the transfer flow).
    const giving = transferSelection && transferSelection.fromUnitId === id && transferSelection.itemId === itemId;
    const giveBtn = canEquip ? `<button class="item-give${giving ? " giving" : ""}" type="button" data-item-give="${escapeHtml(id)}::${escapeHtml(itemId)}" title="Give ${escapeHtml(itemName(itemId))} to a nearby survivor">⤳</button>` : "";
    bagSlots.push(`<div class="item-inventory-slot ${escapeHtml(category)}${equippable ? " equippable" : ""}${canEquip && equippable ? " equipment-target" : ""}${giving ? " giving" : ""}"${equipAttrs}>
      ${giveBtn}
      ${equippable ? `<span class="gear-marker" aria-hidden="true">${gearMarkerIcon()}</span>` : ""}
      <span class="item-inventory-icon" aria-hidden="true">${equippable ? gearIconForItem(itemId) : itemIcon(category)}</span>
      <span class="item-inventory-name">${escapeHtml(item?.name ?? itemId)}</span>
    </div>`);
  }

  return `
    <div class="tactical-unit-head">
      <div class="tactical-unit-title">
        <b>${escapeHtml(survivorName(survivorId))}</b>
        <button type="button" class="profession-pill${professionHasUnlock ? " has-unlock" : ""}" data-profession-survivor="${survivorId}">${escapeHtml(professionName(survivor?.backgroundId) || "Unassigned")}</button>
        <span class="tactical-unit-stats">${escapeHtml(stats)}</span>
      </div>
      ${renderHealthPips(health)}
      ${(() => { const label = tacticalUnitStatusLabel({ unit, isDead, isExtracted, hasActed }); return label ? `<span class="tactical-unit-status">${escapeHtml(label)}</span>` : ""; })()}
    </div>
    <div class="survivor-slot-section equipment-panel">
      <div class="survivor-slot-label">Gear:</div>
      <ul class="equipment-list">${gearSlots}</ul>
    </div>
    <div class="survivor-slot-section">
      <div class="survivor-slot-label">Bag:</div>
      <div class="item-inventory-grid">${bagSlots.join("")}</div>
    </div>`;
}

// The centered "ENEMY TURN" / "SURVIVOR TURN" title card at the start of a side's
// turn (fade in / hold / fade out — see the #tactical-turn-banner CSS).
function showTurnBanner(phase) {
  const banner = document.getElementById("tactical-turn-banner");
  if (!banner) return;
  const tone = phase === "enemy" ? "enemy" : "survivor";
  // TODO(naming): "ENEMY" is a placeholder — HOSTILES / PATROL are candidates.
  const label = phase === "enemy" ? "ENEMY TURN" : "SURVIVOR TURN";
  banner.className = "";
  banner.innerHTML = `<span>${label}</span>`;
  void banner.offsetWidth; // restart the animation even if it fires back-to-back
  banner.classList.add("show", tone);
}

// A transient toast (transfer feedback / errors) near the top of the supply-run map.
function showTacticalToast(message, { error = false } = {}) {
  const host = document.getElementById("tactical-toast");
  if (!host) return;
  const toast = document.createElement("div");
  toast.className = `toast${error ? " error" : ""}`;
  toast.textContent = message;
  host.appendChild(toast);
  setTimeout(() => toast.remove(), 2700);
}

// Result of a give-item attempt (tacticalView.transferTo): show success or the
// specific reason it couldn't happen.
function showTransferResult(result) {
  if (!result || result.reason === "none" || result.reason === "same") return;
  if (result.ok) { showTacticalToast(`Gave ${itemName(result.itemId)} to ${result.targetName}.`); return; }
  if (result.reason === "too-far") { showTacticalToast("Survivors are not physically close enough to transfer items.", { error: true }); return; }
  if (result.reason === "no-room") { showTacticalToast(`${result.targetName} has no room to carry it.`, { error: true }); }
}

function tacticalOrderLabel(order) {
  if (!order) return "";
  if (order.type === "scavenge") return "Scavenge";
  if (order.type === "interact") return "Pick up";
  if (order.type === "extract") return "Extract";
  if (order.type === "recruit") return "Recruit";
  if (order.type === "drop") return "Drop item";
  if (order.type === "attack") return "Attack";
  if (order.type === "move") return "Move";
  return order.type;
}

function tacticalStatusHealthPips(health) {
  const max = Math.max(1, health?.max ?? SURVIVOR_MAX_HEALTH);
  const current = Math.max(0, health?.current ?? max);
  let html = `<div class="tactical-status-hp" style="--hp-max:${max}" aria-label="${current} of ${max} HP">`;
  for (let i = 0; i < max; i += 1) html += `<span class="hp-pip${i < current ? "" : " empty"}"></span>`;
  return `${html}</div>`;
}

function tacticalStatusGearSlots(unit) {
  const equippedBySlot = equipmentLoadoutMap(unit?.equipmentItemIds ?? []);
  const slots = EQUIPMENT_LOADOUT_SLOT_ORDER.map((loadoutSlot) => {
    const equipped = equippedBySlot.get(loadoutSlot);
    const slotLabel = equipmentSlotLabel(loadoutSlot);
    const itemId = equipped?.itemId;
    const icon = itemId
      ? gearIconForItem(itemId)
      : loadoutSlot === EQUIPMENT_LOADOUT_SLOTS.weapon
        ? swordIcon()
        : loadoutSlot === EQUIPMENT_LOADOUT_SLOTS.defense
          ? shieldPlusIcon()
          : backpackIcon();
    const title = itemId ? `${slotLabel}: ${itemName(itemId)}` : `No ${slotLabel.toLowerCase()}`;
    return `<span class="tactical-status-gear-slot${itemId ? "" : " empty"}" title="${escapeHtml(title)}" aria-label="${escapeHtml(title)}">${icon}</span>`;
  });
  return `<div class="tactical-status-gear" aria-label="Equipment">${slots.join("")}</div>`;
}

function tacticalUnitOverlayState({ unit, id, isDead, isExtracted, stagedOrder, engagement, actedIds, skippedIds, playerTurn }) {
  if (isDead) return { text: "Down", tone: "down", cancelable: false };
  if (isExtracted) return { text: "Extracted", tone: "down", cancelable: false };
  if (engagement) return { text: "Engaged: Attacking", tone: "engaged", cancelable: true };
  if (unit?.status === "scavenging") return { text: "Inside", tone: "planned", cancelable: true };
  if (stagedOrder && actedIds.has(id)) return { text: `Next: ${tacticalOrderLabel(stagedOrder)}`, tone: "ordered", cancelable: true };
  if (stagedOrder) return { text: `Planned: ${tacticalOrderLabel(stagedOrder)}`, tone: "planned", cancelable: true };
  if (skippedIds.has(id)) return { text: "Skipped", tone: "ordered", cancelable: false };
  if (actedIds.has(id)) return { text: "Ordered: Moved", tone: "ordered", cancelable: false };
  if (playerTurn) return { text: "Needs orders", tone: "needs-orders", cancelable: false };
  return { text: "Waiting", tone: "ordered", cancelable: false };
}

// The survivor turn stack above the End Turn button: one slide-reveal row per
// survivor with HP pips, loadout icons, and explicit order state.
function renderTacticalUnitStatusRow(state, staged, engagements, actedIds, skippedIds, enemyPhaseActive, over) {
  const host = document.getElementById("tactical-unit-status");
  if (!host) return;
  const ids = [...new Set([...Object.keys(state.units), ...state.deadUnitIds, ...state.extractedUnitIds])];
  const playerTurn = !enemyPhaseActive && !over;
  const selectedId = tacticalView.getSelectedUnitId();
  host.innerHTML = "";
  for (const id of ids) {
    const unit = state.units[id];
    const isDead = state.deadUnitIds.includes(id);
    const isExtracted = state.extractedUnitIds.includes(id);
    const selectable = !isDead && !isExtracted;
    const overlayState = tacticalUnitOverlayState({ unit, id, isDead, isExtracted, stagedOrder: staged[id], engagement: engagements[id], actedIds, skippedIds, playerTurn });
    const row = document.createElement("div");
    row.className = "tactical-unit-status-row";
    const clearButton = document.createElement("button");
    clearButton.type = "button";
    clearButton.className = "tactical-unit-clear";
    clearButton.disabled = !overlayState.cancelable || !selectable;
    clearButton.title = overlayState.cancelable ? "Clear order" : "Nothing to clear";
    clearButton.setAttribute("aria-label", clearButton.title);
    clearButton.innerHTML = xIcon();
    clearButton.addEventListener("click", (event) => {
      event.stopPropagation();
      if (clearButton.disabled) return;
      tacticalView.selectUnit(id);
      tacticalView.clearSelectedOrder();
      renderTacticalChrome();
    });
    row.appendChild(clearButton);

    const box = document.createElement("div");
    box.className = `tactical-unit-status-box ${overlayState.tone}${selectable ? " selectable" : ""}${id === selectedId ? " selected" : ""}`;
    box.innerHTML = `
      <div class="tactical-status-main">
        <span class="name">${escapeHtml(survivorName(unit?.survivorId ?? id))}</span>
        <span class="state">${escapeHtml(overlayState.text)}</span>
      </div>
      ${tacticalStatusGearSlots(unit)}
      <div class="tactical-status-vitals">${tacticalStatusHealthPips(unit?.health)}</div>
    `;
    // Clicking a box selects that survivor for movement, same as its sidebar card.
    if (selectable) box.addEventListener("click", () => { tacticalView.selectUnit(id); renderTacticalChrome(); });
    row.appendChild(box);
    host.appendChild(row);
  }
  host.hidden = ids.length === 0;
}

function renderTacticalChrome() {
  if (!tacticalView) return;
  const state = tacticalView.getState();
  if (activeDefense) { refreshDefenseOverlay(state); maybeResolveDefense(state); }
  const staged = tacticalView.getStagedOrders();
  const engagements = tacticalView.getEngagements();
  const selectedId = tacticalView.getSelectedUnitId();
  const over = tacticalView.isOver();

  const enemyPhaseActive = tacticalView.isEnemyPhaseRunning();
  const transferSelection = tacticalView.getTransferSelection();
  const actedIds = tacticalView.getActedUnitIds();
  const skippedIds = tacticalView.getSkippedUnitIds();
  const unitList = document.getElementById("unit-list");
  unitList.innerHTML = "";
  const allIds = new Set([...Object.keys(state.units), ...state.deadUnitIds, ...state.extractedUnitIds]);
  for (const id of allIds) {
    const isDead = state.deadUnitIds.includes(id);
    const isExtracted = state.extractedUnitIds.includes(id);
    const unit = state.units[id];
    const hasActed = actedIds.has(id) && unit?.status !== "scavenging";
    // While an item is picked up to give, a valid recipient card is a drop target.
    const isTransferTarget = transferSelection && !isDead && !isExtracted && id !== transferSelection.fromUnitId;
    const card = document.createElement("div");
    card.className = ["tactical-unit-card", "survivor-loadout-card", id === selectedId ? "selected" : "", isDead ? "dead lost" : "", isExtracted ? "extracted" : "", hasActed ? "acted" : "", isTransferTarget ? "transfer-target" : ""].filter(Boolean).join(" ");
    card.dataset.tacticalUnit = id;
    card.setAttribute("role", "button");
    card.setAttribute("tabindex", isDead || isExtracted ? "-1" : "0");
    card.setAttribute("aria-pressed", id === selectedId ? "true" : "false");
    card.setAttribute("aria-disabled", isDead || isExtracted ? "true" : "false");
    card.innerHTML = renderTacticalUnitCard({ id, unit, isDead, isExtracted, hasActed, interactive: !enemyPhaseActive, transferSelection });
    const selectCard = () => {
      if (isDead || isExtracted) return;
      // A pending transfer turns a card click into "give to this survivor".
      if (tacticalView.getTransferSelection()) { showTransferResult(tacticalView.transferTo(id)); renderTacticalChrome(); return; }
      tacticalView.selectUnit(id);
    };
    card.addEventListener("click", (event) => {
      if (event.target.closest("[data-profession-survivor]") || event.target.closest("[data-equipment-equip]") || event.target.closest("[data-equipment-unequip]") || event.target.closest("[data-item-give]")) return;
      selectCard();
    });
    card.addEventListener("keydown", (event) => {
      if (event.key !== "Enter" && event.key !== " ") return;
      if (event.target.closest("[data-equipment-equip]") || event.target.closest("[data-equipment-unequip]")) return;
      event.preventDefault();
      selectCard();
    });
    for (const button of card.querySelectorAll("[data-profession-survivor]")) {
      button.addEventListener("click", (event) => {
        event.stopPropagation();
        showProfessionDialog(getSurvivorV4(campaign, button.dataset.professionSurvivor));
      });
    }
    // Equip a carried item / unequip a loadout item (a free action on that unit).
    const parsePair = (value) => { const [unitId, itemId] = String(value).split("::"); return { unitId, itemId }; };
    for (const el of card.querySelectorAll("[data-equipment-equip]")) {
      const equip = () => { const { unitId, itemId } = parsePair(el.dataset.equipmentEquip); tacticalView.equipItem(unitId, itemId); };
      el.addEventListener("click", (event) => { event.stopPropagation(); equip(); });
      el.addEventListener("keydown", (event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); event.stopPropagation(); equip(); } });
    }
    for (const el of card.querySelectorAll("[data-equipment-unequip]")) {
      el.addEventListener("click", (event) => { event.stopPropagation(); const { unitId, itemId } = parsePair(el.dataset.equipmentUnequip); tacticalView.unequipItem(unitId, itemId); });
    }
    // Pick an item up to give to a nearby survivor (click a recipient to hand it over).
    for (const el of card.querySelectorAll("[data-item-give]")) {
      el.addEventListener("click", (event) => { event.stopPropagation(); const { unitId, itemId } = parsePair(el.dataset.itemGive); tacticalView.selectTransferItem(unitId, itemId); renderTacticalChrome(); });
    }
    unitList.appendChild(card);
  }

  renderTacticalUnitStatusRow(state, staged, engagements, actedIds, skippedIds, enemyPhaseActive, over);

  document.getElementById("tactical-status").textContent = over
    ? `Supply run over — turn ${state.turnNumber}.`
    : transferSelection
      ? `Giving ${itemName(transferSelection.itemId)} — click a nearby survivor (Esc to cancel)`
      : `Turn ${state.turnNumber} — ${Object.keys(state.units).length} in the field`;

  // The on-map action button ends the turn during the supply run, then flips to
  // "Return to Map" (purple, Enter) once it's over. It stays enabled in both
  // modes; only the path-clear button greys out at the end.
  const actionBtn = document.getElementById("btn-tactical-advance");
  const enemyPhase = tacticalView.isEnemyPhaseRunning();
  // In a base defense with the wave cleared, the turn action becomes
  // "Return to base" — the defense's exit.
  const defenseReturn = activeDefense && Object.keys(state.enemies || {}).length === 0 && !enemyPhase;
  const returnMode = over || defenseReturn;
  const turnAction = !returnMode && !enemyPhase ? tacticalView.getAdvanceButtonState() : null;
  actionBtn.classList.remove("day-mode");
  actionBtn.classList.toggle("return-mode", returnMode);
  // During the enemy phase the button turns into a "Skip animations" control —
  // yellow, so it reads as a distinct mode from the green End Turn button.
  actionBtn.classList.toggle("skip-mode", enemyPhase && !over);
  actionBtn.classList.toggle("needs-orders-mode", turnAction?.mode === "needs-orders");
  actionBtn.classList.toggle("planned-mode", turnAction?.mode === "planned");
  actionBtn.querySelector(".label").textContent = returnMode ? (activeDefense ? "Return to base ▶" : "Return to Map ▶") : enemyPhase ? "Skip enemy animations ⏩" : turnAction?.label ?? "End Turn ▶";
  actionBtn.querySelector(".hotkey").textContent = returnMode ? "[enter]" : "[spacebar]";
  actionBtn.disabled = false;
  // "Cancel orders" shows when the selected unit has a scheduled chain or a
  // persistent engagement; "Skip turn" is available whenever a live unit is selected.
  const selectedUnit = selectedId ? state.units[selectedId] : null;
  const clearBtn = document.getElementById("btn-tactical-clear");
  clearBtn.hidden = over || !(tacticalView.getSelectedQueueLength() > 0 || (selectedId && engagements[selectedId]));
  clearBtn.disabled = enemyPhase;
  const skipBtn = document.getElementById("btn-tactical-skip");
  skipBtn.hidden = over || !selectedUnit || selectedUnit.status === "scavenging";
  skipBtn.disabled = enemyPhase;

  const threat = tacticalView.getThreat();
  document.getElementById("supply-run-threat-value").textContent = threat;
  document.getElementById("supply-run-threat-fill").style.width = `${Math.min(100, (threat / THREAT_MAX) * 100)}%`;
  renderTutorial();
}

// A threat-gain this turn: flash the gauge and float a labelled toast over the
// canvas so the player can't miss that their actions had a consequence.
const THREAT_SOURCE_LABELS = {
  scavenge: "Noisy search",
  spotted: "Spotted!",
  // Fires automatically every THREAT_TURN_INTERVAL turns regardless of what
  // you're doing — spell that out so it doesn't read as a random penalty.
  lingering: `Lingering too long (every ${THREAT_TURN_INTERVAL} turns)`,
};

function tacticalActivityForEntry(entry) {
  const name = survivorName(entry.unitId);
  if (entry.type === "unit-engaged") {
    return { text: entry.source === "attack" ? `${name} engages target` : `${name} is engaged`, coordinate: entry.coordinate };
  }
  if (entry.type === "unit-disengaged") {
    return { text: `${name} breaks engagement`, coordinate: entry.coordinate, warn: true };
  }
  if (entry.type === "entered-building" || entry.type === "entered-poi") {
    return { text: `${name} went inside`, coordinate: entry.coordinate };
  }
  if (entry.type === "scavenged-item") {
    return { text: `${name} found ${itemName(entry.itemId)}`, coordinate: entry.coordinate };
  }
  if (entry.type === "scavenge-finished") {
    const reason = entry.reason === "bag-full" ? "pack full" : entry.reason === "building-empty" ? "building empty" : "search complete";
    return { text: `${name} came out (${reason})`, coordinate: entry.coordinate };
  }
  if (entry.type === "scavenged") {
    const items = entry.items?.length ? entry.items.map(itemName).join(", ") : "nothing";
    return { text: `${name} found ${items}`, coordinate: entry.coordinate };
  }
  if (entry.type === "picked-up") {
    return { text: `${name} picked up ${itemName(entry.itemId)}`, coordinate: entry.coordinate };
  }
  if (entry.type === "pickup-full") {
    return { text: `${name}'s pack is full`, coordinate: entry.coordinate, warn: true };
  }
  if (entry.type === "items-left-behind") {
    const items = entry.items?.length ? entry.items.map(itemName).join(", ") : "supplies";
    return { text: `${name} left ${items} behind`, coordinate: entry.coordinate, warn: true };
  }
  if (entry.type === "dropped-item") {
    return { text: `${name} dropped ${itemName(entry.itemId)}`, coordinate: entry.coordinate, warn: true };
  }
  if (entry.type === "used-item") {
    return { text: `${name} used ${itemName(entry.itemId)}` };
  }
  return null;
}

function onTacticalActivity(entries, turnNumber) {
  for (const entry of entries || []) {
    const activity = tacticalActivityForEntry(entry);
    if (!activity) continue;
    const unit = tacticalView?.getState().units[entry.unitId];
    const coordinate = activity.coordinate ?? unit?.position ?? null;
    addTacticalActivityRow(turnNumber, activity.text);
    showTacticalEventToast(activity.text, { coordinate, warn: activity.warn });
  }
}

function addTacticalActivityRow(turnNumber, text) {
  const list = document.getElementById("tactical-activity-list");
  if (!list) return;
  const row = document.createElement("li");
  const turn = document.createElement("span");
  turn.className = "turn";
  turn.textContent = `T${turnNumber}`;
  const body = document.createElement("span");
  body.className = "items";
  body.textContent = text;
  row.append(turn, body);
  list.insertBefore(row, list.firstChild);
  while (list.children.length > TACTICAL_ACTIVITY_MAX_ROWS) list.lastChild.remove();
}

function clearTacticalActivity() {
  const list = document.getElementById("tactical-activity-list");
  if (list) list.innerHTML = "";
  const layer = document.getElementById("tactical-event-toasts");
  if (layer) layer.innerHTML = "";
}

function onTacticalLog(entries, turnNumber) {
  onTacticalActivity(entries, turnNumber);
  // A survivor recruited this turn: a floating "Recruited …!" toast over where
  // they were found (the following companion is already on the map).
  for (const entry of entries || []) {
    if (entry.type === "survivor-recruited") showTacticalEventToast(`Recruited ${entry.name}!`, { coordinate: entry.coordinate });
  }
  const threatEvents = (entries || []).filter((entry) => entry.type === "threat");
  if (!threatEvents.length) return;
  const gained = threatEvents.reduce((sum, entry) => sum + entry.amount, 0);
  const spawns = (entries || []).filter((entry) => entry.type === "enemy-spawned" && entry.reason === "threat").length;

  // Make the rise an event: flash the whole stage, raise a banner, and leave
  // the gauge pulsing until the player takes their next turn.
  flashThreatScreen();
  raiseThreatAlert();
  showThreatBanner(spawns);

  // One toast + one log row per distinct source, summing its amount, so a busy
  // turn reads clearly instead of stacking a dozen identical chips/rows.
  const bySource = new Map();
  for (const entry of threatEvents) bySource.set(entry.source, (bySource.get(entry.source) ?? 0) + entry.amount);
  for (const [source, amount] of bySource) {
    const label = THREAT_SOURCE_LABELS[source] ?? "Threat";
    showThreatToast(`${label} +${amount}`);
    addThreatLogRow(turnNumber, label, amount);
  }
  const sources = [...bySource.keys()].map((s) => THREAT_SOURCE_LABELS[s] ?? s).join(", ");
  logLine("death", `  Threat +${gained} (${sources})${spawns ? ` — ${spawns} patrol${spawns === 1 ? "" : "s"} moving in` : ""}.`);
}

// Newest-first threat-event log under the gauge. The list is capped only by its
// scroll height (CSS shows ~5 rows), so nothing is dropped — older events
// scroll below.
function addThreatLogRow(turnNumber, label, amount) {
  const list = document.getElementById("threat-log");
  if (!list) return;
  const row = document.createElement("li");
  row.innerHTML = `<span><span class="turn">Turn ${turnNumber}</span> · ${label}</span><span class="amt">+${amount}</span>`;
  list.insertBefore(row, list.firstChild);
}

function clearThreatLog() {
  const list = document.getElementById("threat-log");
  if (list) list.innerHTML = "";
}

function flashThreatScreen() {
  const flash = document.getElementById("threat-flash");
  if (!flash) return;
  flash.classList.remove("flash");
  void flash.offsetWidth; // restart the CSS animation
  flash.classList.add("flash");
}

// Persistent gauge pulse — stays on until the next turn is taken (cleared in
// the advance handler), so the player can't miss that threat just rose.
function raiseThreatAlert() {
  document.getElementById("supply-run-threat-gauge")?.classList.add("threat-alert");
}

function clearThreatAlert() {
  document.getElementById("supply-run-threat-gauge")?.classList.remove("threat-alert");
}

function showThreatBanner(spawns) {
  const banner = document.getElementById("threat-banner");
  if (!banner) return;
  banner.textContent = spawns
    ? `🔥 Threat is rising — ${spawns} more patrol${spawns === 1 ? "" : "s"} closing in. Wrap up your run!`
    : `🔥 Threat is rising — wrap up your run quickly!`;
  banner.classList.remove("show");
  void banner.offsetWidth; // restart the CSS animation
  banner.classList.add("show");
}

function showThreatToast(text) {
  const layer = document.getElementById("threat-toasts");
  if (!layer) return;
  const toast = document.createElement("div");
  toast.className = "threat-toast";
  toast.textContent = text;
  layer.appendChild(toast);
  setTimeout(() => toast.remove(), 1600);
}

function showTacticalEventToast(text, { coordinate = null, warn = false } = {}) {
  const layer = document.getElementById("tactical-event-toasts");
  if (!layer) return;
  const toast = document.createElement("div");
  toast.className = `tactical-event-toast${warn ? " warn" : ""}`;
  toast.textContent = text;
  if (coordinate && map) {
    const point = map.project(coordinate);
    toast.style.left = `${point.x}px`;
    toast.style.top = `${Math.max(42, point.y - 10)}px`;
  } else {
    toast.style.left = "50%";
    toast.style.top = "72px";
  }
  layer.appendChild(toast);
  setTimeout(() => toast.remove(), 1900);
}

function supplyRunSummaryHtml(outcomes, summary = {}) {
  const summaryData = {
    poiName: "Supply run",
    supplyRunThreat: 0,
    campaignAfter: campaign,
    globalThreatBefore: currentGlobalThreat(campaign),
    globalThreatAfter: currentGlobalThreat(campaign),
    recruited: [],
    ...summary,
  };
  const banked = outcomes.filter((o) => !o.died);
  const lost = outcomes.filter((o) => o.died);
  const recoveredItems = banked.flatMap((o) => o.items ?? []);
  const lostItems = lost.flatMap((o) => o.items ?? []);
  const foodGained = inventoryNutrition(recoveredItems.filter(isFood));
  const materialsGained = inventoryMaterials(recoveredItems.filter(isMaterial));
  const survivorRows = outcomes.map((outcome) => {
    const survivor = getSurvivorV4(campaign, outcome.survivorId);
    const name = escapeHtml(survivor?.name ?? survivorName(outcome.survivorId));
    const profession = escapeHtml(survivor ? professionLabelForSurvivor(survivor) : "Survivor");
    const status = outcome.died ? "Lost in field" : "Extracted";
    const rowClass = outcome.died ? ` class="bad"` : "";
    return `<li${rowClass}>
      <div class="outcome-survivor-summary">
        <b>${name}</b>
        <span class="outcome-survivor-profession">${profession}</span>
        <span class="outcome-survivor-status">${status}</span>
      </div>
      <div class="outcome-survivor-detail">
        ${renderOutcomeItemBoxes(outcome.items)}
      </div>
    </li>`;
  }).join("");
  // Survivors recruited from the field this run (see the recruit flow) — banked
  // to the roster, so look each up in campaignAfter for its name + profession.
  const recruitedRows = summaryData.recruited.map((recruit) => {
    const survivor = getSurvivorV4(summaryData.campaignAfter, recruit.id);
    const name = escapeHtml(survivor?.name ?? recruit.name ?? "Survivor");
    const profession = escapeHtml(survivor ? professionLabelForSurvivor(survivor) : "Survivor");
    return `<li>
      <div class="outcome-survivor-summary">
        <b>${name}</b>
        <span class="outcome-survivor-profession">${profession}</span>
        <span class="outcome-survivor-status">Recruited</span>
      </div>
      <div class="outcome-survivor-detail"><span class="muted">Joined the survivors</span></div>
    </li>`;
  }).join("");
  const globalThreatDelta = summaryData.globalThreatAfter - summaryData.globalThreatBefore;
  return `
    <section class="outcome-card">
      <div class="outcome-hero">
        <span>${lost.length ? "Supply run survived" : "Clean extraction"}</span>
        <strong>${escapeHtml(summaryData.poiName)}</strong>
      </div>
      <div class="outcome-metrics">
        <div><span>Food found</span>${outcomeMetricValueHtml(foodGained, "food")}<em>${formatResource(pantryFood(summaryData.campaignAfter))} stored</em></div>
        <div><span>Materials found</span>${outcomeMetricValueHtml(materialsGained, "materials")}<em>${formatResource(baseMaterials(summaryData.campaignAfter))} stored</em></div>
        <div class="${summaryData.supplyRunThreat > 0 ? "hot" : ""}"><span>Threat</span>${outcomeMetricValueHtml(summaryData.supplyRunThreat)}<em>${formatResource(globalThreatDelta)} global</em></div>
      </div>
      <h3>Squad</h3>
      <ul class="outcome-list">${survivorRows || `<li><span class="muted">No squad outcome recorded.</span></li>`}</ul>
      ${recruitedRows ? `<h3>Recruited</h3><ul class="outcome-list">${recruitedRows}</ul>` : ""}
      ${lostItems.length ? `<h3>Lost in the field</h3>${renderOutcomeItemBoxes(lostItems)}` : ""}
      ${globalThreatCritical(summaryData.campaignAfter) ? `<p class="outcome-warning">Neighborhood threat is critical. The base is drawing attention.</p>` : ""}
    </section>`;
}

function showOutcomeModal(html) {
  document.getElementById("supply-run-outcome-body").innerHTML = html;
  document.getElementById("supply-run-outcome-modal").hidden = false;
}

function closeOutcomeModal() {
  const modal = document.getElementById("supply-run-outcome-modal");
  if (!modal) return;
  modal.hidden = true;
  document.getElementById("supply-run-outcome-body").innerHTML = "";
}

function scoutingReportsFromTacticalState(state, zoneInfo, discoveredKeys) {
  const reports = [];
  let buildingIndex = -1;
  for (const feature of zoneInfo.zone.buildings) {
    buildingIndex += 1;
    const id = String(buildingKey(feature, buildingIndex));
    // Only buildings the squad actually scouted this supply run persist to the map —
    // an undiscovered building stays unknown on the overworld.
    if (discoveredKeys && !discoveredKeys.has(id)) continue;
    const pool = state.buildingContents?.[id] ?? null;
    if (!pool) continue;
    const stocked = pool.contents.length > 0;
    reports.push({
      buildingId: id,
      scavengeState: stocked && pool.promising ? "promising" : stocked ? "stocked" : "emptied",
      itemCount: stocked ? pool.contents.length : 0,
      promising: Boolean(pool.promising),
    });
  }
  return reports;
}

function onTacticalOver() {
  // A base defense reuses this session-over hook: the session is over only when
  // every defender is down (no extraction in a defense), so resolve it as a loss.
  // resolveDefense is idempotent, so a safe-room breach already resolved this
  // turn wins the race harmlessly.
  if (activeDefense) {
    const status = defenseStatus(tacticalView.getState());
    resolveDefense(status === "ongoing" ? "lost-overrun" : status);
    return;
  }
  if (!activeSupplyRun || activeSupplyRun.over) return;
  activeSupplyRun.over = true;
  const tacticalState = tacticalView.getState();
  const outcomes = supplyRunOutcomeFromState(tacticalState, activeSupplyRun.squadUnits);
  const supplyRunThreat = tacticalView.getThreat();
  const campaignBefore = campaign;
  const zoneInfo = zoneInfoFor(poiById(activeSupplyRun.poiId));
  campaign = applySupplyRunOutcome(campaign, { poiId: activeSupplyRun.poiId, survivorOutcomes: outcomes, supplyRunThreat });
  campaign = recordBuildingScouting(campaign, scoutingReportsFromTacticalState(tacticalState, zoneInfo, tacticalView.getDiscoveredBuildingKeys()), { zoneId: zoneInfo.zone.zoneId });
  // Bank survivors recruited mid-run — but only those whose recruiter survived
  // (the "lost if recruiter dies" rule; the companion was already dropped in-run
  // if the recruiter died, but guard here too).
  const recruitedSurvivors = [];
  for (const recruit of tacticalState.pendingRecruits ?? []) {
    if ((tacticalState.deadUnitIds ?? []).includes(recruit.recruiterUnitId)) continue;
    const survivorId = `survivor:wild:${recruit.buildingId}`;
    const before = campaign;
    campaign = recruitSurvivorV4(campaign, { id: survivorId, name: recruit.name, backgroundId: recruit.backgroundId });
    if (campaign !== before) recruitedSurvivors.push({ id: survivorId, name: recruit.name });
  }
  // SOFT HOUSING CAP: recruiting is never blocked; if it pushes the roster past
  // housing capacity we flag a warning shown once the player returns to base.
  // (A future HARD cap would gate recruitSurvivorV4 in campaign-v4 instead.)
  if (recruitedSurvivors.length && isOverHousingCapacity(campaign)) pendingCapacityWarning = true;
  supplyRunsToday += 1;
  save();

  logLine("day", `Day ${campaign.day} · supply run at ${poiById(activeSupplyRun.poiId).name}:`);
  for (const outcome of outcomes) {
    if (outcome.died) logLine("death", `  ${survivorName(outcome.survivorId)} was lost.`);
    else logLine("items", `  ${survivorName(outcome.survivorId)}: ${inventoryLabel(outcome.items)} (+${outcome.value})`);
  }
  for (const recruited of recruitedSurvivors) {
    logLine("day", `  Recruited ${recruited.name} — joined the survivors.`);
  }
  logLine("death", `  Left ${supplyRunThreat} threat on ${poiById(activeSupplyRun.poiId).name} · global threat now ${currentGlobalThreat(campaign)}/${GLOBAL_THREAT_THRESHOLD}.`);
  if (globalThreatCritical(campaign)) logLine("death", `  ⚠ Neighborhood threat is critical — the base is drawing attention.`);

  const summaryHtml = supplyRunSummaryHtml(outcomes, {
    poiName: poiById(activeSupplyRun.poiId).name,
    supplyRunThreat,
    campaignAfter: campaign,
    globalThreatBefore: currentGlobalThreat(campaignBefore),
    globalThreatAfter: currentGlobalThreat(campaign),
    recruited: recruitedSurvivors,
  });
  document.getElementById("supply-run-summary").innerHTML = summaryHtml;
  showOutcomeModal(summaryHtml);
  renderTacticalChrome();
}

function returnToBase() {
  if (activeDefense) { endBaseDefense(); return; } // a defense tears down its own way (keeps base layers, no supply-run outcome)
  closeOutcomeModal();
  if (tacticalView) { tacticalView.destroy(); tacticalView = null; }
  activeSupplyRun = null;
  // A extracted-away or lost squad member may have been deselected; keep only living picks.
  normalizeSelectedSquadIds();
  if (selectedPoiId && !isTargetReachable(selectedPoiId)) selectedPoiId = null;
  setScreen("overworld");
  setOverworldLayersVisible(true);
  setOverworldMarkersVisible(true);
  if (preTacticalCamera && map) {
    map.once("moveend", () => setOverworldCameraConstraints(true));
    fitDefaultOverworldCamera({ duration: TACTICAL_CAMERA_DURATION_MS });
    preTacticalCamera = null;
  } else {
    setOverworldCameraConstraints(true);
  }
  renderOverworld();
  showCapacityWarningIfPending();
}

// SOFT HOUSING CAP warning: shown once, back on the overworld, after a supply run
// recruit pushed the roster over housing capacity. Purely informational — see
// isOverHousingCapacity() in campaign-v4 for the predicate a future hard cap
// would enforce at recruit time.
function showCapacityWarningIfPending() {
  if (!pendingCapacityWarning) return;
  pendingCapacityWarning = false;
  const dialog = document.getElementById("capacity-warning-dialog");
  if (!dialog) return;
  const roster = activeSurvivorsV4(campaign).length;
  const housing = campaignHousingCapacity(campaign);
  const body = document.getElementById("capacity-warning-body");
  if (body) {
    body.textContent = `Your roster is now ${roster} survivor${roster === 1 ? "" : "s"}, but the base only has housing for ${housing}. Build more Barracks or Bedrooms from the Base tab to catch up.`;
  }
  if (typeof dialog.showModal === "function" && !dialog.open) dialog.showModal();
}

// --- Base defense -------------------------------------------------------

function boundsForBaseOutline() {
  const shape = currentBaseShape();
  if (!shape) return null;
  const ring = baseShapeOutlineToGeoJSON(shape)?.features?.[0]?.geometry?.coordinates?.[0];
  if (!ring?.length) return null;
  const lons = ring.map((coordinate) => coordinate[0]);
  const lats = ring.map((coordinate) => coordinate[1]);
  return [[Math.min(...lons), Math.min(...lats)], [Math.max(...lons), Math.max(...lats)]];
}

function startBaseDefense({ waveSize, final = false }) {
  if (tacticalView || activeDefense || !map) return;
  defenseIsFinal = final;
  ensureBaseDefense();
  const shape = currentBaseShape();
  if (!campaign.base?.defense || !shape) { logLine("day", "The base has no defensible layout yet."); return; }

  // The engagement circle around the base: the horde enters from the nearest
  // street crossing this ring. Tighter than a supply run (150m) so it stays zoomed in.
  const center = baseCoordinate();
  const radiusMeters = defenseZoneRadiusMeters(shape);
  const zone = buildTacticalZone({ zoneId: `base-defense:${campaign.base.buildingId ?? "base"}`, centerCoordinate: center, radiusMeters, roadGraph, buildingCollection });

  if (targetPopup?.isOpen()) { popupClosingProgrammatically = true; targetPopup.remove(); popupClosingProgrammatically = false; }
  setOverworldMarkersVisible(false);
  // Base defense reuses the tactical screen + chrome (status stack, End Turn button,
  // engagement/turn UX) — it's the same turn-based game, just played inside the
  // base. So mount into `tacticalView`, drive renderTacticalChrome, and flag the session
  // as a defense so teardown returns to base instead of resolving a supply run.
  document.getElementById("supply-run-poi-name").textContent = "Base defense";
  document.getElementById("supply-run-summary").innerHTML = "";
  clearTacticalActivity();
  setScreen("tactical");

  let defenseMounted = false;
  const mountDefense = () => {
    if (defenseMounted || tacticalView) return;
    if (document.body.dataset.screen !== "tactical") return; // exited during the flight
    defenseMounted = true;
    setLayersVisible(DEFENSE_HIDDEN_LAYER_IDS, false);

    const wallState = campaign.base.defense;
    const detection = detectRooms(shape.cells, wallState);
    const survivors = activeSurvivorsV4(campaign);
    const { grid: baseNavGrid, field: baseField } = buildBaseNavGrid(zone, { shape, wallState, topology: null });
    const spawns = defenderSpawnPositions(shape, detection.rooms, survivors.length);
    // The attacker wave: raiders storm the exterior doors, chipping each down as
    // a destructible field object (base-nav), then flood toward the safe room
    // (their objective) — chipping interior doors on the way. Every door becomes
    // a field object so the overlay renders its HP/breach state uniformly.
    const { fieldObjects, raiders } = buildBaseAssault(zone, shape, wallState, { count: waveSize, objective: safeRoomCentroid(shape, wallState, detection) });
    defenseRender = { shape, wallState, detection, waveSize };
    defenseResolved = false;

    const createSession = () => {
      const units = survivors.map((survivor, index) => ({
        ...createTacticalUnit(survivor),
        inventory: [...(survivor.inventory ?? [])],
        position: spawns[index] ?? center,
      }));
      return createTacticalState({ zoneId: zone.zoneId, units, enemies: raiders, fieldObjects });
    };

    activeDefense = true;
    tacticalView = createTacticalView({
      map,
      // Hide the base building itself from the tactical building layer — it's drawn
      // as walls/rooms by the defense overlay, not as a scavenge blob.
      zone: { ...zone, buildings: zone.buildings.filter((building) => !pointInBuildings(center, [building])) },
      roadGraph,
      navGrid: baseNavGrid,
      poiCoordinate: null, poiBuilding: null, poiEntrances: null,
      tunables: currentTacticalTunables(),
      createSession,
      defense: buildDefenseOverlay(shape, wallState, detection, baseField),
      hooks: { onChange: renderTacticalChrome, onLog: onTacticalLog, onSessionOver: onTacticalOver, onPhaseChange: showTurnBanner, onTransfer: showTransferResult },
    });
    renderTacticalChrome();
  };

  preDefenseCamera = { center: map.getCenter(), zoom: map.getZoom(), bearing: map.getBearing(), pitch: map.getPitch() };
  setOverworldCameraConstraints(false);
  map.once("moveend", mountDefense);
  map.fitBounds(boundsForCircle(center, radiusMeters), { padding: 40, duration: playtestEntry ? 0 : TACTICAL_CAMERA_DURATION_MS });
  // The camera may already be at the base (or fitBounds may not move it), so
  // moveend can never fire — mount directly too (idempotent), mirroring startTacticalRun.
  if (playtestEntry) setTimeout(mountDefense, 0);
}

// Host-built GeoJSON for the base overlay createTacticalView draws in defense mode:
// rooms (safe room tinted), walls, and doorway swings. Labels are deferred.
// `field` (from buildBaseNavGrid) also carries the raw wall/door world segments
// the view turns into vision occluders (walls + intact doors block sight).
function buildDefenseOverlay(shape, wallState, detection, field) {
  const safeRoomCellKey = wallState?.safeRoomCellKey;
  const safeRoom = detection.rooms.find((room) => room.cellKeys?.has(safeRoomCellKey));
  return {
    rooms: roomsToGeoJSON(shape, detection.rooms, safeRoom?.id ?? null, []),
    walls: wallStateToGeoJSON(shape, wallState, []),
    doors: doorSwingsToGeoJSON(shape, shape.cells, wallState, []),
    labels: { type: "FeatureCollection", features: [] },
    wallSegments: field?.walls ?? [],
    doorSegments: field?.doors ?? [],
  };
}

// Re-push the base overlay whenever the tactical state changes, so a door that's
// been chipped shows its remaining HP and a breached one redraws as a broken
// opening. Reads breach state from the live field objects (the same HP the
// raiders' pathing gates on, so the drawn door and the passable door agree).
function refreshDefenseOverlay(state) {
  if (!defenseRender || !tacticalView) return;
  const { shape, wallState } = defenseRender;
  const breachedEdgeKeys = defenseBreachedEdges(state);
  tacticalView.setDefenseOverlay({
    walls: wallStateToGeoJSON(shape, wallState, breachedEdgeKeys),
    doors: doorSwingsToGeoJSON(shape, shape.cells, wallState, breachedEdgeKeys),
    labels: doorHpLabelsToGeoJSON(state.fieldObjects),
  });
}

// The world centroid of the safe room — the raiders' flood objective. Null when
// the base has no resolvable safe room (raiders then fall back to their door).
function safeRoomCentroid(shape, wallState, detection) {
  const safeRoom = detection.rooms.find((room) => room.cellKeys?.has(wallState?.safeRoomCellKey));
  if (!safeRoom?.cellKeys?.size) return null;
  const toWorld = makeToWorld(shape);
  let sumCol = 0, sumRow = 0;
  for (const key of safeRoom.cellKeys) { const [row, col] = key.split(":").map(Number); sumCol += col + 0.5; sumRow += row + 0.5; }
  const n = safeRoom.cellKeys.size;
  return toWorld(sumCol / n, sumRow / n);
}

// Edge keys of doors that have been breached (HP gone) this defense.
function defenseBreachedEdges(state) {
  const set = new Set();
  for (const object of Object.values(state?.fieldObjects || {})) {
    if (object.breached && object.edgeKey) set.add(object.edgeKey);
  }
  return set;
}

// A remaining-HP number over each still-standing door (field objects carry their
// world coordinate + hp). Breached doors get no label — they read as broken.
function doorHpLabelsToGeoJSON(fieldObjects) {
  const features = [];
  for (const object of Object.values(fieldObjects || {})) {
    if (object.kind !== "door" || object.breached || !Array.isArray(object.coordinate)) continue;
    features.push({
      type: "Feature",
      properties: { label: String(object.hp ?? object.maxHp ?? "") },
      geometry: { type: "Point", coordinates: object.coordinate },
    });
  }
  return { type: "FeatureCollection", features };
}

// The win/lose state of a mounted defense, from the live tactical state + base
// geometry: a raider standing in the safe room loses outright; every defender
// down is an overrun; the wave cleared with defenders alive is the win. Order
// matters — a safe-room breach or a wipe is a loss even as the last raider dies.
function defenseStatus(state) {
  if (raiderInSafeRoom(state)) return "lost-safe-room";
  if (Object.keys(state.units || {}).length === 0) return "lost-overrun";
  if (Object.keys(state.enemies || {}).length === 0) return "won";
  return "ongoing";
}

// True when any living raider's position falls inside the safe room's cells.
function raiderInSafeRoom(state) {
  if (!defenseRender) return false;
  const { shape, wallState, detection } = defenseRender;
  const safeRoom = detection.rooms.find((room) => room.cellKeys?.has(wallState?.safeRoomCellKey));
  if (!safeRoom?.cellKeys?.size) return false;
  const fromWorld = makeFromWorld(shape);
  for (const enemy of Object.values(state.enemies || {})) {
    if (!Array.isArray(enemy.position)) continue;
    const [col, row] = fromWorld(enemy.position);
    if (safeRoom.cellKeys.has(cellKey(Math.floor(row), Math.floor(col)))) return true;
  }
  return false;
}

// Called on every defense state change (not mid enemy-animation): once the wave
// is decided, resolve it exactly once.
function maybeResolveDefense(state) {
  if (defenseResolved || tacticalView.isEnemyPhaseRunning()) return;
  const status = defenseStatus(state);
  if (status !== "ongoing") resolveDefense(status);
}

// Persist a decided defense to the campaign (mirrors the retired onDefenseComplete):
// write back defender HP + deaths + threat relief, log it, then branch to the
// run-ending screens (final-day rescue, or a base-fell game over) or a win modal.
function resolveDefense(status) {
  if (defenseResolved) return;
  defenseResolved = true;
  const state = tacticalView.getState();
  const result = status === "won" ? "repelled" : status === "lost-safe-room" ? "safe-room-taken" : "overrun";
  const waveSize = defenseRender?.waveSize ?? 0;
  const outcome = defenseOutcomeFromState(state, { result, waveSize });
  campaign = applyDefenseOutcome(campaign, outcome);
  save();

  const killed = waveSize - Object.keys(state.enemies || {}).length;
  if (result === "repelled") {
    logLine("day", `— Defense held: wave of ${waveSize} repelled, ${killed} attacker(s) killed. —`);
  } else {
    logLine("death", `— The base was overrun on day ${campaign.day}. —`);
  }
  for (const survivorId of outcome.deaths) {
    logLine("death", `  ${survivorName(survivorId) ?? survivorId} was killed defending the base.`);
  }
  for (const survivor of activeSurvivorsV4(campaign)) {
    if (isSurvivorWounded(survivor)) logLine("death", `  ${survivor.name} was wounded defending the base.`);
  }
  if (outcome.breachedDoorCount > 0) logLine("day", `  The crew repairs ${outcome.breachedDoorCount} breached door(s) overnight.`);

  if (defenseIsFinal) {
    // Day-20 climax: this defense ends the run either way.
    campaign = resolveRescue(campaign, result === "repelled" ? "won" : "lost");
    save();
    if (result === "repelled") {
      const out = survivorsExtracted(campaign);
      const total = totalRecruited(campaign);
      logLine("day", `— The evac holds. Airlift inbound. ${out} of ${total} made it out. —`);
      showExtraction();
    } else {
      showGameOver();
    }
    return;
  }

  if (result === "repelled") {
    // Continue the campaign: show the wave-cleared summary; the action button /
    // outcome-return both flow to returnToBase → endBaseDefense.
    const html = defenseOutcomeHtml({ waveSize, killed, deaths: outcome.deaths, breachedDoorCount: outcome.breachedDoorCount });
    document.getElementById("supply-run-summary").innerHTML = html;
    showOutcomeModal(html);
    renderTacticalChrome();
  } else {
    showGameOver(); // safe room taken or all defenders down — the run ends
  }
}

// The wave-cleared summary card (win only). Keeps the defense's own voice rather
// than reusing the raid loot summary.
function defenseOutcomeHtml({ waveSize, killed, deaths, breachedDoorCount }) {
  const lines = [
    `<h3>Wave repelled</h3>`,
    `<p>${killed} of ${waveSize} attacker(s) cut down. Neighborhood threat cools.</p>`,
  ];
  if (deaths.length) lines.push(`<p class="death">Lost defending the base: ${deaths.map((id) => survivorName(id) ?? id).join(", ")}.</p>`);
  if (breachedDoorCount > 0) lines.push(`<p>${breachedDoorCount} door(s) breached — repaired overnight.</p>`);
  return lines.join("");
}

function endBaseDefense() {
  // The defense runs on the raid view; tear it down and clear the flag so the
  // overworld's raid guards don't think a raid is still mounted.
  if (tacticalView) { tacticalView.destroy(); tacticalView = null; }
  activeDefense = false;
  defenseRender = null;
  defenseResolved = false;
  closeOutcomeModal();
  setScreen("overworld");
  setLayersVisible(DEFENSE_HIDDEN_LAYER_IDS, true);
  setOverworldLayersVisible(true);
  setOverworldMarkersVisible(true);
  if (preDefenseCamera) {
    map.once("moveend", () => setOverworldCameraConstraints(true));
    map.easeTo({ ...preDefenseCamera, duration: TACTICAL_CAMERA_DURATION_MS });
    preDefenseCamera = null;
  } else {
    setOverworldCameraConstraints(true);
  }
  renderOverworld();
}

function showGameOver() {
  document.getElementById("game-over-detail").textContent = defenseIsFinal
    ? `The evac came too late — the base fell on day ${campaign.day}, with the airlift almost in sight.`
    : `The base fell on day ${campaign.day}.`;
  document.body.dataset.gameOver = "true";
}

// Victory: the day-20 defense held and the community is airlifted out. Score is
// simply how many survivors made it out of the total ever on the roster.
function showExtraction() {
  const out = survivorsExtracted(campaign);
  const total = totalRecruited(campaign);
  document.getElementById("extraction-score").textContent = `${out} of ${total} survivors made it out`;
  const names = activeSurvivorsV4(campaign).map((survivor) => survivor.name);
  document.getElementById("extraction-detail").textContent = names.length
    ? `Airlifted to Honolulu: ${names.join(", ")}.`
    : "No one made it to the airlift.";
  document.body.dataset.won = "true";
}

function resetRun({ tutorialEnabled = false } = {}) {
  campaign = seedCampaign({ tutorialEnabled });
  ensureBaseDefense();
  selectedSupplyRunSquadIds = new Set(defaultSupplyRunSquadIds(campaign));
  selectedPoiId = null;
  supplyRunsToday = 0;
  save();
  reportNewGameStart("reset", campaign);
  document.getElementById("log").innerHTML = "";
  renderOverworld();
}

// --- Playtest URL entrypoints ------------------------------------------
// Human/agent friendly shortcuts for direct visual verification:
//   /?playtest=supply-run
//   /?playtest=supply-run-end-success
//   /?playtest=base-building
//   /?playtest=professions   (one survivor per profession — browse every card/pill/popup)
// These run against an in-memory deterministic campaign and intentionally skip
// the normal autosave path, so they do not overwrite a tester's real run.

function recordPlaytestState(fields = {}) {
  if (!playtestState) return;
  playtestState = { ...playtestState, ...fields };
}

function playtestTargetCandidates() {
  const reachable = [...buildingTargets.values()]
    .filter((target) => String(target.id) !== String(campaign.base?.buildingId ?? ""))
    .map((target) => ({ target, route: routeToTarget(target.id) }))
    .filter(({ route }) => route?.reachable)
    .sort((a, b) => (
      Number(isTargetReachable(b.target.id)) - Number(isTargetReachable(a.target.id))
      || a.route.distanceMeters - b.route.distanceMeters
      || String(a.target.id).localeCompare(String(b.target.id))
    ))
    .slice(0, 50);

  return reachable
    .map((entry) => ({ ...entry, promising: promisingCountFor(entry.target) }))
    .sort((a, b) => (
      Number(isTargetReachable(b.target.id)) - Number(isTargetReachable(a.target.id))
      || b.promising - a.promising
      || a.route.distanceMeters - b.route.distanceMeters
      || String(a.target.id).localeCompare(String(b.target.id))
    ));
}

function pickPlaytestTarget() {
  const candidates = playtestTargetCandidates();
  if (!candidates.length) return [...buildingTargets.values()][0] ?? POIS[0] ?? null;
  // The nearest promising target sometimes landed in a cul-de-sac with almost
  // no streets — dull, and nothing for guards/patrollers to hold. Rank the
  // reachable candidates by how real a street network their supply-run zone has (most
  // corridors) so the playtest drops you somewhere with actual roads; distance
  // is only the tiebreaker (the playtest flies you to the target anyway). Zone
  // building is ~7ms each and cached, so this is a cheap one-time cost.
  const ranked = candidates
    .map((entry) => ({ entry, corridors: zoneInfoFor(entry.target).zone.corridors.size }))
    .sort((a, b) => b.corridors - a.corridors || a.entry.route.distanceMeters - b.entry.route.distanceMeters);
  return ranked[0]?.entry.target ?? candidates[0].target;
}

function selectPlaytestTarget(target) {
  if (!target) return;
  selectedPoiId = target.id;
  selectedSupplyRunSquadIds = new Set(defaultSupplyRunSquadIds(campaign));
  recordPlaytestState({ targetId: target.id, targetName: target.name });
}

function focusPlaytestTarget(target) {
  if (!map || !target?.coordinate) return;
  map.easeTo({ center: target.coordinate, zoom: Math.max(map.getZoom(), 16.2), duration: 0 });
}

function playtestOutcomes(kind) {
  const survivors = activeSurvivorsV4(campaign);
  return survivors.map((survivor, index) => {
    if (kind === "loss" && index === 0) return { survivorId: survivor.id, died: true, items: [], value: 0 };
    const items = index === 0
      ? ["tampon-box", "instant-coffee"]
      : index === 1
        ? ["mp3-player", "first-aid-kit"]
        : ["peanut-butter", "scrap-metal"];
    return { survivorId: survivor.id, died: false, items, value: items.reduce((sum, itemId) => sum + (getItem(itemId)?.value ?? 0), 0) };
  });
}

function applyPlaytestSupplyRunOutcome(target, kind, { supplyRunThreat = 18 } = {}) {
  const outcomes = playtestOutcomes(kind);
  campaign = applySupplyRunOutcome(campaign, { poiId: target.id, survivorOutcomes: outcomes, supplyRunThreat });
  supplyRunsToday = 1;
  return outcomes;
}

function renderSyntheticSupplyRunResult(target, kind) {
  const campaignBefore = campaign;
  const supplyRunThreat = kind === "loss" ? 42 : 18;
  const outcomes = applyPlaytestSupplyRunOutcome(target, kind, { supplyRunThreat });
  setOverworldMarkersVisible(false);
  setOverworldLayersVisible(false);
  document.getElementById("supply-run-poi-name").textContent = `${target.name} (${kind === "loss" ? 3 : 1} patrol${kind === "loss" ? "s" : ""})`;
  document.getElementById("tactical-status").textContent = "Supply run over — playtest result fixture.";
  document.getElementById("unit-list").innerHTML = outcomes.map((outcome) => {
    const unit = { id: outcome.survivorId, survivorId: outcome.survivorId, inventory: outcome.items ?? [], carryCapacity: DEFAULT_CARRY_CAPACITY };
    return `<div class="${["tactical-unit-card", "survivor-loadout-card", outcome.died ? "dead" : "extracted"].filter(Boolean).join(" ")}">${renderTacticalUnitCard({
      id: outcome.survivorId,
      unit,
      order: null,
      isDead: outcome.died,
      isExtracted: !outcome.died,
    })}</div>`;
  }).join("");
  clearTacticalActivity();
  for (const outcome of outcomes) {
    addTacticalActivityRow(0, outcome.died
      ? `${survivorName(outcome.survivorId)} was lost with ${inventoryLabel(outcome.items)}`
      : `${survivorName(outcome.survivorId)} extracted with ${inventoryLabel(outcome.items)}`);
  }
  document.getElementById("supply-run-threat-value").textContent = kind === "loss" ? "42" : "18";
  document.getElementById("supply-run-threat-fill").style.width = `${kind === "loss" ? 42 : 18}%`;
  document.getElementById("supply-run-summary").innerHTML = `<p class="hint">Supply run complete.</p>${supplyRunSummaryHtml(outcomes, {
    poiName: target.name,
    supplyRunThreat,
    campaignAfter: campaign,
    globalThreatBefore: currentGlobalThreat(campaignBefore),
    globalThreatAfter: currentGlobalThreat(campaign),
  })}`;
  const actionBtn = document.getElementById("btn-tactical-advance");
  actionBtn.classList.remove("day-mode");
  actionBtn.classList.add("return-mode");
  actionBtn.querySelector(".label").textContent = "Return to Map ▶";
  actionBtn.querySelector(".hotkey").textContent = "[enter]";
  document.getElementById("btn-tactical-clear").disabled = true;
  setScreen("tactical");
  recordPlaytestState({ applied: true, screen: "tactical", syntheticResult: true, result: kind });
}

function primeBaseUpgrades(target) {
  const outcomes = activeSurvivorsV4(campaign).map((survivor) => ({
    survivorId: survivor.id,
    died: false,
    items: ["running-shoes", "backpack", "crowbar", "flashlight", "first-aid-kit"],
    value: 95,
  }));
  campaign = applySupplyRunOutcome(campaign, { poiId: target.id, survivorOutcomes: outcomes, supplyRunThreat: 10 });
  campaign = depositCarriedInventory(campaign).campaign;
}

function primeHungryDay() {
  campaign = {
    ...campaign,
    pantry: 0,
    survivorsById: Object.fromEntries(Object.entries(campaign.survivorsById).map(([id, survivor]) => [id, { ...survivor, hunger: 1 }])),
  };
}

function primeEndgameBriefing() {
  campaign = {
    ...campaign,
    day: 2,
    endgame: { ...campaign.endgame, briefingAcknowledged: false },
  };
}

async function applyPlaytestEntry() {
  if (!playtestEntry) return;
  await mapReady;

  // ?playtest=defense — jump straight into a base defense on the seeded base.
  // Needs no POI target; the base layout is generated by ensureBaseDefense.
  if (playtestEntry === "defense") {
    ensureBaseDefense();
    if (!campaign.base?.defense) { recordPlaytestState({ applied: false, error: "no-base" }); return; }
    startBaseDefense({ waveSize: defenseWaveSize(campaign) });
    recordPlaytestState({ applied: true, screen: "defense", syntheticResult: false });
    return;
  }

  const target = pickPlaytestTarget();
  if (!target) {
    recordPlaytestState({ applied: false, error: "no-targets" });
    return;
  }

  const overworldOnlyEntries = new Set(["overworld", "tutorial-overworld", "endgame-briefing", "profession-gallery"]);
  if (!overworldOnlyEntries.has(playtestEntry)) selectPlaytestTarget(target);

  if (playtestEntry === "supply-run-threat") {
    campaign = applySupplyRunOutcome(campaign, { poiId: target.id, survivorOutcomes: [], supplyRunThreat: 55 });
  } else if (playtestEntry === "critical-threat") {
    campaign = applySupplyRunOutcome(campaign, { poiId: target.id, survivorOutcomes: [], supplyRunThreat: GLOBAL_THREAT_THRESHOLD });
  } else if (playtestEntry === "base-upgrades") {
    primeBaseUpgrades(target);
  } else if (playtestEntry === "hungry-day") {
    primeHungryDay();
  } else if (playtestEntry === "endgame-briefing") {
    primeEndgameBriefing();
  } else if (playtestEntry === "base-building") {
    campaign = addMaterials(campaign, 50); // stock materials so rooms are buildable right away
  }

  if (playtestEntry === "base-building") activeTab = "base";
  if (playtestEntry === "base-upgrades") activeTab = "survivors";
  if (playtestEntry === "profession-gallery") activeTab = "survivors";

  renderOverworld();
  showEndgameBriefingIfNeeded();
  if (!overworldOnlyEntries.has(playtestEntry)) focusPlaytestTarget(target);

  if (playtestEntry === "tactical" || playtestEntry === "tutorial-supply-run" || playtestEntry === "supply-run-threat" || playtestEntry === "supply-run-items-nearby" || playtestEntry === "supply-run-turn-states") {
    startSupplyRun(target.id);
    await waitForNextTacticalMounted();
    if (playtestEntry === "supply-run-turn-states") tacticalView.applyPlaytestTurnStates();
    recordPlaytestState({ applied: true, screen: "tactical", syntheticResult: false, turnStates: playtestEntry === "supply-run-turn-states" });
    return;
  }

  if (playtestEntry === "supply-run-end-success") {
    renderSyntheticSupplyRunResult(target, "success");
    return;
  }

  if (playtestEntry === "supply-run-end-loss") {
    renderSyntheticSupplyRunResult(target, "loss");
    return;
  }

  if (playtestEntry === "return-summary") {
    const outcomes = applyPlaytestSupplyRunOutcome(target, "success", { supplyRunThreat: 22 });
    logLine("day", `Playtest return summary · supply run at ${target.name}:`);
    for (const outcome of outcomes) logLine(outcome.died ? "death" : "items", `  ${survivorName(outcome.survivorId)}: ${inventoryLabel(outcome.items)} (+${outcome.value})`);
    renderOverworld();
    recordPlaytestState({ applied: true, screen: "overworld", syntheticResult: false, result: "success" });
    return;
  }

  if (playtestEntry === "base-building") zoomToBaseGrid();

  recordPlaytestState({ applied: true, screen: "overworld", syntheticResult: false });
}

// --- Controls -----------------------------------------------------------

// The Supply Run button now lives inside the target popup (see buildTargetPopupContent).
const menuButton = document.getElementById("btn-menu");
const topbarMenu = document.getElementById("topbar-menu");

function setTopbarMenuOpen(open) {
  topbarMenu.hidden = !open;
  menuButton.setAttribute("aria-expanded", open ? "true" : "false");
}

function pageContextSnapshot() {
  return {
    href: location.href,
    appVersion: document.body.dataset.appVersion || "",
    screen: document.body.dataset.screen || "",
    playtest: playtestEntry || "",
  };
}

function campaignSnapshot() {
  return {
    day: campaign.day,
    screen: document.body.dataset.screen || "",
    alive: activeSurvivorsV4(campaign).length,
    food: pantryFood(campaign),
    equipmentValue: availableEquipmentValue(campaign),
    materials: baseMaterials(campaign),
    globalThreat: currentGlobalThreat(campaign),
    selectedPoiId,
    tacticalActive: Boolean(tacticalView),
    tacticalOver: Boolean(tacticalView?.isOver()),
  };
}

function reportDeviceSnapshot() {
  const connection = navigator.connection || null;
  return {
    userAgent: navigator.userAgent,
    hardwareConcurrency: navigator.hardwareConcurrency || null,
    deviceMemory: navigator.deviceMemory || null,
    effectiveType: connection?.effectiveType || null,
    screen: {
      width: screen.width || 0,
      height: screen.height || 0,
      dpr: window.devicePixelRatio || 1,
    },
    viewport: {
      width: window.innerWidth,
      height: window.innerHeight,
    },
  };
}

function perfSampleSnapshot() {
  const memory = performance.memory || {};
  return {
    t: Math.round(performance.now()),
    heapUsed: memory.usedJSHeapSize || null,
    heapTotal: memory.totalJSHeapSize || null,
    domNodes: document.getElementsByTagName("*").length,
    longTaskCount: 0,
    longTaskTotalMs: 0,
    longTaskMaxMs: 0,
    fps: null,
    droppedFrames: 0,
    worstFrameMs: 0,
    errorCount: 0,
    sourceUpdateTotal: 0,
    stateSizes: {
      autosaveBytes: localStorage.getItem(SAVE_KEY)?.length || 0,
    },
    clock: { running: document.body.dataset.screen === "tactical", paused: false },
  };
}

async function postReport(endpoint, payload, button, successLabel) {
  const originalLabel = button.textContent;
  button.disabled = true;
  button.textContent = "Sending...";
  try {
    const response = await fetch(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok || result.ok === false) throw new Error(result.error || `HTTP ${response.status}`);
    button.textContent = successLabel;
    setTimeout(() => { button.textContent = originalLabel; }, 1600);
  } catch (error) {
    console.warn("report failed", endpoint, error);
    button.textContent = "Report failed";
    setTimeout(() => { button.textContent = originalLabel; }, 2200);
  } finally {
    button.disabled = false;
  }
}

menuButton.addEventListener("click", (event) => {
  event.stopPropagation();
  setTopbarMenuOpen(topbarMenu.hidden);
});

document.addEventListener("click", (event) => {
  if (topbarMenu.hidden || event.target.closest("#topbar-menu") || event.target.closest("#btn-menu")) return;
  setTopbarMenuOpen(false);
});

window.addEventListener("keydown", (event) => {
  if (event.key === "Escape") {
    setTopbarMenuOpen(false);
    setDevToolsOpen(false);
  }
});

document.getElementById("auth-menu-button")?.addEventListener("click", () => {
  setTopbarMenuOpen(false);
  if (isSignedIn()) {
    requestSignOut();
    return;
  }
  showAuthDialog();
});

document.getElementById("auth-discord-button")?.addEventListener("click", () => {
  void signInWithDiscord();
});
document.getElementById("start-auth-button")?.addEventListener("click", () => {
  if (isSignedIn()) {
    requestSignOut();
    return;
  }
  void signInWithDiscord();
});

document.getElementById("auth-sign-out-dialog")?.addEventListener("close", (event) => {
  if (event.target.returnValue === "confirm") void signOut();
});

const DEV_SETTING_LABELS = {
  supplyRun: {
    moveMetersPerTurn: "Survivor movement speed",
    offStreetMultiplier: "Off road slowdown multiplier",
    sightMeters: "Enemy vision length",
    coneDegrees: "Enemy vision angle",
    guardCount: "Default spawn # guards",
    patrollerCount: "Default spawn # patrollers",
  },
  baseDefense: {
    initiative: "Combat initiative",
    attackerCap: "Doorway attacker cap",
    doorHitPoints: "Base door HP",
    approachMetersPerSecond: "Horde approach speed",
    hopMs: "Door hop time",
    roundMs: "Round playback time",
    breachPauseMs: "Breach pause",
  },
};

const DEV_SETTING_UNITS = {
  supplyRun: {
    moveMetersPerTurn: "m/turn",
    offStreetMultiplier: "x",
    sightMeters: "m",
    coneDegrees: "deg",
    guardCount: "guards",
    patrollerCount: "patrollers",
  },
  baseDefense: {
    attackerCap: "enemies",
    doorHitPoints: "HP",
    approachMetersPerSecond: "m/s",
    hopMs: "ms",
    roundMs: "ms",
    breachPauseMs: "ms",
  },
};

function currentTacticalTunables() {
  return { ...devSettings.supplyRun };
}

function applyDevSettings(nextSettings) {
  devSettings = saveDevSettings(nextSettings);
  if (tacticalView) {
    for (const [key, value] of Object.entries(devSettings.supplyRun)) tacticalView.setTunable(key, value);
  }
  syncDevToolsControls();
}

function setDevSetting(section, key, value) {
  applyDevSettings({
    ...devSettings,
    [section]: { ...devSettings[section], [key]: value },
  });
}

function formatDevSettingValue(section, key) {
  const value = devSettings[section][key];
  if (key === "initiative") return value === "enemies-first" ? "Enemies first" : "Survivors first";
  const unit = DEV_SETTING_UNITS[section]?.[key] ?? "";
  return `${Number(value).toLocaleString()}${unit ? ` ${unit}` : ""}`;
}

function renderNumberDevSetting(section, key, schema) {
  const value = devSettings[section][key];
  const label = DEV_SETTING_LABELS[section]?.[key] ?? key;
  return `<div class="dev-setting" data-dev-setting="${section}.${key}">
    <label for="dev-${section}-${key}">${escapeHtml(label)}<span>${escapeHtml(formatDevSettingValue(section, key))}</span></label>
    <div class="setting-pair">
      <input id="dev-${section}-${key}" type="range" min="${schema.min}" max="${schema.max}" step="${schema.step}" value="${value}" data-dev-section="${section}" data-dev-key="${key}">
      <input type="number" min="${schema.min}" max="${schema.max}" step="${schema.step}" value="${value}" data-dev-section="${section}" data-dev-key="${key}" aria-label="${escapeHtml(label)}">
    </div>
  </div>`;
}

function renderChoiceDevSetting(section, key, schema) {
  const value = devSettings[section][key];
  const label = DEV_SETTING_LABELS[section]?.[key] ?? key;
  const options = schema.choices.map((choice) => {
    const display = choice === "enemies-first" ? "Enemy hits first" : "Survivor hits first";
    return `<option value="${escapeHtml(choice)}" ${choice === value ? "selected" : ""}>${escapeHtml(display)}</option>`;
  }).join("");
  return `<div class="dev-setting" data-dev-setting="${section}.${key}">
    <label for="dev-${section}-${key}">${escapeHtml(label)}<span>${escapeHtml(formatDevSettingValue(section, key))}</span></label>
    <select id="dev-${section}-${key}" data-dev-section="${section}" data-dev-key="${key}">${options}</select>
  </div>`;
}

function renderDevTools() {
  for (const section of Object.keys(DEV_SETTING_SCHEMA)) {
    const panel = document.querySelector(`[data-dev-settings-panel="${section}"]`);
    if (!panel) continue;
    panel.innerHTML = Object.entries(DEV_SETTING_SCHEMA[section]).map(([key, schema]) => (
      schema.choices ? renderChoiceDevSetting(section, key, schema) : renderNumberDevSetting(section, key, schema)
    )).join("");
  }
  syncDevToolsControls();
}

function syncDevToolsControls() {
  for (const section of Object.keys(DEV_SETTING_SCHEMA)) {
    for (const key of Object.keys(DEV_SETTING_SCHEMA[section])) {
      const container = document.querySelector(`[data-dev-setting="${section}.${key}"]`);
      if (!container) continue;
      const readout = container.querySelector("label span");
      if (readout) readout.textContent = formatDevSettingValue(section, key);
      for (const input of container.querySelectorAll("[data-dev-section][data-dev-key]")) {
        if (document.activeElement !== input) input.value = devSettings[section][key];
      }
    }
  }
  syncTutorialDevControls();
}

function syncTutorialDevControls() {
  const checkbox = document.querySelector("[data-tutorial-enabled]");
  if (checkbox) checkbox.checked = Boolean(campaign.tutorial?.enabled);
  const readout = document.querySelector("[data-tutorial-completion-readout]");
  if (!readout) return;
  const completedIds = new Set(campaign.tutorial?.completedLessonIds ?? []);
  const completedCount = TUTORIAL_LESSONS.filter((lesson) => completedIds.has(lesson.id)).length;
  readout.textContent = `${completedCount}/${TUTORIAL_LESSONS.length} complete`;
}

function setDevToolsTab(tab) {
  for (const section of document.querySelectorAll(".dev-tools-section")) section.hidden = section.id !== `dev-tab-${tab}`;
  for (const button of document.querySelectorAll(".dev-tools-tab")) {
    const active = button.dataset.devTab === tab;
    button.classList.toggle("active", active);
    button.setAttribute("aria-selected", String(active));
  }
}

function setDevToolsOpen(open) {
  const modal = document.getElementById("dev-tools-modal");
  if (!modal) return;
  modal.hidden = !open;
  if (open) {
    renderDevTools();
    modal.querySelector(".dev-tools-tab.active")?.focus();
  }
}

document.getElementById("btn-dev-tools")?.addEventListener("click", () => {
  setTopbarMenuOpen(false);
  setDevToolsOpen(true);
});
document.getElementById("btn-dev-tools-close")?.addEventListener("click", () => setDevToolsOpen(false));
document.getElementById("dev-tools-modal")?.addEventListener("click", (event) => {
  if (event.target.id === "dev-tools-modal") setDevToolsOpen(false);
});
document.getElementById("dev-tools-modal")?.addEventListener("input", (event) => {
  const input = event.target.closest("[data-dev-section][data-dev-key]");
  if (!input) return;
  setDevSetting(input.dataset.devSection, input.dataset.devKey, input.value);
});
document.getElementById("dev-tools-modal")?.addEventListener("change", (event) => {
  const tutorialToggle = event.target.closest("[data-tutorial-enabled]");
  if (tutorialToggle) {
    campaign = setTutorialEnabled(campaign, tutorialToggle.checked);
    save();
    renderTutorial();
    return;
  }
  const input = event.target.closest("[data-dev-section][data-dev-key]");
  if (!input) return;
  setDevSetting(input.dataset.devSection, input.dataset.devKey, input.value);
});
document.getElementById("btn-dev-tools-reset")?.addEventListener("click", () => {
  applyDevSettings(DEFAULT_DEV_SETTINGS);
  renderDevTools();
});
document.getElementById("btn-tutorial-reset")?.addEventListener("click", () => {
  campaign = resetTutorialProgress(campaign);
  save();
  renderTutorial();
  syncTutorialDevControls();
});
for (const button of document.querySelectorAll(".dev-tools-tab")) {
  button.addEventListener("click", () => setDevToolsTab(button.dataset.devTab));
}

function endCampaignDay() {
  setTopbarMenuOpen(false);
  if (document.body.dataset.screen !== "overworld") return; // no day-advance mid-run/defense
  if (campaignConcluded(campaign)) return; // the run has ended (rescue resolved) — nothing left to advance to
  campaign = advanceCampaignDay(campaign);
  supplyRunsToday = 0;
  save();
  const deposit = campaign.lastInventoryDeposit;
  if (deposit?.items?.length) {
    const recoveredValue = inventoryValue(deposit.spendableItems ?? []);
    logLine("items", `  Deposited ${deposit.items.length} carried item${deposit.items.length === 1 ? "" : "s"}: +${formatResource(recoveredValue)} supply value, +${formatResource(deposit.foodGained)} food, +${formatResource(deposit.materialsGained)} materials.`);
  }
  const feeding = campaign.lastFeeding;
  if (feeding) {
    logLine("day", `— Day ${campaign.day} — fed ${feeding.fed.length}, ${feeding.consumed} food used (${feeding.pantryAfter} left)`);
    for (const id of feeding.nowHungry) logLine("death", `  ${survivorName(id)} went hungry — feed them tomorrow or they're lost.`);
    for (const id of feeding.starved) logLine("death", `  ${survivorName(id)} starved.`);
  } else {
    logLine("day", `— Day ${campaign.day} —`);
  }
  renderOverworld();
  showEndgameBriefingIfNeeded();
  // Then, if a scheduled defense is due, launch it (the day is already fully
  // advanced + saved, so an aborted defense never leaves a half-advanced day).
  // Shown after the endgame briefing modal (if any) so the two don't visually
  // collide on the day they can first coincide (day 2).
  ensureBaseDefense();
  if (isRescueDay(campaign) && campaign.base?.defense) {
    // The terminal beat: the evac window is open and the horde makes its final
    // push, sized off the campaign's lifetime recklessness (finalWaveSize).
    logLine("death", `The evac window is open — the horde throws everything at the walls.`);
    startBaseDefense({ waveSize: finalWaveSize(campaign), final: true });
  } else if (campaign.base?.defense && defenseDueOnDay(campaign.day) && activeSurvivorsV4(campaign).length > 0) {
    logLine("death", `A horde is closing on the base…`);
    startBaseDefense({ waveSize: defenseWaveSize(campaign) });
  }
}

document.getElementById("btn-new-run").addEventListener("click", () => {
  setTopbarMenuOpen(false);
  if (!confirm("Start a new run? This wipes the current roster and progress.")) return;
  resetRun();
});
document.getElementById("start-tutorial-button")?.addEventListener("click", beginTutorialFromSplash);
document.getElementById("start-new-game-button")?.addEventListener("click", beginNewGameFromSplash);
// Both end screens (loss overlay, victory/extraction overlay) tear down the
// defense the same way and start a fresh run.
function dismissEndScreenAndReset() {
  document.body.dataset.gameOver = "false";
  document.body.dataset.won = "false";
  if (tacticalView && activeDefense) { tacticalView.destroy(); tacticalView = null; activeDefense = false; defenseRender = null; }
  defenseIsFinal = false;
  setScreen("overworld");
  setLayersVisible(DEFENSE_HIDDEN_LAYER_IDS, true);
  setOverworldMarkersVisible(true);
  if (preDefenseCamera) {
    map.once("moveend", () => setOverworldCameraConstraints(true));
    map.easeTo({ ...preDefenseCamera, duration: TACTICAL_CAMERA_DURATION_MS });
    preDefenseCamera = null;
  } else {
    setOverworldCameraConstraints(true);
  }
  resetRun();
}
document.getElementById("btn-game-over-new-run").addEventListener("click", dismissEndScreenAndReset);
document.getElementById("btn-extraction-new-run").addEventListener("click", dismissEndScreenAndReset);
document.getElementById("btn-report-bug").addEventListener("click", (event) => {
  postReport("/api/debug/report", {
    sessionId: REPORT_SESSION_ID,
    note: "Bug report from top-bar menu",
    page: pageContextSnapshot(),
    snapshot: campaignSnapshot(),
    events: [{
      t: Math.round(performance.now()),
      category: "ui",
      type: "manual-bug-report",
      screen: document.body.dataset.screen || "",
    }],
  }, event.currentTarget, "Bug sent");
});
document.getElementById("report-lag-button").addEventListener("click", (event) => {
  const sample = perfSampleSnapshot();
  postReport("/api/perf/report", {
    sessionId: REPORT_SESSION_ID,
    note: "Performance issue from top-bar menu",
    page: pageContextSnapshot(),
    device: reportDeviceSnapshot(),
    errors: [],
    samples: [{ ...sample, t: Math.max(0, sample.t - 1000) }, sample],
  }, event.currentTarget, "Perf sent");
});
document.getElementById("btn-defend-now").addEventListener("click", () => {
  setTopbarMenuOpen(false);
  startBaseDefense({ waveSize: defenseWaveSize(campaign) });
});
initDiscordAuth();

// Enable/disable the "Defend now (debug)" menu item to match whether a
// defense is actually startable right now (a generated layout + a living crew).
function syncDevMenu() {
  const button = document.getElementById("btn-defend-now");
  if (!button) return;
  button.disabled = !(Boolean(campaign.base?.defense) && activeSurvivorsV4(campaign).length > 0);
}

for (const button of document.querySelectorAll("#drawer-tabs .drawer-tab")) {
  button.addEventListener("click", () => setActiveTab(button.dataset.tab));
}
document.getElementById("drawer-toggle")?.addEventListener("click", () => setMobileDrawerCollapsed(!mobileDrawerCollapsed));

// The single on-map action button: ends the day on the overworld, ends the turn
// during supply runs, then returns to base once a supply run is over.
document.getElementById("btn-tactical-advance").addEventListener("click", () => {
  if (document.body.dataset.screen === "overworld") { endCampaignDay(); return; }
  if (!tacticalView) return;
  if (tacticalView.isEnemyPhaseRunning()) { tacticalView.skipEnemyAnimation(); return; } // fast-forward the enemy phase
  // Base defense with the wave cleared → "Return to base" (its exit).
  if (activeDefense && Object.keys(tacticalView.getState().enemies || {}).length === 0) { returnToBase(); return; }
  if (tacticalView.isOver()) { returnToBase(); return; }
  // Warn (via a custom popup above the button, not a browser alert) if any
  // survivor took no action this turn and has nothing queued/ongoing.
  const idleIds = tacticalView.getIdleUnitIds();
  if (idleIds.length) {
    const units = tacticalView.getState().units;
    const names = idleIds.map((id) => units[id]?.label ?? id).join(", ");
    showEndTurnConfirm(`${idleIds.length} survivor${idleIds.length === 1 ? "" : "s"} still ${idleIds.length === 1 ? "has" : "have"} no orders (${names}).`, doEndTurn);
    return;
  }
  doEndTurn();
});

function doEndTurn() {
  if (!tacticalView) return;
  hideEndTurnConfirm();
  clearThreatAlert(); // the alert draws attention only until the next turn is taken
  tacticalView.advanceTurn();
}

function hideEndTurnConfirm() {
  document.getElementById("tactical-endturn-confirm").hidden = true;
}

// The custom "no orders" confirm floating above the End Turn button.
function showEndTurnConfirm(message, onConfirm) {
  const popup = document.getElementById("tactical-endturn-confirm");
  document.getElementById("tactical-endturn-confirm-text").textContent = `${message} End the turn anyway?`;
  popup.hidden = false;
  document.getElementById("tactical-endturn-go").onclick = () => { hideEndTurnConfirm(); onConfirm(); };
  document.getElementById("tactical-endturn-cancel").onclick = hideEndTurnConfirm;
}
document.getElementById("btn-tactical-clear").addEventListener("click", () => { tacticalView?.clearSelectedOrder(); renderTacticalChrome(); });
document.getElementById("btn-tactical-skip").addEventListener("click", () => { tacticalView?.skipSelectedUnit(); renderTacticalChrome(); });
document.getElementById("btn-outcome-review").addEventListener("click", closeOutcomeModal);
document.getElementById("btn-outcome-return").addEventListener("click", returnToBase);
window.addEventListener("keydown", (event) => {
  if (document.body.dataset.screen !== "tactical" || !tacticalView) return;
  if (event.key === "Escape" && !document.getElementById("supply-run-outcome-modal").hidden) {
    closeOutcomeModal();
    return;
  }
  // While the "no orders — end anyway?" confirm is up, it captures the keyboard:
  // Space commits (end turn anyway), Esc dismisses (keep planning). Handled
  // before the general end-turn hotkey so Space doesn't just re-open the confirm.
  const endTurnConfirm = document.getElementById("tactical-endturn-confirm");
  if (!endTurnConfirm.hidden) {
    if (event.code === "Space") { event.preventDefault(); document.getElementById("tactical-endturn-go").click(); return; }
    if (event.key === "Escape") { event.preventDefault(); document.getElementById("tactical-endturn-cancel").click(); return; }
  }
  const enemyPhase = tacticalView.isEnemyPhaseRunning();
  // Order-issuing hotkeys are ignored during the enemy phase; Space still works
  // (it skips the animation — see the button handler).
  if (!enemyPhase) {
    if (event.key === "i" || event.key === "I") tacticalView.stageScavengeForSelected();
    // Tab / Shift+Tab cycles the selected survivor; a lone unit hotkey for skip.
    if (event.key === "Tab") { event.preventDefault(); tacticalView.selectNextUnit(event.shiftKey ? -1 : 1); }
    if (event.key === "s" || event.key === "S") { tacticalView.skipSelectedUnit(); renderTacticalChrome(); }
    if (event.key === "Escape") tacticalView.cancelTransfer();
  }
  const actionBtn = document.getElementById("btn-tactical-advance");
  // Space ends the turn (only while the supply run is live); Enter returns to base
  // (only once it's over) — a deliberately different key so a fast run of
  // spacebar end-turns can't accidentally leave the supply run. Both route through
  // the button, and both preventDefault to stop the page scroll and any native
  // activation of the focused button (which would double-fire).
  if (event.code === "Space") { event.preventDefault(); if (!tacticalView.isOver()) actionBtn.click(); }
  if (event.code === "Enter") { event.preventDefault(); if (tacticalView.isOver()) actionBtn.click(); }
});

// #map is the hero in both screens now (tactical-view mounts on top of it rather
// than replacing it), so it always needs to track the window size.
window.addEventListener("resize", () => { map?.resize(); });

// --- Boot ---------------------------------------------------------------

initMap();
ensureBaseDefense();
renderOverworld();
setStartSplashReady(true);
if (playtestEntry) hideStartSplash();
showEndgameBriefingIfNeeded();
applyPlaytestEntry().catch((error) => {
  console.error("Failed to apply playtest entrypoint", error);
  recordPlaytestState({ applied: false, error: String(error?.message ?? error) });
});

// Inspection hook for automated smoke/driving (Playwright) and the console.
window.__apocalypseDispatch = {
  version: "v4-0.15",
  getCampaign: () => campaign,
  getBaseDefense: () => campaign.base?.defense ?? null,
  startBaseDefense: (waveSize) => startBaseDefense({ waveSize: waveSize ?? defenseWaveSize(campaign) }),
  startFinalDefense: () => startBaseDefense({ waveSize: finalWaveSize(campaign), final: true }),
  endBaseDefense,
  getBaseMetrics: () => {
    const feature = baseFeatureForCampaign();
    return feature ? buildingFootprintMetrics(feature) : null;
  },
  getBaseGrid: () => campaign.base?.grid ?? null,
  getBaseRooms: () => ({ ...(campaign.base?.rooms ?? {}) }),
  getBaseRoomRegions: () => {
    const ctx = baseInteriorContext();
    if (!ctx) return [];
    const built = campaign.base?.rooms ?? {};
    return ctx.detection.rooms.map((room) => {
      const regionKey = roomSignature(room);
      return { regionKey, buildable: !room.isCorridor, type: built[regionKey] ?? "" };
    });
  },
  buildRoom,
  isOverHousingCapacity: () => isOverHousingCapacity(campaign),
  // Force the soft over-capacity warning (reads live roster vs housing for its text).
  warnOverCapacity: () => { pendingCapacityWarning = true; showCapacityWarningIfPending(); },
  getBaseShape: () => { const s = currentBaseShape(); return s ? { angleDegrees: s.angleDegrees, cellSizeMeters: s.cellSizeMeters, cellCount: s.cells.size, corners: s.corners } : null; },
  getScreen: () => document.body.dataset.screen,
  getTileInfo: () => ({ source: tileGeometry.source, label: tileGeometry.label, tile: DEFAULT_TILE, buildingCount: buildingTargets.size }),
  getPois: () => POIS.map(({ id, name, coordinate, buildingTargetId }) => ({ id, name, coordinate, buildingTargetId, discovered: isPoiDiscovered(campaign, id) })),
  getBuildingTargets: () => [...buildingTargets.values()].map(({ id, name, coordinate }) => ({ id, name, coordinate })),
  getPlaytestState: () => playtestState ? { ...playtestState } : null,
  isTargetReachable,
  selectTarget: (id) => { if (buildingTargets.has(id)) { selectedPoiId = id; renderOverworld(); } },
  getMap: () => map,
  getTacticalView: () => tacticalView,
  startSupplyRun,
  returnToBase,
};
